import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { idDoParametro, trocarParametroNaUrl } from '../config/rotas';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, EstadoDaLista, mensagemDoErroDeCarga, opcaoAtualForaDaLista, useEstadoDaCarga, campo, textos } from '../design';
import { podeClassificar, confirmacaoDeExcluirTransacao, avisoDeSubstituicaoDoExtrato, confirmacaoDeExcluirExtrato } from '../utils/conciliacao';
import { usePedirTexto, useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { bankAccountsService, bankTransactionsService, expenseCategoriesService, suppliersService, importLogService, recurringExpensesService, invoicesService, investmentsService } from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/formatters';
import { agruparContasPorPai, filaDoAssistente } from '../utils/bankAccountsView';
import PeriodSelector, { PeriodFilter, defaultPeriodFilter, periodToParams } from './PeriodSelector';
import InvestmentAccountPanel from './InvestmentAccountPanel';
import { lerVarredura, fraseDoSaldoDaVarredura } from '../utils/contaDeVarredura';
import CascaDeModal from './shared/CascaDeModal';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaAcoes, contornoDoSaldoDeConta,
} from './shared/lista/PecasDaLista';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';
import ExtratoSicrediDoCartao from './ExtratoSicrediDoCartao';
import MaisAcoes from './shared/MaisAcoes';
import { contaPelaApi } from '../utils/extratoSicrediApi';

// "Atualizar agora" do extrato pela API é @Roles(ADMIN) no backend: grava lançamento.
const isAdmin = (): boolean => {
  try { return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin'; }
  catch { return false; }
};

/**
 * A TELA DE CIMA DE CONTAS NO PADRÃO DA CENTRAL (23/10/2026, continuação do padrão das listas). Superfície neutra e valor
 * em branco: o saldo em verde, o "Aplicado" em ciano e o selo "Aplicação" em ciano eram decoração. A cor fica onde é
 * estado: o saldo negativo leva a barra vermelha no cartão; transação pendente, posição velha (7 dias), sem posição e
 * divergência do banco mantêm o tom. "Tudo conciliado" é ausência de pendência: sai a pílula verde, fica o ✓ neutro. O
 * "liberam em" é agenda, não alerta: sai o âmbar.
 */
const CARTAO_DA_CONTA = 'bg-central-cartao rounded-central border border-central-borda';
// O contorno do cartão de conta segue a decisão C (contornoDoSaldoDeConta, nas peças da lista).
const barraDoSaldo = (v: any) => contornoDoSaldoDeConta(parseFloat(v) || 0);

/** `accept` do seletor de arquivo por formato travado na conta. */
const ACCEPT_BY_FORMAT: Record<string, string> = {
  PDF: '.pdf,.PDF',
  OFX: '.ofx,.OFX',
  XLS: '.xls,.xlsx,.csv',
};

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Categorias manuais para classificar transações que não são fatura nem despesa
// (transferências entre contas próprias, empréstimos a sócio, captação de empréstimo).
// Classificar resolve a transação (sai das pendências) sem vincular documento.
const MANUAL_TX_CATEGORIES = [
  'Transferência Interna',
  // Aplicação/resgate em fundo e sweep automático. Diferente de "Transferência
  // Interna" num ponto que só aparece no Fluxo de Caixa: esta é uma das quatro
  // categorias que o `cash-flow.service` lista como movimento NÃO operacional,
  // então a saída aparece explicando a diferença entre o saldo do relatório e o
  // do banco. Classificada como Transferência Interna, a saída some do painel.
  'Movimentação Financeira',
  'Empréstimo a Sócio',
  'Captação de Empréstimo',
  'Aporte de Sócio',
  'Distribuição de Lucro',
  'Outras Receitas',
  'Estorno Bancário',
];

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    tag: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 014 12V7a4 4 0 014-4z' /></svg>,
    upload: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    eye: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>,
    arrowLeft: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' /></svg>,
    link: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' /></svg>,
    unlink: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' /></svg>,
    'eye-off': <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' /></svg>,
    'alert-triangle': <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' /></svg>,
    repeat: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    document: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' /></svg>,
    download: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' /></svg>,
  };
  return icons[name] || null;
};


// ============ RECONCILE MODAL (smart suggestions + search + create expense) ============
// Nota CANCELADA não cobra nada — deixá-la selecionável levava o operador a
// somar um valor que o cliente nunca deveu (a FAT202606002 tinha duas
// canceladas de R$ 1.320 e R$ 1.380 escondendo a única válida, de R$ 820).
// Fora do componente de propósito: é usada dentro de um useMemo que roda antes
// do corpo terminar, e como `const` daria TDZ (página branca).
function nfsUteis(data: any): any[] {
  return (data?.fiscalDocuments || []).filter((d: any) => d.fiscalStatus !== 'Cancelada');
}

/**
 * Quanto AINDA se deve numa nota fiscal.
 *
 * Os três lugares que somavam NF usavam `totalValue`, o valor cheio, e só
 * pulavam as com `status === 'paid'`. Uma NF `partial` — R$ 1.000 com R$ 600 já
 * pagos — entrava como R$ 1.000, e o pagamento saía R$ 600 sobrepagando. A
 * coluna "Pago" já era exibida na tabela; o cálculo é que a ignorava.
 */
function nfSaldo(d: any): number {
  const total = Number(d?.totalValue) || 0;
  const pago = Number(d?.paidValue ?? d?.paidAmount) || 0;
  return Math.max(total - pago, 0);
}

/**
 * Confirmação do "clicou numa sugestão → concilia", compartilhada pelas TRÊS
 * telas que chamam os mesmos dois endpoints.
 *
 * Só o modal principal ganhou, em 28/07, o aviso de que o lançamento ia sair
 * PARCIAL. O wizard Manual e o modal de importação seguiram conciliando com um
 * clique, sem confirmação nenhuma — e o wizard sem nem trava de duplo clique,
 * o que dispara duas chamadas na mesma transação. Manter o texto e a decisão
 * num lugar só é o que impede a próxima tela de nascer sem o aviso.
 *
 * Avisa nos DOIS sentidos. O caso "transação maior que o saldo" faltava: o
 * confirm mostrava o saldo do alvo e o backend lançava o valor da transação,
 * exatamente a inversão que o fix de 28/07 corrigiu no outro sentido. Acima de
 * 20% o backend recusa (bank-transactions.service.ts:882), mas abaixo disso ele
 * aceita calado.
 */
// D9 (10/10/2026): era window.confirm, a caixa nativa do navegador, fora da casca do HUB. Agora devolve título e
// corpo, e quem chama usa o ConfirmDialog (useConfirm). O texto é o mesmo; o título é curto (a casca o trunca).
function confirmacaoDoVinculo(tx: any, match: any): { title: string; message: string } {
  const valorTx = Math.abs(Number(tx.amount));
  const saldoAlvo = Number(match.remainingAmount ?? match.amount ?? 0);
  const substantivo = match.type === 'expense' ? 'despesa' : 'fatura';
  const alvo = match.type === 'multi_invoice'
    ? `${match.ids?.length || 0} faturas (R$ ${fmt(Number(match.amount ?? 0))})`
    : `${substantivo} "${match.description || match.name || ''}" (R$ ${fmt(saldoAlvo)})`;

  // Estorno de tarifa: o crédito vira filha NEGATIVA do grupo do mês, abatendo
  // o total. Não é pagamento nenhum, então os avisos de "parcial" e "maior que
  // o saldo" abaixo não se aplicam — eles comparariam a devolução com a
  // previsão do grupo e diriam bobagem.
  if (match.refund) {
    return {
      title: 'Confirmar estorno?',
      message: `Transação: ${tx.description} (+R$ ${fmt(valorTx)})\n` +
        `→ abate R$ ${fmt(valorTx)} de "${match.description || ''}"\n\n` +
        `A devolução entra como lançamento negativo no grupo do mês.`,
    };
  }

  let aviso = '';
  if (match.type !== 'multi_invoice' && valorTx < saldoAlvo - 0.01) {
    aviso = `\n\n⚠ Serão lançados R$ ${fmt(valorTx)} (valor da transação). A ${substantivo} fica PARCIAL, restando R$ ${fmt(saldoAlvo - valorTx)}.\nSe este depósito cobre mais de uma fatura, cancele e use "Selecionar multiplas faturas".`;
  } else if (match.type !== 'multi_invoice' && valorTx > saldoAlvo + 0.01) {
    aviso = `\n\n⚠ A transação é R$ ${fmt(valorTx - saldoAlvo)} MAIOR que o saldo desta ${substantivo} (R$ ${fmt(saldoAlvo)}), e o valor lançado é o da transação: R$ ${fmt(valorTx)}.\nConfira se o depósito não é de outra ${substantivo} ou se cobre mais de uma.`;
  }

  // Sugestão que só entrou porque a poda por fornecedor não deixou nenhuma
  // candidata: o extrato nomeia outra contraparte, e a semelhança de valor é
  // o único motivo de ela estar na tela.
  if (match.otherSupplier) {
    aviso += `\n\n⚠ O extrato nomeia OUTRO fornecedor. Esta despesa aparece só por semelhança de valor — confira antes de vincular.`;
  }

  return {
    title: 'Confirmar vínculo?',
    message: `Transação: ${tx.description} (R$ ${fmt(valorTx)})\n→ ${alvo}${aviso}`,
  };
}

/**
 * Despacho do vínculo. O `amount` do lado da despesa é redundante de propósito:
 * o backend faz `dto.amount || txAmount` (bank-transactions.service.ts:2373),
 * então mandar |tx.amount| ou omitir dá no mesmo — fica explícito para a
 * intenção da tela não depender do default do backend.
 */
async function dispatchReconcileMatch(tx: any, match: any): Promise<any> {
  if (match.type === 'multi_invoice') {
    return bankTransactionsService.reconcileMultiInvoice(tx.id, match.ids);
  } else if (match.type === 'expense') {
    return bankTransactionsService.reconcilePartial(tx.id, { expenseId: match.id, amount: Math.abs(Number(tx.amount)) });
  } else if (match.type === 'invoice') {
    return bankTransactionsService.reconcilePartial(tx.id, { invoiceId: match.id });
  } else {
    // Antes um `else` solto tratava qualquer coisa como fatura. Se o shape da
    // sugestão mudar, isso posta o id de uma despesa como invoiceId e baixa a
    // fatura de outra pessoa. Falhar alto é a única resposta segura.
    throw new Error(`Tipo de sugestão desconhecido: "${match.type}". Vínculo não realizado.`);
  }
}

// `onClose` é só fechar (cancelar não mexe em nada no servidor, então não pode
// custar requisição nenhuma). `onReconciled` é o caminho de sucesso: o pai
// atualiza a linha com o que o POST devolveu e recarrega o resto por baixo.
const ReconcileModal = ({ transaction, onClose, onReconciled, selectedAccount, showToast }: { transaction: any; onClose: () => void; onReconciled: (saved?: any) => void; selectedAccount: any; showToast: (msg: string, type?: 'success' | 'error') => void }) => {
  const confirmar = useConfirm();
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [sugLoading, setSugLoading] = useState(true);
  // ⛔ As listas que respondem "já existe documento para este dinheiro?" são ESTADO (09/10/2026). Zeravam com a API
  // fora e diziam "Nenhuma … encontrada", com "Criar Despesa" na aba ao lado: o operador criava a despesa DUPLICADA de
  // uma que existe, e a conciliação a amarrava ao extrato. Sem a lista lida, criar fica travado (porqueNaoCriar).
  const cargaDasSugestoes = useEstadoDaCarga();
  const cargaDasDespesasDoMes = useEstadoDaCarga();
  const cargaDoFormulario = useEstadoDaCarga();
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchErro, setSearchErro] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'suggestions' | 'search' | 'search_expense' | 'create'>('suggestions');
  const searchTimeout = React.useRef<any>(null);

  // Multi-select state for credit transactions
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);
  // Rateio: um depósito pagando PARTE de cada fatura. Necessário para órgão
  // público que paga por empenho — o depósito cobre só as NFs empenhadas e o
  // resto da fatura continua devendo.
  const [rateioMode, setRateioMode] = useState(false);
  const [rateioValores, setRateioValores] = useState<Record<number, string>>({});
  // NFs escolhidas por fatura no rateio: o valor passa a vir do documento em vez
  // de ser digitado, e o pagamento fica gravado contra a nota que o empenho cobre.
  const [rateioNfs, setRateioNfs] = useState<Record<number, Set<number>>>({});
  const [rateioNfsData, setRateioNfsData] = useState<Record<number, any>>({});
  const [rateioNfsAberta, setRateioNfsAberta] = useState<number | null>(null);

  // Expense search state
  const [expSearchQuery, setExpSearchQuery] = useState('');
  const expSearchTimeout = React.useRef<any>(null);

  // Month-based expense browsing
  const [expenseMonth, setExpenseMonth] = useState(() => {
    const d = (transaction.date || '').split('T')[0].split('-');
    return d.length >= 2 ? `${d[0]}-${d[1]}` : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  });
  const [expensesByMonth, setExpensesByMonth] = useState<any[]>([]);
  const [expMonthLoading, setExpMonthLoading] = useState(false);

  // Create expense form state
  const [categories, setCategories] = useState<any[]>([]);
  const [suppliersList, setSuppliersList] = useState<any[]>([]);
  const txDay = (() => {
    try { return parseInt(String(transaction.date).split('T')[0].split('-')[2], 10); } catch { return 1; }
  })();
  const txYear = (() => {
    try { return parseInt(String(transaction.date).split('T')[0].split('-')[0], 10); } catch { return new Date().getFullYear(); }
  })();
  const defaultGenerateUntil = `${txYear}-12`;
  const [newExpense, setNewExpense] = useState({
    description: transaction.description || '',
    categoryId: '',
    supplierId: '',
    newSupplierName: '',
    paymentMethod: 'pix',
    notes: '',
    parentExpenseId: '',
    createRecurring: false,
    recurringDayOfMonth: String(txDay),
    generateUntil: defaultGenerateUntil,
  });
  const [createSupplierMode, setCreateSupplierMode] = useState(false);
  // Contrapartida que o extrato já nomeia. A aba "Criar Despesa" nascia com
  // Fornecedor "Nenhum" e Categoria vazia mesmo quando o CNPJ estava escrito na
  // descrição ("PAGAMENTO PIX 35125567000179 LICITAR DIGITAL"), obrigando a
  // caçar o fornecedor num select de 119 itens e a lembrar a categoria de cor.
  const [identificado, setIdentificado] = useState<any>(null);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState<any[]>([]);
  const [parentSearchErro, setParentSearchErro] = useState<string | null>(null);
  const [parentSearchLoading, setParentSearchLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState<any>(null);
  const parentSearchTimeout = React.useRef<any>(null);
  // Sub-modal: pay specific fiscal documents within a grouped invoice
  const [nfPickerInvoice, setNfPickerInvoice] = useState<any>(null);
  const [nfPickerData, setNfPickerData] = useState<any>(null);
  const [nfPickerLoading, setNfPickerLoading] = useState(false);
  const [nfPickerSelected, setNfPickerSelected] = useState<Set<number>>(new Set());

  const { rodar: rodarSugestoes } = cargaDasSugestoes;
  const { rodar: rodarFormulario } = cargaDoFormulario;
  const lerSugestoes = useCallback(async () => {
    setSugLoading(true);
    await rodarSugestoes(async () => {
      const data = await bankTransactionsService.suggestMatches(transaction.id);
      setSuggestions(Array.isArray(data) ? data : []);
    });
    setSugLoading(false);
  }, [transaction.id, rodarSugestoes]);
  // Categorias e fornecedores do "Criar Despesa": sem eles o formulário abria com os selects vazios, sem dizer por quê,
  // e "+ Novo fornecedor" criava de novo um fornecedor que já existe.
  const lerFormulario = useCallback(() => rodarFormulario(async () => {
    const [cats, sups] = await Promise.all([
      expenseCategoriesService.getAll(),
      suppliersService.getAll(),
    ]);
    setCategories(Array.isArray(cats) ? cats : []);
    setSuppliersList(Array.isArray(sups) ? sups : []);
  }), [rodarFormulario]);

  useEffect(() => {
    lerSugestoes();
    lerFormulario();
    // Pré-preenche fornecedor e categoria. Só preenche campo VAZIO: se o user
    // já escolheu alguma coisa, a resposta do servidor não pode passar por cima.
    const identificar = async () => {
      try {
        const info = await bankTransactionsService.identifyCounterparty(transaction.id);
        if (!info?.supplierId) return;
        setIdentificado(info);
        setNewExpense(p => ({
          ...p,
          supplierId: p.supplierId || String(info.supplierId),
          categoryId: p.categoryId || (info.categoryId ? String(info.categoryId) : ''),
        }));
      } catch {}
    };
    identificar();
  }, [transaction.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load expenses when tab is active or month changes
  const { rodar: rodarDespesasDoMes } = cargaDasDespesasDoMes;
  const lerDespesasDoMes = useCallback(async () => {
    setExpMonthLoading(true);
    setExpensesByMonth([]); // a lista é do mês: a do mês anterior não fica sob o novo
    await rodarDespesasDoMes(async () => {
      const data = await bankTransactionsService.searchExpenses('', selectedAccount?.id, expenseMonth);
      setExpensesByMonth(Array.isArray(data) ? data : []);
    });
    setExpMonthLoading(false);
  }, [expenseMonth, selectedAccount?.id, rodarDespesasDoMes]);
  useEffect(() => {
    if (activeTab !== 'search_expense') return;
    lerDespesasDoMes();
  }, [activeTab, lerDespesasDoMes]);

  /** Por que criar a despesa está travado (ou undefined). */
  const erroDeExistencia = cargaDasSugestoes.estado === 'erro' ? cargaDasSugestoes.erro
    : cargaDasDespesasDoMes.estado === 'erro' ? cargaDasDespesasDoMes.erro : null;
  const porqueNaoCriar = erroDeExistencia
    ? `Não consegui ler se já existe documento para este valor (${erroDeExistencia}); criar agora pode duplicar uma despesa que já existe. Tente de novo em Sugestões ou em Vincular Despesa antes de criar.`
    : cargaDasSugestoes.estado !== 'pronto'
      ? 'Lendo as sugestões: criar só depois de saber se já existe documento para este valor.'
      : cargaDoFormulario.estado === 'erro'
        ? `Não consegui carregar as categorias e os fornecedores (${cargaDoFormulario.erro}): sem eles, criar pode duplicar o fornecedor ou gravar sem a categoria certa.`
        : undefined;

  const navigateExpenseMonth = (delta: number) => {
    const [y, m] = expenseMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setExpenseMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const expenseMonthLabel = (() => {
    const [y, m] = expenseMonth.split('-').map(Number);
    const name = new Date(y, m - 1).toLocaleString('pt-BR', { month: 'long', timeZone: 'America/Sao_Paulo' });
    return `${name.charAt(0).toUpperCase() + name.slice(1)} ${y}`;
  })();

  // Filter expenses locally by search query
  const filteredExpenses = expSearchQuery
    ? expensesByMonth.filter((exp: any) =>
        (exp.description || '').toLowerCase().includes(expSearchQuery.toLowerCase()) ||
        String(exp.amount).includes(expSearchQuery)
      )
    : expensesByMonth;

  const handleExpenseSearch = (q: string) => {
    setExpSearchQuery(q);
  };

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await bankTransactionsService.searchInvoices(q);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchErro(null);
      } catch (e) {
        // Calava: "Nenhuma fatura encontrada para X" com a API fora, e o operador conciliava como outra coisa.
        setSearchResults([]);
        setSearchErro(mensagemDoErroDeCarga(e));
      }
      setSearchLoading(false);
    }, 400);
  };

  const handleReconcile = async (match: any) => {
    if (saving) return;
    if (!await confirmar(confirmacaoDoVinculo(transaction, match))) return;
    setSaving(true);
    try {
      const saved = await dispatchReconcileMatch(transaction, match);
      showToast('Transacao conciliada');
      onReconciled(saved);
    } catch (err: any) {
      showToast(err.response?.data?.message || err.message || 'Erro ao conciliar', 'error');
    }
    setSaving(false);
  };

  const openNfPicker = async (match: any) => {
    setNfPickerInvoice(match);
    setNfPickerSelected(new Set());
    setNfPickerData(null);
    setNfPickerLoading(true);
    try {
      const data = await invoicesService.getFiscalDocumentsPaymentStatus(match.id);
      setNfPickerData(data);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao carregar notas fiscais', 'error');
      setNfPickerInvoice(null);
    }
    setNfPickerLoading(false);
  };

  const toggleNfSelected = (docId: number) => {
    setNfPickerSelected(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const nfPickerSelectedTotal = React.useMemo(() => {
    if (!nfPickerData) return 0;
    return nfsUteis(nfPickerData)
      .filter((d: any) => nfPickerSelected.has(d.id))
      .reduce((s: number, d: any) => s + nfSaldo(d), 0);
  }, [nfPickerData, nfPickerSelected]);

  const handleReconcileByNf = async () => {
    if (!nfPickerInvoice || nfPickerSelected.size === 0) return;
    if (!await confirmar({
      title: 'Confirmar vínculo das notas?',
      message: `${nfPickerSelected.size} NF(s) (R$ ${fmt(nfPickerSelectedTotal)}) à transação "${transaction.description}".`,
    })) return;
    setSaving(true);
    try {
      const saved = await bankTransactionsService.reconcilePartial(transaction.id, {
        invoiceId: nfPickerInvoice.id,
        amount: Math.round(nfPickerSelectedTotal * 100) / 100,
        fiscalDocumentIds: Array.from(nfPickerSelected),
      });
      showToast(
        `${nfPickerSelected.size} NF(s) conciliada(s) (R$ ${fmt(nfPickerSelectedTotal)})`,
      );
      onReconciled(saved);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao conciliar por NF', 'error');
    }
    setSaving(false);
  };

  const toggleInvoiceSelection = (match: any) => {
    setSelectedInvoices(prev => {
      const exists = prev.find(m => m.id === match.id);
      return exists ? prev.filter(m => m.id !== match.id) : [...prev, match];
    });
  };

  const selectedTotal = selectedInvoices.reduce((sum, m) => sum + Number(m.remainingAmount ?? m.amount), 0);
  const txAmount = Math.abs(Number(transaction.amount));
  const rateioTotal = selectedInvoices.reduce((sum, m) => sum + (Number(rateioValores[m.id]) || 0), 0);

  // Ao ligar o rateio, pré-distribui o depósito na ordem da seleção, cada
  // fatura levando no máximo o próprio saldo. Assim o caso comum (o depósito
  // quita as primeiras e paga parte da última) já sai pronto.
  // Faturas que a busca/sugestão trouxe do próprio pagador (as de terceiros
  // vêm marcadas e não entram numa distribuição automática).
  const faturasSugeridas = (activeTab === 'search' ? searchResults : suggestions)
    .filter((m: any) => m.type === 'invoice' && !m.otherClient);
  const totalSugerido = faturasSugeridas.reduce(
    (sum: number, m: any) => sum + Number(m.remainingAmount ?? m.amount ?? 0), 0);
  // Nenhuma sugestão isolada fecha com o depósito, mas há mais de uma fatura e
  // juntas elas cobrem o valor: é o caso clássico de pagamento por empenho.
  const precisaDistribuir =
    faturasSugeridas.length >= 2 &&
    !faturasSugeridas.some((m: any) =>
      Math.abs(Number(m.remainingAmount ?? m.amount ?? 0) - Math.abs(Number(transaction.amount))) < 0.5) &&
    totalSugerido > Math.abs(Number(transaction.amount));

  // Mesma tolerância do backend — a tela não pode oferecer um botão que a API recusa.
  const tolerancia = Math.max(0.5, txAmount * 0.01);
  // Depósito menor que as faturas escolhidas: é necessariamente pagamento
  // parcial, não há conciliação total possível.
  const depositoMenor = selectedTotal - txAmount > tolerancia;

  /**
   * Pré-distribui o depósito PELAS NOTAS FISCAIS pendentes das faturas
   * selecionadas — é assim que órgão público paga: um empenho cobre NFs
   * inteiras, não frações arbitrárias de fatura.
   *
   * A versão anterior era uma cascata gulosa (enche a primeira fatura até o
   * saldo, o resto vai pra próxima). Dava R$ 2.620 na FAT202607014 e R$ 0,00 na
   * FAT202606002 quando a FUNDETEC tinha pago uma NF de CADA — e a tela ainda
   * carimbava "✓ bate com a transação". Palpite errado com cara de validado é
   * pior que palpite nenhum: quando não há combinação clara, agora deixa em
   * branco para o operador preencher.
   */
  const distribuirDeposito = React.useCallback(async () => {
    const alvo = Math.round(txAmount * 100);

    // Uma fatura só: o depósito inteiro é dela, sem ambiguidade.
    if (selectedInvoices.length === 1) {
      const m = selectedInvoices[0];
      const saldo = Number(m.remainingAmount ?? m.amount);
      setRateioValores({ [m.id]: Math.min(saldo, txAmount).toFixed(2) });
      return;
    }

    // Carrega as notas que ainda faltam (poucas faturas, poucas notas).
    const faltando = selectedInvoices.filter(m => !rateioNfsData[m.id]);
    let dados = rateioNfsData;
    if (faltando.length > 0) {
      const carregadas = await Promise.all(
        faltando.map(m =>
          invoicesService.getFiscalDocumentsPaymentStatus(m.id)
            .then(d => [m.id, d] as const)
            .catch(() => [m.id, null] as const),
        ),
      );
      dados = { ...rateioNfsData };
      carregadas.forEach(([id, d]) => { if (d) dados[id] = d; });
      setRateioNfsData(dados);
    }

    // Candidatas: nota válida e ainda não quitada.
    const candidatas: Array<{ invoiceId: number; docId: number; centavos: number }> = [];
    for (const m of selectedInvoices) {
      for (const d of nfsUteis(dados[m.id])) {
        if (d.status === 'paid') continue;
        // Saldo, não valor cheio: NF parcialmente paga entrava pelo total e a
        // busca de combinação trabalhava com números errados.
        candidatas.push({ invoiceId: m.id, docId: d.id, centavos: Math.round(nfSaldo(d) * 100) });
      }
    }

    const aplicar = (escolhidas: typeof candidatas) => {
      const notas: Record<number, Set<number>> = {};
      const valores: Record<number, string> = {};
      for (const m of selectedInvoices) { notas[m.id] = new Set(); valores[m.id] = ''; }
      for (const c of escolhidas) {
        notas[c.invoiceId].add(c.docId);
        valores[c.invoiceId] = (((Number(valores[c.invoiceId]) || 0) * 100 + c.centavos) / 100).toFixed(2);
      }
      setRateioNfs(notas);
      setRateioValores(valores);
    };

    const emBranco = () => {
      setRateioNfs({});
      setRateioValores(Object.fromEntries(selectedInvoices.map(m => [m.id, ''])));
    };

    if (candidatas.length === 0) { emBranco(); return; }

    // Caso comum: todas as notas pendentes somam o depósito.
    const somaTudo = candidatas.reduce((acc, c) => acc + c.centavos, 0);
    if (Math.abs(somaTudo - alvo) <= 1) { aplicar(candidatas); return; }

    // Senão, procura a combinação de notas que dá o valor do depósito. São
    // poucas notas, então a busca exaustiva é barata. Só aplica se houver UMA
    // resposta possível — com duas, escolher por conta própria seria chute.
    if (candidatas.length > 14) { emBranco(); return; }
    const solucoes: Array<typeof candidatas> = [];
    for (let mascara = 1; mascara < (1 << candidatas.length); mascara++) {
      let soma = 0;
      const combo: typeof candidatas = [];
      for (let i = 0; i < candidatas.length; i++) {
        if (mascara & (1 << i)) { soma += candidatas[i].centavos; combo.push(candidatas[i]); }
      }
      if (Math.abs(soma - alvo) <= 1) {
        solucoes.push(combo);
        if (solucoes.length > 1) break;
      }
    }
    if (solucoes.length === 1) aplicar(solucoes[0]);
    else emBranco();
  }, [selectedInvoices, txAmount, rateioNfsData]);

  const ligarRateio = (ligado: boolean) => {
    setRateioMode(ligado);
    if (!ligado) {
      setRateioNfs({});
      setRateioNfsAberta(null);
      return;
    }
    void distribuirDeposito();
  };

  // O modo sai dos NÚMEROS, não de uma flag: se o depósito não cobre o que foi
  // selecionado, só existe pagamento parcial. Antes o operador precisava
  // descobrir sozinho a caixinha "Ratear", e o caminho padrão era um botão
  // verde propondo conciliar R$ 4.580 com um depósito de R$ 2.620 — que a API
  // recusa. Redistribui a cada mudança de seleção, já que trocar as faturas
  // invalida os valores anteriores de qualquer forma.
  const idsSelecionados = selectedInvoices.map(m => m.id).join(',');
  React.useEffect(() => {
    if (!multiSelect || selectedInvoices.length === 0 || !depositoMenor) return;
    setRateioMode(true);
    setRateioNfs({});
    void distribuirDeposito();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSelecionados, depositoMenor, multiSelect]);

  const handleMultiReconcile = async () => {
    if (selectedInvoices.length === 0) return;
    // Allocation em branco vira `Number('') = 0` e ia no payload como
    // { invoiceId, amount: 0 }. O backend recusa (allocations com amount <= 0),
    // então era um botão habilitado para operação que a API rejeita — e o
    // operador não sabia qual fatura faltava preencher.
    if (rateioMode) {
      const semValor = selectedInvoices.filter(m => !(Number(rateioValores[m.id]) > 0));
      if (semValor.length > 0) {
        showToast(
          `Informe o valor de ${semValor.length === 1 ? 'a fatura' : `${semValor.length} faturas`} sem valor preenchido`,
          'error',
        );
        return;
      }
    }
    const valorEnviado = rateioMode ? rateioTotal : selectedTotal;
    if (!await confirmar({
      title: 'Confirmar vínculo das faturas?',
      message: `${selectedInvoices.length} fatura(s) (R$ ${fmt(valorEnviado)}) à transação "${transaction.description}".`,
    })) return;
    setSaving(true);
    try {
      const ids = selectedInvoices.map(m => m.id);
      let saved: any;
      if (rateioMode) {
        saved = await bankTransactionsService.reconcileMultiInvoice(transaction.id, undefined,
          selectedInvoices.map(m => {
            const notas = Array.from(rateioNfs[m.id] || []);
            return {
              invoiceId: m.id,
              amount: Number(rateioValores[m.id] || 0),
              ...(notas.length ? { fiscalDocumentIds: notas } : {}),
            };
          }));
      } else {
        saved = await bankTransactionsService.reconcileMultiInvoice(transaction.id, ids);
      }
      showToast(`${selectedInvoices.length} faturas conciliadas`);
      onReconciled(saved);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao conciliar', 'error');
    }
    setSaving(false);
  };

  // Carrega as NFs de uma fatura do rateio (mesmo endpoint do "Pagar NF
  // específica", que já devolve quanto de cada nota foi pago).
  const abrirNfsDoRateio = async (invoiceId: number) => {
    if (rateioNfsAberta === invoiceId) { setRateioNfsAberta(null); return; }
    setRateioNfsAberta(invoiceId);
    if (rateioNfsData[invoiceId]) return;
    try {
      const data = await invoicesService.getFiscalDocumentsPaymentStatus(invoiceId);
      setRateioNfsData(prev => ({ ...prev, [invoiceId]: data }));
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao carregar notas fiscais', 'error');
      setRateioNfsAberta(null);
    }
  };

  // Marcar/desmarcar uma NF recalcula o valor daquela fatura pela soma das
  // notas: o número vem do documento, não da digitação.
  const toggleNfRateio = (invoiceId: number, docId: number) => {
    setRateioNfs(prev => {
      const atual = new Set(prev[invoiceId] || []);
      if (atual.has(docId)) atual.delete(docId); else atual.add(docId);
      const docs = nfsUteis(rateioNfsData[invoiceId]);
      const soma = docs
        .filter((d: any) => atual.has(d.id))
        .reduce((acc: number, d: any) => acc + nfSaldo(d), 0);
      setRateioValores(v => ({ ...v, [invoiceId]: atual.size ? soma.toFixed(2) : '' }));
      return { ...prev, [invoiceId]: atual };
    });
  };

  const handleParentSearch = (q: string) => {
    setParentSearchQuery(q);
    if (parentSearchTimeout.current) clearTimeout(parentSearchTimeout.current);
    if (!q || q.length < 2) { setParentSearchResults([]); return; }
    parentSearchTimeout.current = setTimeout(async () => {
      setParentSearchLoading(true);
      try {
        const data = await bankTransactionsService.searchExpenses(q, selectedAccount?.id);
        setParentSearchResults(Array.isArray(data) ? data : []);
        setParentSearchErro(null);
      } catch (e) {
        setParentSearchResults([]);
        setParentSearchErro(mensagemDoErroDeCarga(e));
      }
      setParentSearchLoading(false);
    }, 400);
  };

  const handleCreateExpenseAndReconcile = async () => {
    // A trava do botão é a primeira linha; esta é a segunda, para qualquer caminho que chame o criar.
    if (porqueNaoCriar) { showToast(porqueNaoCriar, 'error'); return; }
    if (!newExpense.categoryId) { showToast('Selecione uma categoria', 'error'); return; }
    if (!newExpense.description.trim()) { showToast('Preencha a descricao', 'error'); return; }
    setSaving(true);
    try {
      let supplierId = newExpense.supplierId ? Number(newExpense.supplierId) : undefined;
      // Create new supplier if needed
      if (createSupplierMode && newExpense.newSupplierName.trim()) {
        const newSup = await suppliersService.create({ name: newExpense.newSupplierName.trim() });
        supplierId = newSup.id;
      }
      const recurringDay = newExpense.createRecurring ? parseInt(newExpense.recurringDayOfMonth, 10) : undefined;
      if (newExpense.createRecurring && (!recurringDay || recurringDay < 1 || recurringDay > 31)) {
        showToast('Dia do vencimento da recorrência deve estar entre 1 e 31', 'error');
        setSaving(false);
        return;
      }
      const res = await bankTransactionsService.createExpenseAndReconcile(transaction.id, {
        description: newExpense.description.trim(),
        categoryId: Number(newExpense.categoryId),
        supplierId,
        bankAccountId: selectedAccount?.id,
        paymentMethod: newExpense.paymentMethod || undefined,
        notes: newExpense.notes || undefined,
        parentExpenseId: selectedParent ? Number(selectedParent.id) : undefined,
        createRecurring: newExpense.createRecurring || undefined,
        recurringDayOfMonth: recurringDay,
      });
      let generatedSuffix = '';
      if (res?.recurring && newExpense.createRecurring && newExpense.generateUntil) {
        const txMonth = String(transaction.date).split('T')[0].substring(0, 7);
        const [ty, tm] = txMonth.split('-').map(Number);
        const nextMonth = tm === 12 ? `${ty + 1}-01` : `${ty}-${String(tm + 1).padStart(2, '0')}`;
        if (newExpense.generateUntil >= nextMonth) {
          try {
            const gen = await recurringExpensesService.generateRange({
              fromMonth: nextMonth,
              toMonth: newExpense.generateUntil,
              recurringExpenseId: res.recurring.id,
            });
            generatedSuffix = ` · ${gen.generated} parcela${gen.generated !== 1 ? 's' : ''} antecipada${gen.generated !== 1 ? 's' : ''}`;
          } catch (e) {
            // Não desfaz a conciliação, mas DIZ: o toast dizia "recorrência cadastrada" e escondia que as parcelas até
            // ${generateUntil} não foram geradas.
            generatedSuffix = ` · as parcelas até ${newExpense.generateUntil} NÃO foram geradas (${mensagemDoErroDeCarga(e)}); gere em Despesas → Recorrentes`;
          }
        }
      }
      showToast(res?.recurring
        ? `Despesa criada, transação conciliada e recorrência cadastrada${generatedSuffix}`
        : 'Despesa criada e transacao conciliada');
      onReconciled(res);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao criar despesa', 'error');
    }
    setSaving(false);
  };

  const isPaid = (match: any) => match.matchReason?.includes('já paga');

  const renderMatchCard = (match: any, idx: number, isBestMatch = false) => {
    const isSelected = multiSelect && match.type === 'invoice' && selectedInvoices.some(m => m.id === match.id);
    const handleClick = () => {
      if (multiSelect && match.type === 'invoice') {
        toggleInvoiceSelection(match);
      } else {
        handleReconcile(match);
      }
    };
    // Casamento por documento fiscal (órgão paga NFSe/NFCom separados): o valor a aplicar
    // é o partialAmount (= valor do PIX = valor da NFSe/NFCom), não o total da fatura.
    const isFiscalDocMatch = match.type === 'invoice' && match.partialAmount != null;
    const hasPartial = match.type === 'invoice' && !isFiscalDocMatch && match.remainingAmount != null && Number(match.remainingAmount) < Number(match.amount);
    const matchAmount = isFiscalDocMatch ? Number(match.partialAmount) : hasPartial ? Number(match.remainingAmount) : Number(match.amount);
    const amountDiff = Math.abs(matchAmount - txAmount);
    const isExactMatch = amountDiff < 0.01;
    const isCloseMatch = amountDiff < txAmount * 0.05 && !isExactMatch;
    const matchReason = (match.matchReason || '').replace('✓ já paga', '').replace(/,\s*$/, '').replace(/^,\s*/, '').trim();
    const typeColor = match.type === 'expense' ? 'bg-red-400' : match.type === 'multi_invoice' ? 'bg-purple-400' : isPaid(match) ? 'bg-slate-400' : 'bg-green-400';
    const typeLabel = match.type === 'expense' ? 'Despesa' : match.type === 'multi_invoice' ? 'Múltiplas' : 'Fatura';

    // Parse description: "Desc - Supplier (Category) [Grupo: N itens]" or "Fatura XXX - Cliente"
    const desc = match.description || '';
    const mainDesc = desc.split(' - ')[0].split(' (')[0].split(' [')[0];
    const subParts: string[] = [];
    if (desc.includes(' - ')) subParts.push(desc.split(' - ')[1]?.split(' (')[0]?.split(' [')[0] || '');
    if (desc.includes('(') && desc.includes(')')) subParts.push(desc.match(/\(([^)]+)\)/)?.[1] || '');

    return (
      <button
        key={`${match.type}-${match.id || idx}-${idx}`}
        onClick={handleClick}
        disabled={saving}
        className={`text-left rounded-lg border p-3 hover:border-slate-500 hover:bg-slate-700/80 transition-all disabled:opacity-50 ${
          isSelected ? 'border-green-500/40 bg-green-500/10' :
          isBestMatch ? 'border-green-400/60 bg-green-500/10 ring-1 ring-green-500/30' :
          match.otherClient || match.otherSupplier ? 'border-amber-500/40 bg-amber-500/5' :
          isExactMatch ? 'border-green-500/40 bg-green-500/5' :
          isCloseMatch ? 'border-yellow-500/30 bg-yellow-500/5' :
          'border-slate-600/50 bg-slate-700/30'
        }`}
      >
        {/* Fatura de OUTRO cliente: o extrato identificou o pagador e não é
            este. Só aparece quando o valor bate exato, e sempre no fim da
            lista — clicar aqui daria a fatura de um cliente como paga com o
            dinheiro de outro. */}
        {match.otherClient && (
          <div className="mb-1.5">
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-400 font-medium">
              ⚠ Outro cliente — confira antes
            </span>
          </div>
        )}
        {/* Despesa de outro fornecedor: o extrato nomeia uma contraparte e a
            poda não deixou nenhuma candidata dela. Aparece só por semelhança
            de valor, marcada, para a tela não ficar vazia. */}
        {match.otherSupplier && (
          <div className="mb-1.5">
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-400 font-medium">
              ⚠ Outro fornecedor — confira antes
            </span>
          </div>
        )}
        {isBestMatch && !multiSelect && (
          <div className="mb-1.5">
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-green-500/20 text-green-400 font-medium">Melhor correspondencia</span>
          </div>
        )}
        {multiSelect && match.type === 'invoice' && (
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-slate-500'}`}>
              {isSelected && <Icon name="check" className="w-2.5 h-2.5 text-white" />}
            </div>
            <span className="text-[10px] text-slate-500">{isSelected ? 'Selecionada' : 'Clique para selecionar'}</span>
          </div>
        )}
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${typeColor}`} />
              <p className="text-sm font-medium text-slate-200 truncate">{mainDesc}</p>
            </div>
            {subParts.filter(Boolean).length > 0 && (
              <p className="text-xs text-slate-400 mt-0.5 truncate ml-3.5">{subParts.filter(Boolean).join(' · ')}</p>
            )}
            <div className="flex items-center gap-2 mt-1 ml-3.5">
              {match.dueDate && <span className="text-xs text-slate-500">Venc: {fmtDate(match.dueDate)}</span>}
              {matchReason && <span className="text-xs text-slate-500">{matchReason}</span>}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className={`font-bold text-sm ${isExactMatch ? 'text-green-400' : TONE_TEXT.warning}`}>R$ {fmt(matchAmount)}</span>
            {isFiscalDocMatch && <span className="block text-[10px] text-slate-500">parcial · total R$ {fmt(match.amount)}</span>}
            {hasPartial && <span className="block text-[10px] text-slate-500">saldo · total R$ {fmt(match.amount)}</span>}
            <div className="flex items-center gap-1 justify-end mt-1">
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                match.type === 'expense' ? 'bg-red-500/20 text-red-400' :
                isPaid(match) ? TONE_BADGE.neutral :
                'bg-green-500/20 text-green-400'
              }`}>{isPaid(match) ? 'Paga' : typeLabel}</span>
              {match.score > 0 && <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${match.score >= 70 ? TONE_BADGE.open : match.score >= 40 ? TONE_BADGE.warning : 'bg-slate-600 text-slate-300'}`}>{match.score}%</span>}
            </div>
            {match.isGroup && (
              <span className="block mt-1 text-[10px] text-purple-400">Grupo: {match.childrenCount} {match.childrenCount === 1 ? 'item' : 'itens'}</span>
            )}
          </div>
        </div>
        {isExactMatch && (
          <div className="mt-2 text-[10px] text-green-400 font-medium">Valor exato</div>
        )}
        {match.type === 'invoice' && !multiSelect && !isPaid(match) && (
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); openNfPicker(match); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openNfPicker(match); } }}
            className="mt-2 text-[10px] text-slate-300 hover:text-white font-medium inline-block cursor-pointer"
          >
            📋 Pagar NF específica
          </span>
        )}
      </button>
    );
  };

  // Keep row format for invoice search (multi-select needs full-width rows)
  const renderMatchRow = (match: any, idx: number) => {
    const isSelected = multiSelect && match.type === 'invoice' && selectedInvoices.some(m => m.id === match.id);
    const handleClick = () => {
      if (multiSelect && match.type === 'invoice') {
        toggleInvoiceSelection(match);
      } else {
        handleReconcile(match);
      }
    };
    return (
      <button key={`${match.type}-${match.id}-${idx}`} onClick={handleClick} disabled={saving} className={`w-full flex items-center justify-between rounded-lg px-4 py-3 hover:bg-slate-700 transition-colors text-left group disabled:opacity-50 ${isSelected ? 'bg-green-500/10 border border-green-500/30' : match.otherClient || match.otherSupplier ? 'bg-amber-500/5 border border-amber-500/30' : 'bg-slate-700/50'}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {multiSelect && match.type === 'invoice' && (
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-slate-500'}`}>
              {isSelected && <Icon name="check" className="w-3 h-3 text-white" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${match.type === 'expense' ? 'bg-red-400' : match.type === 'multi_invoice' ? 'bg-purple-400' : isPaid(match) ? 'bg-slate-400' : 'bg-green-400'}`} />
              <p className="text-slate-200 text-sm font-medium">{match.description}</p>
              {match.otherClient && (
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-400 font-medium flex-shrink-0">⚠ Outro cliente</span>
              )}
              {match.otherSupplier && (
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-400 font-medium flex-shrink-0">⚠ Outro fornecedor</span>
              )}
              {isPaid(match) && <span className={`px-1.5 py-0.5 text-xs rounded ${TONE_BADGE.neutral} font-medium flex-shrink-0`}>Paga</span>}
            </div>
            <div className="flex items-center gap-3 ml-4 mt-1">
              {match.dueDate && <span className="text-slate-500 text-xs">Venc: {fmtDate(match.dueDate)}</span>}
              {match.matchReason && <span className="text-slate-500 text-xs">{match.matchReason.replace('✓ já paga', '').replace(/,\s*$/, '').replace(/^,\s*/, '')}</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 ml-4 flex-shrink-0">
          {(() => {
            const hasPartial = match.remainingAmount != null && Number(match.remainingAmount) < Number(match.amount);
            const display = hasPartial ? Number(match.remainingAmount) : Number(match.amount);
            return (
              <>
                <span className="text-white font-bold text-sm">R$ {fmt(display)}</span>
                {hasPartial && <span className="text-[10px] text-slate-500">saldo · total R$ {fmt(match.amount)}</span>}
              </>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          {match.score > 0 && <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${match.score >= 70 ? TONE_BADGE.open : match.score >= 40 ? TONE_BADGE.warning : 'bg-slate-600 text-slate-300'}`}>{match.score}%</span>}
          {!multiSelect && <span className="text-green-400 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold ml-1">Vincular</span>}
        </div>
      </button>
    );
  };

  return (
    <>
    {/* Altura FIXA ('fixa' da casca), no padrão do modal de cliente: Sugestões
        costuma trazer dois cards e Vincular Despesa traz a grade do mês inteira,
        então a caixa crescia e encolhia a cada troca de aba e as abas fugiam do
        cursor. Só o miolo rola. As abas agora são as da casca (fixas acima do
        corpo); não fecha ao clicar fora (como já era). */}
    <CascaDeModal
      largura="xl"
      altura="fixa"
      titulo="Conciliar Transação"
      subtitulo={`${fmtDate(transaction.date)} · ${transaction.description || ''}`}
      onClose={onClose}
      faixa={[{
        k: transaction.type === 'credit' ? 'Crédito' : 'Débito',
        v: `${transaction.type === 'credit' ? '+' : '-'} R$ ${fmt(Math.abs(transaction.amount))}`,
        cls: transaction.type === 'credit' ? TONE_TEXT.open : TONE_TEXT.danger,
      }]}
      abas={[
        { chave: 'suggestions', rotulo: `Sugestões${suggestions.length > 0 ? ` (${suggestions.length})` : ''}` },
        ...(transaction.type === 'credit' ? [{ chave: 'search', rotulo: 'Pesquisar Fatura' }] : []),
        ...(transaction.type === 'debit'
          ? [{ chave: 'search_expense', rotulo: 'Vincular Despesa' }, { chave: 'create', rotulo: 'Criar Despesa' }]
          : []),
      ]}
      abaAtiva={activeTab}
      onAba={(k) => setActiveTab(k as any)}
      corpoClassName="p-6 space-y-4"
      rodape={<button onClick={onClose} className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>}
    >

          {/* Diagnóstico antes da escolha: a tela já sabe que nenhuma sugestão
              fecha sozinha com o depósito, então diz isso e oferece a ação em vez
              de esperar o operador descobrir a caixinha de múltiplas faturas. O
              clique num card sozinho aplicaria o valor CHEIO do depósito naquela
              fatura, consumindo a transação e escondendo que parte era de outra. */}
          {transaction.type === 'credit' && !multiSelect && !sugLoading && precisaDistribuir && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <p className="text-sm text-amber-300 font-medium">
                Nenhuma fatura sozinha fecha com R$ {fmt(txAmount)}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                As {faturasSugeridas.length} faturas sugeridas somam R$ {fmt(totalSugerido)}. Se o depósito
                cobre parte de mais de uma (órgão que paga por empenho, por exemplo), distribua entre elas.
              </p>
              <button
                onClick={() => { setMultiSelect(true); setSelectedInvoices(faturasSugeridas); }}
                className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors"
              >
                Distribuir entre as {faturasSugeridas.length} faturas
              </button>
            </div>
          )}

          {/* Multi-select toggle for credits */}
          {transaction.type === 'credit' && (activeTab === 'suggestions' || activeTab === 'search') && (
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={multiSelect} onChange={e => { setMultiSelect(e.target.checked); if (!e.target.checked) setSelectedInvoices([]); }} className="rounded border-slate-500 bg-slate-700 focus:ring-primary" />
                <span className="text-slate-400 text-sm">Selecionar multiplas faturas</span>
              </label>
              {multiSelect && (
                <div className="flex items-center gap-3">
                  {(() => {
                    const visibleInvoices = (activeTab === 'search' ? searchResults : suggestions).filter((m: any) => m.type === 'invoice');
                    const allSelected = visibleInvoices.length > 0 && visibleInvoices.every((m: any) => selectedInvoices.some(s => s.id === m.id));
                    return (
                      <button
                        onClick={() => {
                          if (allSelected) {
                            setSelectedInvoices([]);
                          } else {
                            setSelectedInvoices(visibleInvoices);
                          }
                        }}
                        className="text-xs text-slate-300 hover:text-white transition-colors"
                      >
                        {allSelected ? 'Desmarcar todas' : 'Selecionar todas'}
                      </button>
                    );
                  })()}
                  {selectedInvoices.length > 0 && (
                    <>
                      <span className="text-slate-400 text-xs">{selectedInvoices.length} selecionada{selectedInvoices.length !== 1 ? 's' : ''}</span>
                      <span className={`font-bold text-sm ${Math.abs(selectedTotal - txAmount) < 0.50 ? 'text-green-400' : TONE_TEXT.warning}`}>
                        R$ {fmt(selectedTotal)} / R$ {fmt(txAmount)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Multi-select action bar */}
          {multiSelect && selectedInvoices.length > 0 && (() => {
            const somaAtual = rateioMode ? rateioTotal : selectedTotal;
            const diff = somaAtual - txAmount;
            const matches = Math.abs(diff) < 0.01;
            const estouraSaldo = rateioMode && selectedInvoices.some(m => {
              const saldo = Number(m.remainingAmount ?? m.amount);
              return (Number(rateioValores[m.id]) || 0) > saldo + saldo * 0.2 + 0.5;
            });
            return (
              <div className="space-y-2">
                <div className={`flex items-center justify-between rounded-lg px-4 py-2.5 text-sm border ${matches ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <span className="text-slate-300">{rateioMode ? 'Soma distribuída' : 'Soma dos saldos selecionados'}</span>
                  <span className={`font-bold ${matches ? 'text-green-400' : 'text-yellow-400'}`}>
                    R$ {fmt(somaAtual)}
                    {matches
                      ? ' ✓ bate com a transação'
                      : ` (${diff > 0 ? 'sobra' : 'falta'} R$ ${fmt(Math.abs(diff))})`}
                  </span>
                </div>

                {/* Depósito menor que as faturas = pagamento parcial, ponto.
                    Não é escolha do operador, então não é caixinha: a tela
                    avisa e já abre a distribuição. */}
                {depositoMenor ? (
                  <div className={`px-3 py-2 rounded-lg ${TONE_SURFACE.progress} border ${TONE_BORDER.progress} text-sm`}>
                    <span className={`${TONE_TEXT.progress} font-medium`}>Pagamento parcial</span>
                    <span className="text-slate-400">
                      {' '}— o depósito de R$ {fmt(txAmount)} não cobre as {selectedInvoices.length} fatura(s)
                      selecionada(s) (R$ {fmt(selectedTotal)}).
                      {Object.values(rateioNfs).some(set => set.size > 0)
                        ? ' Distribuído pelas notas fiscais em aberto — confira antes de confirmar.'
                        : ' Não deu para deduzir pelas notas fiscais: informe quanto cada fatura recebeu.'}
                    </span>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer px-1">
                    <input
                      type="checkbox"
                      checked={rateioMode}
                      onChange={e => ligarRateio(e.target.checked)}
                      className="rounded border-slate-500 bg-slate-700 focus:ring-primary"
                    />
                    <span className="text-slate-400 text-sm">
                      Dividir o depósito entre as faturas (pagamento parcial)
                    </span>
                  </label>
                )}

                {rateioMode && (
                  <div className="space-y-1.5 max-h-[22rem] overflow-y-auto pr-1">
                    {selectedInvoices.map(m => {
                      const saldo = Number(m.remainingAmount ?? m.amount);
                      const valor = Number(rateioValores[m.id]) || 0;
                      const excede = valor > saldo + saldo * 0.2 + 0.5;
                      const sobra = Math.round((saldo - valor) * 100) / 100;
                      const nfsSelecionadas = rateioNfs[m.id] || new Set<number>();
                      const nfsAberto = rateioNfsAberta === m.id;
                      const nfsDaFatura = nfsUteis(rateioNfsData[m.id]);
                      return (
                        <div key={m.id} className="bg-slate-700/40 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-200 text-sm truncate">{m.description}</p>
                              <p className="text-slate-500 text-xs">
                                saldo R$ {fmt(saldo)}
                                {valor > 0 && !excede && (sobra > 0.009
                                  ? ` · ficará Parcial, restam R$ ${fmt(sobra)}`
                                  : ' · quita a fatura')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="text-slate-500 text-xs">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={rateioValores[m.id] ?? ''}
                                onChange={e => {
                                  // Digitar por cima descarta a escolha de NFs: o
                                  // backend recusa pagamento cujo valor não bate
                                  // com as notas apontadas.
                                  setRateioValores(v => ({ ...v, [m.id]: e.target.value }));
                                  setRateioNfs(prev => (prev[m.id]?.size ? { ...prev, [m.id]: new Set<number>() } : prev));
                                }}
                                className={`w-28 px-2 py-1 bg-slate-700 border rounded text-white text-sm text-right focus:outline-none ${excede ? 'border-red-500' : 'border-slate-600 focus:border-primary'}`}
                              />
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => abrirNfsDoRateio(m.id)}
                            className="mt-1 text-xs text-slate-300 hover:text-white transition-colors"
                          >
                            {nfsSelecionadas.size > 0
                              ? `📋 ${nfsSelecionadas.size} NF(s) escolhida(s) — o valor veio delas`
                              : nfsAberto ? '📋 Fechar notas fiscais' : '📋 Somar pelas notas fiscais'}
                          </button>

                          {nfsAberto && (
                            <div className="mt-2 space-y-1">
                              {nfsDaFatura.length === 0 ? (
                                <p className="text-slate-500 text-xs">Nenhuma nota fiscal emitida para esta fatura.</p>
                              ) : nfsDaFatura.map((d: any) => {
                                const marcada = nfsSelecionadas.has(d.id);
                                const jaPaga = d.status === 'paid';
                                return (
                                  <button
                                    key={d.id}
                                    type="button"
                                    disabled={jaPaga}
                                    onClick={() => toggleNfRateio(m.id, d.id)}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${
                                      jaPaga ? 'bg-slate-800/40 opacity-50 cursor-not-allowed'
                                        : marcada ? 'bg-green-500/15 border border-green-500/40'
                                        : 'bg-slate-700/50 hover:bg-slate-700 border border-transparent'
                                    }`}
                                  >
                                    <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${marcada ? 'bg-green-500 border-green-500' : 'border-slate-500'}`}>
                                      {marcada && <Icon name="check" className="w-2 h-2 text-white" />}
                                    </div>
                                    <span className="text-slate-400 uppercase w-12 flex-shrink-0">{d.type}</span>
                                    <span className="text-white font-mono w-16 flex-shrink-0">{d.documentNumber || '—'}</span>
                                    <span className="text-slate-300 flex-1 text-right">R$ {fmt(d.totalValue)}</span>
                                    {jaPaga && <span className="text-green-400 flex-shrink-0">já paga</span>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={handleMultiReconcile}
                  disabled={saving || estouraSaldo || !matches}
                  className="w-full px-4 py-3 bg-success text-white rounded-lg hover:bg-success-strong font-medium disabled:opacity-50 transition-colors"
                >
                  {saving
                    ? 'Conciliando...'
                    : estouraSaldo
                      ? 'Há valor acima do saldo da fatura'
                      : !matches
                        ? rateioMode
                          ? 'A divisão precisa somar o valor da transação'
                          : `Faltam R$ ${fmt(Math.abs(diff))} para fechar com a transação`
                        : rateioMode
                          ? `Conciliar pagamento parcial de ${selectedInvoices.length} fatura${selectedInvoices.length !== 1 ? 's' : ''} (R$ ${fmt(somaAtual)})`
                          : `Conciliar ${selectedInvoices.length} fatura${selectedInvoices.length !== 1 ? 's' : ''} (R$ ${fmt(somaAtual)})`}
                </button>
              </div>
            );
          })()}

          {/* Suggestions tab */}
          {activeTab === 'suggestions' && (
            <>
              {sugLoading ? (
                <div className="py-8 text-center text-slate-400 text-sm">Buscando correspondencias...</div>
              ) : cargaDasSugestoes.estado === 'erro' ? (
                <EstadoDaLista carga={cargaDasSugestoes} temDados={false} oQue="as sugestões" onTentarDeNovo={lerSugestoes} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>{null}</EstadoDaLista>
              ) : suggestions.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-1">
                  {suggestions.map((match: any, idx: number) => renderMatchCard(match, idx, idx === 0 && suggestions.length > 1 && !match.otherSupplier && match.score >= 50 && match.score > (suggestions[1]?.score || 0)))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-slate-500 text-sm">Nenhuma sugestao encontrada</p>
                  <p className="text-slate-600 text-xs mt-1">Use a aba "Pesquisar Fatura" para buscar por cliente ou codigo</p>
                </div>
              )}
            </>
          )}

          {/* Search tab */}
          {activeTab === 'search' && (
            <>
              <div className="relative">
                <input
                  type="text"
                  className={`w-full px-4 py-3 ${campo} rounded-lg pl-10`}
                  placeholder="Pesquisar por cliente, codigo da fatura ou valor..."
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  autoFocus
                />
                <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchLoading ? (
                <div className="py-4 text-center text-slate-400 text-sm">Buscando...</div>
              ) : searchResults.length > 0 ? (
                multiSelect ? (
                  <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
                    {searchResults.map((match: any, idx: number) => renderMatchRow(match, idx))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-1">
                    {searchResults.map((match: any, idx: number) => renderMatchCard(match, idx))}
                  </div>
                )
              ) : searchQuery.length >= 2 && searchErro ? (
                <p role="alert" className={`text-sm text-center py-4 ${TONE_TEXT.warning}`} data-testid="busca-erro">Não consegui buscar faturas: {searchErro}.</p>
              ) : searchQuery.length >= 2 ? (
                <p className="text-slate-500 text-sm text-center py-4">Nenhuma fatura encontrada para "{searchQuery}"</p>
              ) : (
                <p className="text-slate-600 text-xs text-center py-4">Digite pelo menos 2 caracteres para pesquisar</p>
              )}
            </>
          )}

          {/* Search Expense tab */}
          {activeTab === 'search_expense' && (
            <>
              {/* Month selector */}
              <div className="flex items-center justify-between">
                <button onClick={() => navigateExpenseMonth(-1)} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors text-sm">
                  &larr;
                </button>
                <span className="text-white font-medium text-sm">{expenseMonthLabel}</span>
                <button onClick={() => navigateExpenseMonth(1)} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors text-sm">
                  &rarr;
                </button>
              </div>
              {/* Search filter */}
              <div className="relative">
                <input
                  type="text"
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg pl-10 text-sm`}
                  placeholder="Filtrar despesas..."
                  value={expSearchQuery}
                  onChange={e => handleExpenseSearch(e.target.value)}
                />
                <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {expMonthLoading ? (
                <div className="py-8 text-center text-slate-400 text-sm">Carregando despesas...</div>
              ) : cargaDasDespesasDoMes.estado === 'erro' ? (
                <EstadoDaLista carga={cargaDasDespesasDoMes} temDados={false} oQue="as despesas do mês" onTentarDeNovo={lerDespesasDoMes} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>{null}</EstadoDaLista>
              ) : filteredExpenses.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-1">
                  {filteredExpenses.map((exp: any, idx: number) => {
                    const expAmount = Number(exp.amount);
                    const amountDiff = Math.abs(expAmount - txAmount);
                    const isExactMatch = amountDiff < 0.01;
                    const isCloseMatch = amountDiff < txAmount * 0.05 && !isExactMatch;
                    const statusColors: any = {
                      pendente: 'bg-yellow-500/20 text-yellow-400',
                      vencido: 'bg-red-500/20 text-red-400',
                      parcial: TONE_BADGE.progress,
                      pago: 'bg-green-500/20 text-green-400',
                    };
                    const statusLabel: any = {
                      pendente: 'Pendente',
                      vencido: 'Vencido',
                      parcial: 'Parcial',
                      pago: 'Pago',
                    };
                    const status = (exp.matchReason || '').includes('já paga') ? 'pago' : (exp.matchReason || '').toLowerCase();
                    return (
                      <button
                        key={`exp-${exp.id}-${idx}`}
                        onClick={() => handleReconcile(exp)}
                        disabled={saving}
                        className={`text-left rounded-lg border p-3 hover:border-slate-500 hover:bg-slate-700/80 transition-all disabled:opacity-50 ${isExactMatch ? 'border-green-500/40 bg-green-500/5' : isCloseMatch ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-slate-600/50 bg-slate-700/30'}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-200 truncate">{(exp.description || '').split(' - ')[0].split(' (')[0]}</p>
                            <p className="text-xs text-slate-400 mt-0.5 truncate">
                              {exp.description.includes(' - ') ? exp.description.split(' - ')[1]?.split(' (')[0] : ''}
                              {exp.description.includes('(') && exp.description.includes(')') ? ` · ${exp.description.match(/\(([^)]+)\)/)?.[1] || ''}` : ''}
                            </p>
                            {exp.dueDate && <p className="text-xs text-slate-500 mt-1">Venc: {fmtDate(exp.dueDate)}</p>}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className={`font-bold text-sm ${isExactMatch ? 'text-green-400' : TONE_TEXT.warning}`}>R$ {fmt(exp.amount)}</span>
                            {statusLabel[status] && (
                              <span className={`block mt-1 px-1.5 py-0.5 text-[10px] rounded font-medium ${statusColors[status] || 'bg-slate-600 text-slate-300'}`}>
                                {statusLabel[status]}
                              </span>
                            )}
                            {exp.isGroup && (
                              <span className="block mt-1 text-[10px] text-purple-400">Grupo: {exp.childrenCount} {exp.childrenCount === 1 ? 'item' : 'itens'}</span>
                            )}
                          </div>
                        </div>
                        {isExactMatch && (
                          <div className="mt-2 text-[10px] text-green-400 font-medium">Valor exato</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-slate-500 text-sm">Nenhuma despesa encontrada em {expenseMonthLabel}</p>
                  <p className="text-slate-600 text-xs mt-1">Use as setas para navegar entre meses</p>
                </div>
              )}
            </>
          )}

          {/* Create Expense tab */}
          {activeTab === 'create' && (
            <div className="space-y-4">
              {porqueNaoCriar && (
                <p role="alert" className={`px-3 py-2 rounded-lg border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-criar="travado">
                  {porqueNaoCriar}
                </p>
              )}
              <div className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs">Valor da despesa (da transacao)</p>
                <p className="text-red-400 font-bold text-lg">R$ {fmt(Math.abs(transaction.amount))}</p>
              </div>

              {/* Só aparece enquanto o palpite continua valendo — trocou o fornecedor à mão, some. */}
              {identificado?.supplierId && String(identificado.supplierId) === newExpense.supplierId && (
                <div className={`rounded-lg p-3 border text-xs ${identificado.confident
                  ? 'bg-emerald-900/20 border-emerald-700/50 text-emerald-300'
                  : 'bg-amber-900/20 border-amber-700/50 text-amber-300'}`}>
                  <span className="font-semibold">
                    {identificado.confident ? 'Identificado automaticamente' : 'Palpite pelo nome — confira'}
                  </span>{' '}
                  <span className="text-slate-300">
                    fornecedor <strong>{identificado.supplierName}</strong>
                    {identificado.matchedBy === 'cnpj' && ' pelo CNPJ da descrição'}
                    {identificado.matchedBy === 'documento' && ' pelo documento do extrato'}
                    {identificado.matchedBy === 'nome' && ' pelo nome na descrição'}
                    {identificado.categoryName
                      ? `, categoria ${identificado.categoryName} (padrão do fornecedor).`
                      : '. Este fornecedor não tem categoria padrão — escolha abaixo.'}
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-400 mb-1">Descricao *</label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                  value={newExpense.description}
                  onChange={e => setNewExpense(p => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Categoria *</label>
                <select
                  className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                  value={newExpense.categoryId}
                  onChange={e => setNewExpense(p => ({ ...p, categoryId: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  {opcaoAtualForaDaLista(categories, newExpense.categoryId, identificado?.categoryName || `categoria #${newExpense.categoryId}`, cargaDoFormulario.estado === 'erro')}
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm text-slate-400">Fornecedor</label>
                  <button
                    type="button"
                    onClick={() => { setCreateSupplierMode(!createSupplierMode); setNewExpense(p => ({ ...p, supplierId: '', newSupplierName: '' })); }}
                    // Sem a lista de fornecedores lida, "+ Novo" criaria de novo um que já existe.
                    disabled={cargaDoFormulario.estado === 'erro' && !createSupplierMode}
                    className="text-xs text-slate-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {createSupplierMode ? 'Selecionar existente' : '+ Novo fornecedor'}
                  </button>
                </div>
                {createSupplierMode ? (
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                    placeholder="Nome do novo fornecedor..."
                    value={newExpense.newSupplierName}
                    onChange={e => setNewExpense(p => ({ ...p, newSupplierName: e.target.value }))}
                  />
                ) : (
                  <select
                    className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                    value={newExpense.supplierId}
                    onChange={e => {
                      const supId = e.target.value;
                      const sup = suppliersList.find((s: any) => String(s.id) === supId);
                      setNewExpense(p => ({
                        ...p,
                        supplierId: supId,
                        ...(sup?.defaultCategoryId && !p.categoryId ? { categoryId: String(sup.defaultCategoryId) } : {}),
                      }));
                    }}
                  >
                    <option value="">Nenhum (opcional)</option>
                    {opcaoAtualForaDaLista(suppliersList, newExpense.supplierId, identificado?.supplierName || `fornecedor #${newExpense.supplierId}`, cargaDoFormulario.estado === 'erro')}
                    {suppliersList.map((sup: any) => (
                      <option key={sup.id} value={sup.id}>{sup.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Adicionar ao grupo (opcional)</label>
                {selectedParent ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-lg border border-slate-500">
                    <span className="text-white text-sm flex-1 truncate">{selectedParent.description}</span>
                    <span className="text-slate-200 text-xs font-mono">R$ {Number(selectedParent.amount).toFixed(2)}</span>
                    <button
                      type="button"
                      onClick={() => { setSelectedParent(null); setParentSearchQuery(''); setParentSearchResults([]); }}
                      className="text-slate-400 hover:text-red-400 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                      placeholder="Buscar despesa pai..."
                      value={parentSearchQuery}
                      onChange={e => handleParentSearch(e.target.value)}
                    />
                    {parentSearchLoading && <p className="text-xs text-slate-500 mt-1">Buscando...</p>}
                    {parentSearchErro && parentSearchQuery.length >= 2 && (
                      <p role="alert" className={`text-xs mt-1 ${TONE_TEXT.warning}`} data-testid="busca-mae-erro">Não consegui buscar despesas: {parentSearchErro}.</p>
                    )}
                    {parentSearchResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-central-elevado border border-slate-600 rounded-lg shadow-lg">
                        {parentSearchResults.map((exp: any) => (
                          <button
                            key={exp.id}
                            type="button"
                            onClick={() => {
                              setSelectedParent(exp);
                              setParentSearchQuery('');
                              setParentSearchResults([]);
                              if (!newExpense.categoryId && exp.categoryId) {
                                setNewExpense(p => ({ ...p, categoryId: String(exp.categoryId) }));
                              }
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center justify-between"
                          >
                            <span className="text-white text-sm truncate">{exp.description}</span>
                            <span className="text-slate-400 text-xs font-mono ml-2">R$ {Number(exp.amount).toFixed(2)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Forma de Pagamento</label>
                  <select
                    className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                    value={newExpense.paymentMethod}
                    onChange={e => setNewExpense(p => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="debito_automatico">Debito Automatico</option>
                    <option value="cartao_credito">Cartao de Credito</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Observacoes</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                    placeholder="Opcional..."
                    value={newExpense.notes}
                    onChange={e => setNewExpense(p => ({ ...p, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-slate-600/60 bg-slate-700/30 p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newExpense.createRecurring}
                    onChange={e => setNewExpense(p => ({ ...p, createRecurring: e.target.checked }))}
                    className="accent-primary"
                  />
                  <span className="text-sm text-slate-200 font-medium">Criar também recorrência mensal</span>
                </label>
                {newExpense.createRecurring && (
                  <div className="pl-6 space-y-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Dia do vencimento (1-31)</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        className={`w-24 px-3 py-2 ${campo} rounded-lg text-sm`}
                        value={newExpense.recurringDayOfMonth}
                        onChange={e => setNewExpense(p => ({ ...p, recurringDayOfMonth: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Gerar parcelas antecipadas até (opcional)</label>
                      <input
                        type="month"
                        className={`px-3 py-2 ${campo} rounded-lg text-sm`}
                        value={newExpense.generateUntil}
                        onChange={e => setNewExpense(p => ({ ...p, generateUntil: e.target.value }))}
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Cria as despesas do próximo mês até o mês escolhido (default: dezembro do ano da transação). Deixe em branco para deixar o cron gerar mês a mês.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleCreateExpenseAndReconcile}
                disabled={saving || !newExpense.categoryId || !newExpense.description.trim() || !!porqueNaoCriar}
                title={porqueNaoCriar}
                className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? 'Criando...' : (newExpense.createRecurring ? 'Criar Despesa, Recorrência e Conciliar' : 'Criar Despesa e Conciliar')}
              </button>
            </div>
          )}
    </CascaDeModal>

      {/* Sub-modal: pick specific NFs within a grouped invoice. NFs selecionadas:
          antes fechava ao clicar fora (quando não estava gravando); agora só pelo
          × ou Cancelar, que seguem travados enquanto concilia. */}
      {nfPickerInvoice && (
        <CascaDeModal
          largura="md"
          zIndex="z-[60]"
          titulo="Pagamento por Nota Fiscal"
          subtitulo={`${nfPickerInvoice.description} · Tx R$ ${fmt(txAmount)}`}
          onClose={() => { if (!saving) setNfPickerInvoice(null); }}
          corpoClassName="p-4"
          rodape={
            <>
              <div className="flex-1 text-sm">
                <p className="text-slate-400">Selecionadas: <span className="text-white font-medium">{nfPickerSelected.size}</span></p>
                <p className="text-slate-400">Total: <span className="text-white font-bold">R$ {fmt(nfPickerSelectedTotal)}</span></p>
                {/* Botão desabilitado precisa dizer POR QUÊ. Antes o rodapé só
                    informava a diferença e o operador podia somar NFs acima do
                    valor da transação sem nada impedir. */}
                {nfPickerSelectedTotal > txAmount + 0.01 ? (
                  <p className={`text-xs mt-0.5 ${TONE_TEXT.danger}`}>
                    Excede a transação em R$ {fmt(nfPickerSelectedTotal - txAmount)} — desmarque alguma NF
                  </p>
                ) : Math.abs(nfPickerSelectedTotal - txAmount) > 0.01 && nfPickerSelected.size > 0 && (
                  <p className={`text-xs mt-0.5 ${Math.abs(nfPickerSelectedTotal - txAmount) < txAmount * 0.05 ? TONE_TEXT.warning : 'text-slate-500'}`}>
                    Diferença vs transação: R$ {fmt(Math.abs(nfPickerSelectedTotal - txAmount))}
                  </p>
                )}
              </div>
              <button onClick={() => setNfPickerInvoice(null)} disabled={saving} className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 text-sm disabled:opacity-50">
                Cancelar
              </button>
              <button
                onClick={handleReconcileByNf}
                disabled={saving || nfPickerSelected.size === 0 || nfPickerSelectedTotal > txAmount + 0.01}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 text-sm"
              >
                {saving ? 'Conciliando…' : `Conciliar ${nfPickerSelected.size} NF${nfPickerSelected.size !== 1 ? 's' : ''}`}
              </button>
            </>
          }
        >
              {nfPickerLoading ? (
                <div className="text-center text-slate-400 py-8">Carregando notas fiscais…</div>
              ) : !nfPickerData || nfsUteis(nfPickerData).length === 0 ? (
                <div className="text-center text-slate-400 py-8">Esta fatura não tem notas fiscais individuais.</div>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-12 gap-2 px-2 py-1 text-[10px] uppercase text-slate-500 font-semibold">
                    <div className="col-span-1"></div>
                    <div className="col-span-2">Tipo</div>
                    <div className="col-span-2">Nº</div>
                    <div className="col-span-3 text-right">Valor</div>
                    <div className="col-span-2 text-right">Pago</div>
                    <div className="col-span-2 text-center">Status</div>
                  </div>
                  {nfsUteis(nfPickerData).map((d: any) => {
                    const isSelected = nfPickerSelected.has(d.id);
                    const isPaid = d.status === 'paid';
                    const statusClass =
                      d.status === 'paid' ? TONE_BADGE.open
                      : d.status === 'partial' ? TONE_BADGE.warning
                      : 'bg-slate-700 text-slate-400';
                    const statusLabel = d.status === 'paid' ? 'Pago' : d.status === 'partial' ? 'Parcial' : 'Pendente';
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => !isPaid && toggleNfSelected(d.id)}
                        disabled={isPaid || saving}
                        className={`w-full grid grid-cols-12 gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors ${
                          isPaid ? 'bg-slate-800/40 opacity-50 cursor-not-allowed' :
                          isSelected ? 'bg-orange-500/5 border border-orange-500/50' :
                          'bg-slate-700/40 hover:bg-slate-700/70 border border-transparent'
                        }`}
                      >
                        <div className="col-span-1 flex items-center">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-slate-500 border-slate-300' :
                            isPaid ? 'border-slate-600' : 'border-slate-500'
                          }`}>
                            {isSelected && <Icon name="check" className="w-2.5 h-2.5 text-white" />}
                          </div>
                        </div>
                        <div className="col-span-2 text-slate-300 uppercase">{d.type}</div>
                        <div className="col-span-2 text-white font-mono">{d.documentNumber || '—'}</div>
                        <div className="col-span-3 text-right text-slate-300">R$ {fmt(d.totalValue)}</div>
                        <div className="col-span-2 text-right text-green-400">
                          {d.paidAmount > 0 ? `R$ ${fmt(d.paidAmount)}` : '—'}
                        </div>
                        <div className="col-span-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>{statusLabel}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
        </CascaDeModal>
      )}
    </>
  );
};

// ============ BULK RECONCILE MODAL (multi-select transactions → one invoice) ============
const BulkReconcileModal = ({ selectedTransactions, onClose, selectedAccount, showToast, loadTransactions, onClearSelection, onKeepSelection }: {
  selectedTransactions: any[];
  onClose: () => void;
  selectedAccount: any;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  loadTransactions: (id: number, opts?: { silent?: boolean }) => Promise<void>;
  onClearSelection: () => void;
  /** Depois de uma falha no meio do lote: deixa selecionadas só as que faltam. */
  onKeepSelection?: (ids: number[]) => void;
}) => {
  const confirmar = useConfirm();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchErro, setSearchErro] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimeout = React.useRef<any>(null);

  const totalSelected = selectedTransactions.reduce((sum, tx) => sum + Math.abs(Number(tx.amount)), 0);

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await bankTransactionsService.searchInvoices(q);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchErro(null);
      } catch (e) {
        // Calava: "Nenhuma fatura encontrada para X" com a API fora, e o operador conciliava como outra coisa.
        setSearchResults([]);
        setSearchErro(mensagemDoErroDeCarga(e));
      }
      setSearchLoading(false);
    }, 400);
  };

  const handleBulkReconcile = async (invoice: any) => {
    if (!await confirmar({
      title: 'Confirmar vínculo em lote?',
      message: `${selectedTransactions.length} transação(ões) à fatura "${invoice.description?.split(' - ')[0] || invoice.description || ''}".`,
    })) return;
    setSaving(true);
    // D4 (10/10/2026): uma a uma; a falha no meio dizia só "Erro ao conciliar", sem dizer quantas tinham entrado, sem
    // recarregar, e com a seleção inteira de pé. Agora diz quantas, qual falhou e por quê, recarrega, e deixa
    // selecionadas só as que faltam. (Reenviar não duplica: o servidor recusa transação já conciliada.)
    let feitas = 0;
    try {
      for (const tx of selectedTransactions) {
        await bankTransactionsService.reconcilePartial(tx.id, { invoiceId: invoice.id });
        feitas++;
      }
      showToast(`${selectedTransactions.length} transações conciliadas com fatura ${invoice.description?.split(' - ')[0] || ''}`);
      if (selectedAccount) await loadTransactions(selectedAccount.id, { silent: true });
      onClearSelection();
      onClose();
    } catch (err: any) {
      const falhou = selectedTransactions[feitas];
      const faltam = selectedTransactions.slice(feitas).map((t) => t.id);
      showToast(
        `${feitas} de ${selectedTransactions.length} conciliadas; a de ${fmtDate(falhou?.date)} (R$ ${fmt(Math.abs(Number(falhou?.amount)))}) falhou: `
        + `${err.response?.data?.message || mensagemDoErroDeCarga(err)}. As ${faltam.length} que faltam seguem selecionadas.`,
        'error',
      );
      if (feitas > 0 && selectedAccount) await loadTransactions(selectedAccount.id, { silent: true });
      if (onKeepSelection) onKeepSelection(faltam);
      if (feitas > 0) onClose();
    }
    setSaving(false);
  };

  return (
    // Busca digitada: não fecha ao clicar fora (como já era).
    <CascaDeModal
      largura="md"
      titulo={`Conciliar ${selectedTransactions.length} Transações com Fatura`}
      onClose={onClose}
      corpoClassName="p-6 space-y-4"
      rodape={<button onClick={onClose} className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>}
    >
          {/* Selected transactions summary */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <p className="text-slate-400 text-xs mb-2">Transações selecionadas ({selectedTransactions.length})</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {selectedTransactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate flex-1">{tx.description}</span>
                  <span className="text-green-400 font-medium ml-3">R$ {fmt(Math.abs(tx.amount))}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-600 mt-2 pt-2 flex justify-between">
              <span className="text-slate-400 text-sm font-medium">Total</span>
              <span className="text-green-400 font-bold">R$ {fmt(totalSelected)}</span>
            </div>
          </div>

          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              className={`w-full px-4 py-3 ${campo} rounded-lg pl-10`}
              placeholder="Pesquisar fatura por cliente, código ou valor..."
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              autoFocus
            />
            <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Search results */}
          {searchLoading ? (
            <div className="py-4 text-center text-slate-400 text-sm">Buscando...</div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {searchResults.map((match: any, idx: number) => {
                const invoiceAmount = Number(match.amount);
                const diff = Math.abs(invoiceAmount - totalSelected);
                const isExactMatch = diff < 0.01;
                const isCloseMatch = diff < totalSelected * 0.10 && !isExactMatch;
                const desc = match.description || '';
                const mainDesc = desc.split(' - ')[0].split(' (')[0];
                const clientName = desc.includes(' - ') ? desc.split(' - ')[1]?.split(' (')[0] : '';

                return (
                  <button
                    key={`${match.id}-${idx}`}
                    onClick={() => handleBulkReconcile(match)}
                    disabled={saving}
                    className={`w-full text-left rounded-lg border p-4 hover:border-slate-500 hover:bg-slate-700/80 transition-all disabled:opacity-50 ${
                      isExactMatch ? 'border-green-500/40 bg-green-500/10 ring-1 ring-green-500/30' :
                      isCloseMatch ? 'border-yellow-500/30 bg-yellow-500/5' :
                      'border-slate-600/50 bg-slate-700/30'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                          <p className="text-sm font-medium text-slate-200">{mainDesc}</p>
                        </div>
                        {clientName && <p className="text-xs text-slate-400 mt-0.5 ml-4">{clientName}</p>}
                        {match.dueDate && <p className="text-xs text-slate-500 mt-1 ml-4">Venc: {fmtDate(match.dueDate)}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`font-bold text-sm ${isExactMatch ? 'text-green-400' : TONE_TEXT.warning}`}>R$ {fmt(match.amount)}</span>
                        {isExactMatch && <p className="text-[10px] text-green-400 mt-1">Valor exato</p>}
                        {isCloseMatch && <p className="text-[10px] text-yellow-400 mt-1">Valor aproximado</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : searchQuery.length >= 2 && searchErro ? (
            <p role="alert" className={`text-sm text-center py-4 ${TONE_TEXT.warning}`} data-testid="busca-erro">Não consegui buscar faturas: {searchErro}.</p>
          ) : searchQuery.length >= 2 ? (
            <p className="text-slate-500 text-sm text-center py-4">Nenhuma fatura encontrada para "{searchQuery}"</p>
          ) : (
            <p className="text-slate-600 text-xs text-center py-4">Digite pelo menos 2 caracteres para pesquisar</p>
          )}
    </CascaDeModal>
  );
};

const BulkReconcileExpenseModal = ({ selectedTransactions, onClose, selectedAccount, showToast, loadTransactions, onClearSelection }: {
  selectedTransactions: any[];
  onClose: () => void;
  selectedAccount: any;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  loadTransactions: (id: number, opts?: { silent?: boolean }) => Promise<void>;
  onClearSelection: () => void;
  /** Depois de uma falha no meio do lote: deixa selecionadas só as que faltam. */
  onKeepSelection?: (ids: number[]) => void;
}) => {
  const confirmar = useConfirm();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState<string>(() => {
    const firstTx = selectedTransactions[0];
    if (firstTx?.date) {
      const d = String(firstTx.date).slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(d)) return d;
    }
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const searchTimeout = React.useRef<any>(null);

  const totalSelected = selectedTransactions.reduce((sum, tx) => sum + Math.abs(Number(tx.amount)), 0);
  const bankAccountId = selectedTransactions[0]?.bankAccountId || selectedAccount?.id;

  // Falhando, zerava e dizia "Nenhuma despesa encontrada em {mês}": a mãe existia e não aparecia. Agora é estado.
  const cargaDasMaes = useEstadoDaCarga();
  const { rodar: rodarMaes } = cargaDasMaes;
  const loadResults = React.useCallback(async (q: string, m: string) => {
    setLoading(true);
    await rodarMaes(async () => {
      const data = await bankTransactionsService.searchExpenses(q, bankAccountId, m);
      const arr = Array.isArray(data) ? data : [];
      // Only keep parents (no parentExpenseId) — children can't be parents of new children
      const parents = arr.filter((e: any) => !e.parentExpenseId);
      setResults(parents);
    });
    setLoading(false);
  }, [bankAccountId, rodarMaes]);

  React.useEffect(() => {
    loadResults('', month);
  }, [month, loadResults]);

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => loadResults(q, month), 350);
  };

  const handleConfirm = async (parent: any) => {
    if (!await confirmar({ title: 'Conciliar como filhas?', message: `${selectedTransactions.length} transações como filhas de "${parent.description}".` })) return;
    setSaving(true);
    try {
      const result = await bankTransactionsService.bulkReconcileWithExpense(
        selectedTransactions.map(tx => tx.id),
        parent.id,
      );
      showToast(`${result.reconciledCount} transações vinculadas a "${parent.description}"`);
      if (selectedAccount) await loadTransactions(selectedAccount.id, { silent: true });
      onClearSelection();
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao conciliar', 'error');
    }
    setSaving(false);
  };

  const adjMonth = (delta: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    const names = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${names[m - 1]} ${y}`;
  })();

  return (
    // Busca e mês escolhidos: não fecha ao clicar fora (como já era).
    <CascaDeModal
      largura="md"
      titulo={`Conciliar ${selectedTransactions.length} Transações como Filhas de uma Despesa`}
      onClose={onClose}
      corpoClassName="p-6 space-y-4"
      rodape={
        <button onClick={onClose} className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium" disabled={saving}>
          {saving ? 'Conciliando...' : 'Cancelar'}
        </button>
      }
    >
          <div className="bg-slate-700/50 rounded-lg p-4">
            <p className="text-slate-400 text-xs mb-2">Transações selecionadas ({selectedTransactions.length})</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {selectedTransactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate flex-1">{tx.description}</span>
                  <span className="text-red-400 font-medium ml-3">R$ {fmt(Math.abs(tx.amount))}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-600 mt-2 pt-2 flex justify-between">
              <span className="text-slate-400 text-sm font-medium">Total</span>
              <span className="text-red-400 font-bold">R$ {fmt(totalSelected)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => adjMonth(-1)} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300">‹</button>
            <span className="flex-1 text-center text-slate-200 font-medium">{monthLabel}</span>
            <button onClick={() => adjMonth(1)} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300">›</button>
          </div>

          <div className="relative">
            <input
              type="text"
              className={`w-full px-4 py-3 ${campo} rounded-lg pl-10`}
              placeholder="Filtrar despesa-mãe (ex: Taxas, IOF, Tarifas)..."
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              autoFocus
            />
            <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <p className="text-xs text-slate-500">As transações serão criadas como despesas filhas, vinculadas à despesa-mãe. O valor da mãe é recalculado automaticamente.</p>

          {loading ? (
            <div className="py-4 text-center text-slate-400 text-sm">Carregando...</div>
          ) : results.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {results.map((exp: any) => (
                <button
                  key={exp.id}
                  onClick={() => handleConfirm(exp)}
                  disabled={saving}
                  className="w-full text-left rounded-lg border border-slate-600/50 bg-slate-700/30 p-4 hover:border-slate-500 hover:bg-slate-700/80 transition-all disabled:opacity-50"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" />
                        <p className="text-sm font-medium text-slate-200">{exp.description}</p>
                      </div>
                      {exp.category && <p className="text-xs text-slate-400 mt-0.5 ml-4">{exp.category}</p>}
                      {exp.dueDate && <p className="text-xs text-slate-500 mt-1 ml-4">Venc: {fmtDate(exp.dueDate)}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="font-bold text-sm text-white">R$ {fmt(exp.amount)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            cargaDasMaes.estado === 'erro'
              ? <EstadoDaLista carga={cargaDasMaes} temDados={false} oQue="as despesas" onTentarDeNovo={() => loadResults(searchQuery, month)} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>{null}</EstadoDaLista>
              : <p className="text-slate-500 text-sm text-center py-4">Nenhuma despesa encontrada em {monthLabel}{searchQuery ? ` com "${searchQuery}"` : ''}</p>
          )}
    </CascaDeModal>
  );
};

type BankTxFocus = { txId: number; bankAccountId: number; date: string };

type BankModalCtx = {
  showToast: (message: string, type?: 'success' | 'error') => void;
  selectedAccount: any; transactions: any; periodFilter: any; currentMonthStr: string;
  wizardHandled: Set<number>; setWizardHandled: any; setWizardMode: any;
  wizardSkipped: any; setWizardSkipped: any; wizardReconciled: any; setWizardReconciled: any;
  loadTransactions: any; loadOfficialStatements: any; loadAccounts: any;
  /** Lista de contas — o form de conta de investimento precisa oferecer a corrente de origem. */
  accounts: any[];
};

  const AccountForm = ({ onClose, account , ctx }: { onClose: () => void; account?: any; ctx: BankModalCtx }) => {
  const { showToast, selectedAccount, transactions, periodFilter, currentMonthStr, wizardHandled, setWizardHandled, setWizardMode, wizardSkipped, setWizardSkipped, wizardReconciled, setWizardReconciled, loadTransactions, loadOfficialStatements, loadAccounts, accounts } = ctx;
    const [form, setForm] = useState({
      bankName: account?.bankName || '',
      bankCode: account?.bankCode || '',
      agencyNumber: account?.agencyNumber || '',
      accountNumber: account?.accountNumber || '',
      accountType: account?.accountType || 'corrente',
      initialBalance: account?.initialBalance || 0,
      color: account?.color || '#64748b',
      // Trava de formato do extrato. '' = sem restrição. Conta nova nasce travada em
      // PDF; conta existente mantém o que já tem, inclusive "sem restrição".
      statementFormat: account ? (account.statementFormat || '') : 'PDF',
      // Conta de investimento: vínculo com a corrente que a alimenta + produto.
      parentAccountId: account?.parentAccountId ?? '',
      investmentProduct: account?.investmentProduct || '',
      investmentTitle: account?.investmentTitle || '',
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.bankName || !form.accountNumber) {
        showToast('Banco e número da conta são obrigatórios', 'error');
        return;
      }
      setSaving(true);
      try {
        const isInvestment = form.accountType === 'investimento';
        const payload: any = {
          ...form,
          initialBalance: parseFloat(String(form.initialBalance)) || 0,
          // null (e não '') para destravar: o DTO aceita null e recusa string vazia.
          statementFormat: form.statementFormat || null,
          // Campos de fundo só viajam quando o tipo é investimento. Mandar
          // string vazia reprovaria no @IsInt do DTO.
          parentAccountId: isInvestment && form.parentAccountId ? Number(form.parentAccountId) : undefined,
          investmentProduct: isInvestment ? (form.investmentProduct || undefined) : undefined,
          investmentTitle: isInvestment ? (form.investmentTitle || undefined) : undefined,
        };
        if (account) {
          await bankAccountsService.update(account.id, payload);
          showToast('Conta atualizada');
        } else {
          await bankAccountsService.create(payload);
          showToast('Conta criada');
        }
        await loadAccounts();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar', 'error');
      }
      setSaving(false);
    };

    return (
      // Formulário: não fecha ao clicar fora (como já era). O envio vive no rodapé,
      // ligado ao <form> pelo atributo `form`; o Enter nos campos continua enviando.
      <CascaDeModal
        largura="sm"
        titulo={account ? 'Editar Conta' : 'Nova Conta Bancária'}
        subtitulo={account ? `${account.bankName || ''} ${account.accountNumber || ''}`.trim() || undefined : undefined}
        onClose={onClose}
        corpoClassName="p-6"
        rodape={
          <>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button type="submit" form="form-conta-bancaria" disabled={saving} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Salvando...' : (account ? 'Atualizar' : 'Criar')}
            </button>
          </>
        }
      >
          <form id="form-conta-bancaria" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Banco *</label>
                <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })} placeholder="ex: Sicredi" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Código Banco</label>
                <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.bankCode} onChange={e => setForm({ ...form, bankCode: e.target.value })} placeholder="ex: 748" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Agência</label>
                <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.agencyNumber} onChange={e => setForm({ ...form, agencyNumber: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nº Conta *</label>
                <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.accountNumber} onChange={e => setForm({ ...form, accountNumber: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Tipo</label>
                <select className={`w-full px-3 py-2 ${campo} rounded`} value={form.accountType} onChange={e => setForm({ ...form, accountType: e.target.value })}>
                  <option value="corrente">Corrente</option>
                  <option value="poupanca">Poupança</option>
                  <option value="investimento">Aplicação financeira</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Saldo Inicial (R$)</label>
                <input type="number" step="0.01" className={`w-full px-3 py-2 ${campo} rounded`} value={form.initialBalance} onChange={e => setForm({ ...form, initialBalance: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Cor</label>
                <input type="color" className="w-full h-10 bg-slate-700 rounded border border-slate-600 cursor-pointer" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
              </div>
            </div>
            {form.accountType !== 'investimento' && (
              <div>
                <label className="block text-sm text-slate-400 mb-1">Formato do extrato aceito</label>
                <select
                  className={`w-full px-3 py-2 ${campo} rounded`}
                  value={form.statementFormat}
                  onChange={e => setForm({ ...form, statementFormat: e.target.value })}
                >
                  <option value="PDF">Somente PDF (recomendado)</option>
                  <option value="OFX">Somente OFX</option>
                  <option value="XLS">Somente Excel / CSV</option>
                  <option value="">Sem restrição</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Importar dois formatos na mesma conta duplica lançamentos: o mesmo débito vem com
                  identificador e descrição diferentes em cada arquivo, e o sistema não consegue ver que é o mesmo.
                  O PDF é o preferido porque traz a coluna Saldo em toda linha, que é o que permite conferir o extrato linha a linha.
                </p>
              </div>
            )}
            {form.accountType === 'investimento' && (
              <div className="grid grid-cols-2 gap-4 bg-central-fundo/50 border border-central-borda rounded-lg p-4">
                <div className="col-span-2">
                  <p className="text-xs text-cyan-400 mb-2">Dados da aplicação (do extrato de aplicação do banco)</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Conta corrente de origem</label>
                  <select
                    className={`w-full px-3 py-2 ${campo} rounded`}
                    value={form.parentAccountId}
                    onChange={e => setForm({ ...form, parentAccountId: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    {accounts.filter((a: any) => a.accountType !== 'investimento').map((a: any) => (
                      <option key={a.id} value={a.id}>{a.bankName} — {a.accountNumber}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Título / nº da aplicação</label>
                  <input
                    className={`w-full px-3 py-2 ${campo} rounded`}
                    placeholder="0739500099-0"
                    value={form.investmentTitle}
                    onChange={e => setForm({ ...form, investmentTitle: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-slate-400 mb-1">Produto</label>
                  <input
                    className={`w-full px-3 py-2 ${campo} rounded`}
                    placeholder="SICREDI LIQ EMP FIRF REF DI RL"
                    value={form.investmentProduct}
                    onChange={e => setForm({ ...form, investmentProduct: e.target.value })}
                  />
                </div>
                <p className="col-span-2 text-[11px] text-slate-500">
                  Saldo inicial fica zero: o saldo do fundo vem das aplicações e da posição, não é digitado.
                </p>
              </div>
            )}
          </form>
      </CascaDeModal>
    );
  };

  const OfxImportModal = ({ account, onClose, initialTab , ctx }: { account: any; onClose: () => void; initialTab?: 'import' | 'official'; ctx: BankModalCtx }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  const { showToast, selectedAccount, transactions, periodFilter, currentMonthStr, wizardHandled, setWizardHandled, setWizardMode, wizardSkipped, setWizardSkipped, wizardReconciled, setWizardReconciled, loadTransactions, loadOfficialStatements, loadAccounts } = ctx;
    const [modalTab, setModalTab] = useState<'import' | 'official'>(initialTab || 'import');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [activeUnreconciled, setActiveUnreconciled] = useState<any>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Official statement state
    const [stmtFile, setStmtFile] = useState<File | null>(null);
    const [stmtMonth, setStmtMonth] = useState(periodFilter.months[0] || currentMonthStr);
    const [stmtUploading, setStmtUploading] = useState(false);
    const [stmtList, setStmtList] = useState<any[]>([]);
    const [stmtDeleting, setStmtDeleting] = useState<number | null>(null);
    // ⛔ A lista dos extratos salvos é estado (09/10/2026): falhando, dizia "Nenhum extrato oficial salvo" ao lado do
    // upload, e o upload SUBSTITUI o extrato do mês. Sem a lista lida, o envio fica travado.
    const cargaDosExtratos = useEstadoDaCarga();
    const { rodar: rodarExtratos } = cargaDosExtratos;
    const extratosLidos = cargaDosExtratos.atualizadoEm !== null;

    const loadStatements = useCallback(() => rodarExtratos(async () => {
      const data = await bankAccountsService.getStatements(account.id);
      setStmtList(Array.isArray(data) ? data : []);
    }), [account.id, rodarExtratos]);

    useEffect(() => {
      if (modalTab === 'official') loadStatements();
    }, [modalTab, loadStatements]);

    const handleUploadStatement = async () => {
      if (!stmtFile) return;
      setStmtUploading(true);
      try {
        await bankAccountsService.uploadStatement(account.id, stmtFile, stmtMonth);
        showToast('Extrato oficial enviado com sucesso');
        setStmtFile(null);
        await loadStatements();
        await loadOfficialStatements(account.id);
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao enviar extrato', 'error');
      }
      setStmtUploading(false);
    };

    const handleViewStatement = async (stmtId: number) => {
      try {
        const { url } = await bankAccountsService.getStatementUrl(stmtId);
        window.open(url, '_blank');
      } catch {
        showToast('Erro ao abrir extrato', 'error');
      }
    };

    const handleDeleteStatement = async (stmt: any) => {
      const stmtId = stmt.id;
      if (!await confirmarPerigo(confirmacaoDeExcluirExtrato(stmt, fmtMonthLabel(stmt.referenceMonth)))) return;
      setStmtDeleting(stmtId);
      try {
        await bankAccountsService.deleteStatement(stmtId);
        showToast('Extrato excluido');
        await loadStatements();
        await loadOfficialStatements(account.id);
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
      }
      setStmtDeleting(null);
    };

    const handleImport = async () => {
      if (!file) return;
      setUploading(true);
      try {
        const res = await bankAccountsService.importFile(account.id, file);
        setResult(res);
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao importar extrato', 'error');
      }
      setUploading(false);
    };

    const handleQuickReconcile = async (tx: any, match: any) => {
      if (actionLoading) return;
      if (!await confirmar(confirmacaoDoVinculo(tx, match))) return;
      setActionLoading(true);
      try {
        await dispatchReconcileMatch(tx, match);
        showToast('Conciliado com sucesso');
        // Remove from unreconciled list
        setResult((prev: any) => ({
          ...prev,
          unreconciledTransactions: prev.unreconciledTransactions.filter((u: any) => u.id !== tx.id),
          reconciled: (prev.reconciled || 0) + 1,
        }));
        if (selectedAccount) {
          await Promise.all([
            loadTransactions(selectedAccount.id, { silent: true }),
            loadAccounts({ apenasSaldos: true }),
          ]);
        }
      } catch (err: any) {
        showToast(err.response?.data?.message || err.message || 'Erro ao conciliar', 'error');
      }
      setActionLoading(false);
    };

    const unreconciled = result?.unreconciledTransactions || [];

    const fmtFileSize = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const fmtMonthLabel = (m: string) => {
      const [y, mo] = m.split('-').map(Number);
      const name = new Date(y, mo - 1).toLocaleString('pt-BR', { month: 'long', timeZone: 'America/Sao_Paulo' });
      return `${name.charAt(0).toUpperCase() + name.slice(1)} ${y}`;
    };

    return (
      // Arquivo escolhido: não fecha ao clicar fora (como já era). A largura cresce
      // quando a importação deixa transações sem conciliar, como antes.
      <CascaDeModal
        largura={modalTab === 'import' && result && unreconciled.length > 0 ? 'lg' : 'sm'}
        titulo="Extrato Bancário"
        subtitulo={`${account.bankName} — Conta ${account.accountNumber}`}
        onClose={onClose}
        abas={[
          { chave: 'import', rotulo: <><Icon name="upload" className="w-4 h-4 inline mr-1.5 -mt-0.5" />Importar Transações</> },
          { chave: 'official', rotulo: <><Icon name="document" className="w-4 h-4 inline mr-1.5 -mt-0.5" />Extrato Oficial</> },
        ]}
        abaAtiva={modalTab}
        onAba={(k) => setModalTab(k as any)}
        corpoClassName="p-6 space-y-4"
        rodape={
          modalTab === 'official' || result ? (
            <button onClick={onClose} className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Fechar</button>
          ) : (
            <>
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
              <button onClick={handleImport} disabled={!file || uploading} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
                {uploading ? 'Importando...' : 'Importar'}
              </button>
            </>
          )
        }
      >
          {modalTab === 'official' ? (
            <>

              {/* Upload section */}
              <div className="bg-slate-700/30 rounded-lg p-4 space-y-3 border border-slate-600/50">
                <p className="text-sm font-medium text-slate-200">Enviar Extrato Oficial (PDF)</p>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-400 mb-1">Mes de referencia</label>
                    <input
                      type="month"
                      value={stmtMonth}
                      onChange={e => setStmtMonth(e.target.value)}
                      className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                    />
                  </div>
                </div>
                <div
                  className="rounded-lg border border-dashed border-transparent"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setStmtFile(e.dataTransfer?.files?.[0] || null); }}
                >
                  <label className="block text-xs text-slate-400 mb-1">Arquivo PDF</label>
                  <input type="file" accept=".pdf,.PDF" className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600" onChange={e => setStmtFile(e.target.files?.[0] || null)} />
                </div>
                {extratosLidos && avisoDeSubstituicaoDoExtrato(stmtList.find((x: any) => x.referenceMonth === stmtMonth), fmtMonthLabel(stmtMonth)) && (
                  <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="extrato-substitui">
                    {avisoDeSubstituicaoDoExtrato(stmtList.find((x: any) => x.referenceMonth === stmtMonth), fmtMonthLabel(stmtMonth))}
                  </p>
                )}
                {!extratosLidos && cargaDosExtratos.estado !== 'carregando' && (
                  <p className={`text-xs ${TONE_TEXT.warning}`} data-extratos="envio-travado">
                    Não consegui ler os extratos já salvos: enviar agora pode substituir o deste mês sem você ver. Tente de novo abaixo antes de enviar.
                  </p>
                )}
                <button
                  onClick={handleUploadStatement}
                  disabled={!stmtFile || stmtUploading || !extratosLidos}
                  className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 text-sm"
                >
                  {stmtUploading ? 'Enviando...' : 'Enviar Extrato'}
                </button>
              </div>

              {/* List of existing statements */}
              <div>
                <p className="text-sm font-medium text-slate-200 mb-2">Extratos Salvos</p>
                <EstadoDaLista
                  carga={cargaDosExtratos}
                  temDados={stmtList.length > 0}
                  oQue="os extratos oficiais"
                  onTentarDeNovo={loadStatements}
                  vazio={<p className="text-slate-500 text-sm text-center py-4">Nenhum extrato oficial salvo</p>}
                >
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {stmtList.map((stmt: any) => (
                      <div key={stmt.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-4 py-3 border border-slate-600/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-200">{fmtMonthLabel(stmt.referenceMonth)}</p>
                          <p className="text-xs text-slate-500 truncate">{stmt.originalName} ({fmtFileSize(stmt.fileSize)})</p>
                        </div>
                        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                          <button onClick={() => handleViewStatement(stmt.id)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg" title="Visualizar">
                            <Icon name="eye" className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteStatement(stmt)} disabled={stmtDeleting === stmt.id} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50" title="Excluir">
                            <Icon name="trash" className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </EstadoDaLista>
              </div>
            </>
          ) : (
          <>
            {!result ? (
              <>
                <div
                  className="rounded-lg border border-dashed border-transparent"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setFile(e.dataTransfer?.files?.[0] || null); }}
                >
                  <label className="block text-sm text-slate-400 mb-1">
                    {account.statementFormat ? `Arquivo (somente ${account.statementFormat})` : 'Arquivo (OFX, Excel, CSV ou PDF)'}
                  </label>
                  <input
                    type="file"
                    accept={ACCEPT_BY_FORMAT[account.statementFormat as string] || '.ofx,.OFX,.xls,.xlsx,.csv,.pdf,.PDF'}
                    className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600"
                    onChange={e => setFile(e.target.files?.[0] || null)}
                  />
                  {account.statementFormat && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Esta conta aceita só {account.statementFormat}. Outro formato duplicaria os lançamentos que já entraram,
                      então o sistema recusa antes de gravar. Para mudar, edite a conta.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Status banner */}
                {result.imported > 0 ? (
                  <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 ${TONE_SURFACE.success} ${TONE_BORDER.success}`}>
                    <div className="w-8 h-8 bg-central-elevado rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="check" className={TONE_TEXT.success} />
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${TONE_TEXT.success}`}>Importação concluída</p>
                      <p className="text-slate-400 text-xs">{result.imported} nova{result.imported !== 1 ? 's' : ''} transacao{result.imported !== 1 ? 'es' : ''} adicionada{result.imported !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ) : (
                  <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`}>
                    <div className="w-8 h-8 bg-central-elevado rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="alert-triangle" className={TONE_TEXT.warning} />
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${TONE_TEXT.warning}`}>Nenhuma transação nova</p>
                      <p className="text-slate-400 text-xs">Todas as {result.duplicates || 0} transacoes do arquivo ja existem no sistema</p>
                    </div>
                  </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="bg-slate-700/50 rounded-lg px-3 py-2 text-center">
                    <p className="text-slate-400 text-xs">Importadas</p>
                    <p className={`font-bold text-lg ${TONE_TEXT.open}`}>{result.imported || 0}</p>
                  </div>
                  {/* Duplicata é fato (já existia), não atenção: neutro. Pendente
                      pede ação: warning. Laranja não é tom de valor (§12.4). */}
                  <div className="bg-slate-700/50 rounded-lg px-3 py-2 text-center">
                    <p className="text-slate-400 text-xs">Duplicatas</p>
                    <p className="text-slate-300 font-bold text-lg">{result.duplicates || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg px-3 py-2 text-center">
                    <p className="text-slate-400 text-xs">Conciliadas</p>
                    <p className={`font-bold text-lg ${TONE_TEXT.progress}`}>{result.reconciled || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg px-3 py-2 text-center">
                    <p className="text-slate-400 text-xs">Pendentes</p>
                    <p className={`font-bold text-lg ${TONE_TEXT.warning}`}>{unreconciled.length}</p>
                  </div>
                </div>

                {result.balance !== undefined && result.balance !== null && (
                  <div className="bg-slate-700/50 rounded-lg px-4 py-3 flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Saldo atualizado do extrato</span>
                    <span className={`font-bold text-lg ${result.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      R$ {fmt(result.balance)}
                    </span>
                  </div>
                )}

                {result.internalTransfers > 0 && (
                  <div className={`border rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${TONE_SURFACE.info} ${TONE_BORDER.info} ${TONE_TEXT.info}`}>
                    <Icon name="repeat" className="w-4 h-4" />
                    {result.internalTransfers} transferencia{result.internalTransfers !== 1 ? 's' : ''} interna{result.internalTransfers !== 1 ? 's' : ''} detectada{result.internalTransfers !== 1 ? 's' : ''} e marcada{result.internalTransfers !== 1 ? 's' : ''} automaticamente
                  </div>
                )}

                {/* Unreconciled transactions with suggestions */}
                {unreconciled.length > 0 && (
                  <div>
                    <p className="text-slate-300 font-semibold text-sm mb-2">Transacoes nao conciliadas:</p>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {unreconciled.map((tx: any) => (
                        <div key={tx.id} className="bg-slate-700/30 rounded-lg border border-slate-600/50">
                          <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${tx.type === 'credit' ? TONE_BADGE.open : TONE_BADGE.danger}`}>
                                  {tx.type === 'credit' ? 'C' : 'D'}
                                </span>
                                <span className="text-slate-200 text-sm font-medium truncate">{tx.description}</span>
                              </div>
                              <span className="text-slate-500 text-xs">{fmtDate(tx.date)}</span>
                            </div>
                            <div className="flex items-center gap-3 ml-3">
                              <span className={`font-bold text-sm ${tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                                R$ {fmt(Math.abs(tx.amount))}
                              </span>
                              <button
                                onClick={() => setActiveUnreconciled(activeUnreconciled?.id === tx.id ? null : tx)}
                                className="px-2 py-1 text-xs bg-slate-600 text-slate-300 rounded hover:bg-slate-500"
                              >
                                {activeUnreconciled?.id === tx.id ? 'Fechar' : tx.suggestedMatches?.length > 0 ? `${tx.suggestedMatches.length} sugestoes` : 'Conciliar'}
                              </button>
                            </div>
                          </div>

                          {/* Expanded suggestions */}
                          {activeUnreconciled?.id === tx.id && tx.suggestedMatches?.length > 0 && (
                            <div className="px-4 pb-3 space-y-1">
                              <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Sugestoes:</p>
                              {tx.suggestedMatches.map((match: any, idx: number) => (
                                <button
                                  key={idx}
                                  onClick={() => handleQuickReconcile(tx, match)}
                                  disabled={actionLoading}
                                  className="w-full flex items-center justify-between bg-slate-700/50 rounded px-3 py-2 hover:bg-slate-600/50 transition-colors text-left group disabled:opacity-50"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-slate-200 text-sm truncate">{match.description}</p>
                                    <p className="text-slate-500 text-xs">
                                      {match.matchReason} {match.dueDate && `— venc. ${fmtDate(match.dueDate)}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                    <span className="text-white text-sm font-medium">R$ {fmt(match.amount)}</span>
                                    <span className="px-1.5 py-0.5 text-xs rounded bg-slate-600 text-slate-300">{match.score}%</span>
                                    <span className="text-green-400 opacity-0 group-hover:opacity-100 text-xs font-medium">Vincular</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {activeUnreconciled?.id === tx.id && (!tx.suggestedMatches || tx.suggestedMatches.length === 0) && (
                            <div className="px-4 pb-3">
                              <p className="text-slate-500 text-xs">Nenhuma sugestao encontrada. Use o Conciliar pendentes, na barra da conta.</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
          )}
      </CascaDeModal>
    );
  };

  const ReconciliationWizard = ({ ctx }: { ctx: BankModalCtx }) => {
    const confirmar = useConfirm();
    const { showToast, selectedAccount, transactions, periodFilter, currentMonthStr, wizardHandled, setWizardHandled, setWizardMode, wizardSkipped, setWizardSkipped, wizardReconciled, setWizardReconciled, loadTransactions, loadOfficialStatements, loadAccounts } = ctx;
    // Fila por id, não por posição: ver filaDoAssistente. O índice que existia
    // aqui pulava uma transação a cada vínculo.
    // <any> explícito: `transactions` chega do ctx como any, e sem isso o
    // genérico cai no tipo mínimo da fila (só id e isReconciled).
    const { atual: current, total: totalDaRodada } = filaDoAssistente<any>(transactions, wizardHandled);
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [sugLoading, setSugLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [linking, setLinking] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [createForm, setCreateForm] = useState({ description: '', categoryId: 0, notes: '' });

    // ⛔ No wizard, as sugestões e as categorias são ESTADO (09/10/2026): falhando, diziam "Nenhuma correspondencia
    // encontrada" com o "Criar Despesa" ao lado, e a despesa saía DUPLICADA. Sem elas lidas, criar fica travado.
    const cargaDasSugestoesDoWizard = useEstadoDaCarga();
    const cargaDasCategoriasDoWizard = useEstadoDaCarga();
    const { rodar: rodarSugestoesDoWizard } = cargaDasSugestoesDoWizard;
    const { rodar: rodarCategoriasDoWizard } = cargaDasCategoriasDoWizard;

    // Load categories once
    useEffect(() => {
      rodarCategoriasDoWizard(async () => {
        const cats = await expenseCategoriesService.getAll();
        setCategories(Array.isArray(cats) ? cats : []);
      });
    }, [rodarCategoriasDoWizard]);

    // Load suggestions from backend for current transaction
    const lerSugestoesDoWizard = useCallback(async () => {
      if (!current) return;
      setSugLoading(true);
      setSuggestions([]); // as sugestões são da transação: as da anterior não ficam sob a nova
      await rodarSugestoesDoWizard(async () => {
        const data = await bankTransactionsService.suggestMatches(current.id);
        setSuggestions(Array.isArray(data) ? data : []);
      });
      setSugLoading(false);
    }, [current, rodarSugestoesDoWizard]);
    useEffect(() => {
      if (!current) return;
      setShowCreateForm(false);
      lerSugestoesDoWizard();
    }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

    const porqueNaoCriarNoWizard = cargaDasSugestoesDoWizard.estado === 'erro'
      ? `Não consegui ler se já existe documento para este valor (${cargaDasSugestoesDoWizard.erro}); criar agora pode duplicar uma despesa que já existe.`
      : cargaDasSugestoesDoWizard.estado !== 'pronto'
        ? 'Lendo as sugestões: criar só depois de saber se já existe documento para este valor.'
        : cargaDasCategoriasDoWizard.estado === 'erro'
          ? `Não consegui carregar as categorias (${cargaDasCategoriasDoWizard.erro}).`
          : undefined;

    // Sai da fila e a próxima assume o lugar. Não espera o refetch: a fila é
    // local, então o assistente anda na hora mesmo com a lista ainda chegando.
    const marcarTratada = (tx: any) => {
      setWizardHandled((prev: Set<number>) => {
        const next = new Set(prev);
        next.add(tx.id);
        return next;
      });
    };

    const handleLink = async (match: any) => {
      // Sem esta trava, duplo clique no card dispara DUAS conciliações na mesma
      // transação (o wizard não tinha estado de submissão nenhum).
      if (linking) return;
      if (!await confirmar(confirmacaoDoVinculo(current, match))) return;
      setLinking(true);
      try {
        await dispatchReconcileMatch(current, match);
        setWizardReconciled(prev => prev + 1);
        showToast('Transacao vinculada');
        marcarTratada(current);
        // O refetch corre atrás: a tabela por baixo e o saldo do card se
        // atualizam sozinhos, e o operador já está vendo a próxima transação.
        if (selectedAccount) {
          void loadTransactions(selectedAccount.id, { silent: true });
          void loadAccounts({ apenasSaldos: true });
        }
      } catch (err: any) {
        showToast(err.response?.data?.message || err.message || 'Erro ao vincular', 'error');
        setLinking(false);
        return;
      }
      setLinking(false);
    };

    const handleSkip = () => {
      setWizardSkipped(prev => prev + 1);
      marcarTratada(current);
    };

    const porqueNaoVincular = porqueNaoCriarNoWizard || (!createForm.categoryId ? 'Escolha a categoria da despesa.' : undefined);
    const handleCreateExpense = async () => {
      if (porqueNaoVincular) return;
      if (!current) return;
      setCreating(true);
      try {
        await bankTransactionsService.createExpenseAndReconcile(current.id, {
          description: createForm.description || current.description || 'Despesa importada',
          // Sem cair na primeira categoria nem no id 1 (D5): grava só a que alguém escolheu.
          categoryId: createForm.categoryId,
          bankAccountId: selectedAccount?.id,
          notes: createForm.notes || undefined,
        });
        setWizardReconciled(prev => prev + 1);
        showToast('Despesa criada e vinculada');
        marcarTratada(current);
        if (selectedAccount) void loadTransactions(selectedAccount.id, { silent: true });
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao criar despesa', 'error');
      }
      setCreating(false);
    };

    // Fim da fila. Antes o assistente se fechava sozinho aqui e caía na tabela;
    // agora mostra o fecho, que é onde o operador lê o que fez. E o texto
    // distingue os dois fins: quem pulou transação não conciliou tudo, e dizer
    // que sim esconderia justamente o que ficou para trás.
    if (!current) {
      return (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-8 text-center">
          <div className={`p-4 rounded-full inline-block mb-3 ${wizardSkipped > 0 ? 'bg-amber-500/10' : 'bg-green-500/10'}`}>
            <Icon name={wizardSkipped > 0 ? 'alert-triangle' : 'check'} className={`w-8 h-8 ${wizardSkipped > 0 ? 'text-amber-400' : 'text-green-400'}`} />
          </div>
          <p className="text-white font-semibold text-lg">
            {wizardSkipped > 0 ? 'Fim da fila' : 'Todas as transacoes estao conciliadas!'}
          </p>
          <p className="text-slate-400 text-sm mt-1">{wizardReconciled} vinculadas, {wizardSkipped} puladas</p>
          {wizardSkipped > 0 && (
            <p className="text-amber-400/90 text-xs mt-2">
              {wizardSkipped === 1 ? 'A transação pulada continua pendente' : `As ${wizardSkipped} transações puladas continuam pendentes`} na conta.
            </p>
          )}
          <button onClick={() => setWizardMode(false)} className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium">
            Voltar
          </button>
        </div>
      );
    }

    const progress = totalDaRodada > 0 ? (wizardHandled.size / totalDaRodada) * 100 : 0;

    return (
      <div className="space-y-4">
        {/* Progress bar */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">Progresso: {wizardHandled.size + 1} de {totalDaRodada} transacoes</span>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-400">{wizardReconciled} vinculadas</span>
              <span className="text-slate-400">{wizardSkipped} puladas</span>
            </div>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Current transaction */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Transacao Atual</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${current.type === 'credit' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {current.type === 'credit' ? 'Credito' : 'Debito'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-slate-400 text-xs">Data</p>
              <p className="text-white font-medium">{fmtDate(current.date)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Descricao</p>
              <p className="text-white font-medium">{current.description}</p>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs">Valor</p>
              <p className={`text-xl font-bold ${current.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                {current.type === 'credit' ? '+' : '-'} R$ {fmt(Math.abs(current.amount))}
              </p>
            </div>
          </div>

          {/* Suggestions from backend */}
          {sugLoading ? (
            <div className="py-4 text-center text-slate-400 text-sm">Buscando correspondencias...</div>
          ) : cargaDasSugestoesDoWizard.estado === 'erro' ? (
            <EstadoDaLista carga={cargaDasSugestoesDoWizard} temDados={false} oQue="as sugestões" onTentarDeNovo={lerSugestoesDoWizard} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>{null}</EstadoDaLista>
          ) : suggestions.length > 0 ? (
            <div>
              <p className="text-slate-400 text-xs mb-2 uppercase font-semibold">
                Sugestoes inteligentes ({current.type === 'credit' ? 'faturas' : 'despesas'})
              </p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {suggestions.map((match: any, idx: number) => (
                  <button key={idx} onClick={() => handleLink(match)} disabled={linking} className="w-full flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-3 hover:bg-slate-700 transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${match.type === 'expense' ? 'bg-red-400' : match.type === 'multi_invoice' ? 'bg-purple-400' : 'bg-green-400'}`} />
                        <p className="text-slate-200 text-sm font-medium truncate">{match.description}</p>
                      </div>
                      <p className="text-slate-500 text-xs ml-3">
                        {match.matchReason}
                        {match.dueDate && ` — venc. ${fmtDate(match.dueDate)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="text-white font-bold text-sm">R$ {fmt(match.amount)}</span>
                      <span className="px-1.5 py-0.5 text-xs rounded bg-slate-600 text-slate-300">{match.score}%</span>
                      <span className="text-green-400 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-medium">Vincular</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-sm py-2">Nenhuma correspondencia encontrada</p>
          )}

          {/* Create expense form (only for debits) */}
          {current.type === 'debit' && showCreateForm && (
            <div className="mt-3 p-3 bg-slate-700/30 rounded-lg border border-slate-600/50 space-y-3">
              <p className="text-xs text-slate-400 uppercase font-semibold">Criar despesa e vincular</p>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Descricao</label>
                <input className={`w-full px-3 py-2 ${campo} rounded text-sm`} value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} placeholder={current.description} />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Categoria</label>
                <select className={`w-full px-3 py-2 ${campo} rounded text-sm`} value={createForm.categoryId} onChange={e => setCreateForm({ ...createForm, categoryId: Number(e.target.value) })}>
                  <option value={0}>Selecionar...</option>
                  {categories.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Observacoes</label>
                <input className={`w-full px-3 py-2 ${campo} rounded text-sm`} value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} placeholder="Opcional" />
              </div>
              {porqueNaoVincular && <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="porque-nao-vincular">{porqueNaoVincular}</p>}
              <button onClick={handleCreateExpense} disabled={creating || !!porqueNaoVincular} title={porqueNaoVincular} className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium text-sm disabled:opacity-50">
                {creating ? 'Criando...' : `Criar Despesa R$ ${fmt(Math.abs(current.amount))} e Vincular`}
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-4 pt-4 border-t border-slate-700">
            <button onClick={handleSkip} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium text-sm">
              Pular
            </button>
            {current.type === 'debit' && (
              <button
                onClick={() => { setShowCreateForm(!showCreateForm); setCreateForm({ description: current.description || '', categoryId: 0, notes: '' }); }}
                disabled={!showCreateForm && !!porqueNaoCriarNoWizard}
                title={showCreateForm ? undefined : porqueNaoCriarNoWizard}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showCreateForm ? 'Cancelar' : 'Criar Despesa'}
              </button>
            )}
            <button onClick={() => setWizardMode(false)} className="px-4 py-2.5 border border-slate-600 text-slate-400 rounded-lg hover:bg-slate-700 text-sm">
              Sair do Wizard
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AutoReconcileResultModal = ({ result, onClose , ctx }: { result: any; onClose: () => void; ctx: BankModalCtx }) => {
  const { showToast, selectedAccount, transactions, periodFilter, currentMonthStr, wizardHandled, setWizardHandled, setWizardMode, wizardSkipped, setWizardSkipped, wizardReconciled, setWizardReconciled, loadTransactions, loadOfficialStatements, loadAccounts } = ctx;
    const total = (result.expensesReconciled || 0) + (result.invoicesReconciled || 0);
    const matchTypeLabels: Record<string, string> = {
      exact: 'Conta + Valor + Data',
      amount_date: 'Valor + Data',
      amount_supplier: 'Valor + Fornecedor',
      amount_client: 'Valor + Cliente',
      amount_only: 'Valor (aprox.)',
    };

    return (
      // Só leitura: passa a fechar ao clicar fora (antes não fechava), como o
      // Resultado do Envio. As quatro contagens viram a faixa da casca.
      <CascaDeModal
        largura="base"
        fecharAoClicarFora
        titulo="Resultado da Auto-Conciliação"
        onClose={onClose}
        faixa={[
          { k: 'Processadas', v: result.totalProcessed || 0 },
          { k: 'Despesas', v: result.expensesReconciled || 0 },
          { k: 'Faturas', v: result.invoicesReconciled || 0 },
          { k: 'Pagas', v: result.expensesMarkedPaid || 0 },
        ]}
        corpoClassName="p-6 space-y-4"
        rodape={<button onClick={onClose} className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium">Fechar</button>}
      >
            {total === 0 ? (
              <div className="text-center py-6">
                <p className="text-slate-400">Nenhuma correspondência encontrada.</p>
                <p className="text-slate-500 text-sm mt-1">Tente o Wizard manual para conciliar transações individualmente.</p>
              </div>
            ) : (
              <>
                <p className="text-slate-400 text-sm">Detalhes das conciliações realizadas:</p>
                <div className="space-y-2">
                  {(result.details || []).map((d: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-700/50 rounded-lg px-3 py-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${d.matchedTo === 'expense' ? 'bg-red-400' : 'bg-green-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-200 text-sm truncate">{d.description}</p>
                        <p className="text-slate-500 text-xs truncate">→ {d.matchedDescription}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-white text-sm font-medium">R$ {fmt(d.amount)}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600 text-slate-300">{matchTypeLabels[d.matchType] || d.matchType}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
      </CascaDeModal>
    );
  };

export default function BankAccountsTab({ onNavigate, initialFocusTx, onFocusConsumed }: {
  onNavigate?: (section: string, search?: string) => void;
  initialFocusTx?: BankTxFocus | null;
  onFocusConsumed?: () => void;
}) {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  const pedirTexto = usePedirTexto();
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
  // A CONTA ABERTA NA URL (15/10/2026, estado na URL, PR 4): "?conta=<id>" com replace. A da URL é lida na montagem e
  // aberta quando as contas chegam; até lá a URL fica como veio. Conta que não existe (ou que não está na lista) sai.
  const contaDaUrl = useRef<number | null>(idDoParametro(window.location.search, 'conta'));
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  // Refetch que NÃO limpa a tabela (ver loadTransactions). Vira um "atualizando"
  // discreto no topo da lista em vez de trocar as linhas por um spinner.
  const [transactionsRefreshing, setTransactionsRefreshing] = useState(false);
  // A CONFERÊNCIA de saldo (sistema × extrato) que não rodou (08/10/2026). Antes `.catch(() => {})`: o alerta de
  // divergência simplesmente não aparecia, e a ausência dele parecia "saldo confere". E é POR CONTA: a conferência da
  // conta anterior não pode ficar sob a nova quando a da nova falha.
  const [balanceCheckErro, setBalanceCheckErro] = useState<string | null>(null);
  const contaDaConferenciaRef = useRef<number | null>(null);
  // `saldoComparado` é o número contra o qual o `diff` foi calculado (backend #445, 14/10/2026): numa conta com fundo de
  // varredura é a SOMA da corrente com as filhas, porque o extrato traz o disponível consolidado. `currentBalance` segue
  // sendo o da conta sozinha; mostrar ele como "Sistema" deixava os três números do banner sem fechar entre si.
  // Backend #450: o saldo do extrato tem GRANDEZA (corrente ou consolidado), gravada por linha. `saldoUsadoNaComparacao`
  // é o número que entrou no diff (a corrente sozinha quando o extrato é da corrente); 'desconhecida' vem com
  // `matches`/`diff` nulos e o `motivoSemComparacao`. Sem `grandezaDoExtrato` é o backend antigo: o banner de antes.
  const [balanceCheck, setBalanceCheck] = useState<{
    matches: boolean | null; diff: number | null; statementBalance: number | null; currentBalance: number;
    saldoComparado?: number; consolidado?: boolean; contasSomadas?: number[]; notaDaComparacao?: string;
    grandezaDoExtrato?: 'corrente' | 'consolidado' | 'desconhecida'; saldoUsadoNaComparacao?: number | null;
    motivoSemComparacao?: string;
  } | null>(null);

  // Filters for transactions — period-based navigation
  const currentMonthStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(() => ({
    ...defaultPeriodFilter(),
  }));
  const startDate = useMemo(() => {
    if (periodFilter.mode === 'dateRange') return periodFilter.startDate;
    const m = periodFilter.months[0] || currentMonthStr;
    return `${m}-01`;
  }, [periodFilter, currentMonthStr]);
  const endDate = useMemo(() => {
    if (periodFilter.mode === 'dateRange') return periodFilter.endDate;
    const m = periodFilter.months[0] || currentMonthStr;
    const [y, mo] = m.split('-').map(Number);
    const lastDay = new Date(y, mo, 0).getDate();
    return `${m}-${String(lastDay).padStart(2, '0')}`;
  }, [periodFilter, currentMonthStr]);
  const [onlyUnreconciled, setOnlyUnreconciled] = useState(false);

  // Modals
  const [accountModal, setAccountModal] = useState<'create' | 'edit' | null>(null);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [ofxModal, setOfxModal] = useState<any | null>(null);
  const [ofxModalInitialTab, setOfxModalInitialTab] = useState<'import' | 'official'>('import');
  const [reconcileModal, setReconcileModal] = useState<any | null>(null);
  const [notesModal, setNotesModal] = useState<any | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  // Id da transação com ação em curso (desconciliar/excluir/ignorar/toggle).
  // Desabilita os botões daquela linha até a request terminar.
  const [busyTxId, setBusyTxId] = useState<number | null>(null);
  // Fila de dinheiro sem documento do outro lado (ver loadWithoutCounterparty).
  const [noCounterparty, setNoCounterparty] = useState<any[]>([]);
  const [noCounterpartyLoading, setNoCounterpartyLoading] = useState(false);
  // ⛔ As cargas da tela são ESTADO (09/10/2026): falhando, zeravam ou só logavam. A pior era a das transações: a troca
  // de conta (ou de mês) não limpava a lista, e com a leitura da nova falhando a conta ANTERIOR ficava sob a nova.
  const cargaDasContas = useEstadoDaCarga();
  const cargaDasTransacoes = useEstadoDaCarga();
  const cargaSemContrapartida = useEstadoDaCarga();
  const { rodar: rodarContas } = cargaDasContas;
  const { rodar: rodarTransacoes } = cargaDasTransacoes;
  const { rodar: rodarSemContrapartida } = cargaSemContrapartida;
  const [erroDosUltimosExtratos, setErroDosUltimosExtratos] = useState<string | null>(null);
  const [erroDosExtratosOficiais, setErroDosExtratosOficiais] = useState<string | null>(null);
  const [showNoCounterparty, setShowNoCounterparty] = useState(false);
  const [categoryModal, setCategoryModal] = useState<any | null>(null);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [traceModal, setTraceModal] = useState<any | null>(null);
  const [wizardMode, setWizardMode] = useState(false);
  // Ids que já passaram pelas mãos do operador nesta rodada do assistente —
  // vinculados ou pulados. Era um índice numa lista que encolhe, e por isso o
  // assistente pulava uma transação a cada vínculo (ver ReconciliationWizard).
  const [wizardHandled, setWizardHandled] = useState<Set<number>>(new Set());
  const [wizardReconciled, setWizardReconciled] = useState(0);
  const [wizardSkipped, setWizardSkipped] = useState(0);
  const [autoReconciling, setAutoReconciling] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [autoReconcileResult, setAutoReconcileResult] = useState<any>(null);
  const [lastImports, setLastImports] = useState<Record<number, any>>({});
  const [investmentSummaries, setInvestmentSummaries] = useState<Record<number, any>>({});
  /** O resumo que NÃO deu para ler, por conta: o card dizia "Sem posição registrada", o que é falso. */
  const [investmentSummaryErros, setInvestmentSummaryErros] = useState<Record<number, string>>({});
  const [officialStatements, setOfficialStatements] = useState<any[]>([]);

  // Multi-select transactions for bulk reconciliation
  const [selectedTxIds, setSelectedTxIds] = useState<Set<number>>(new Set());
  const [bulkReconcileModal, setBulkReconcileModal] = useState(false);
  const [bulkReconcileExpenseModal, setBulkReconcileExpenseModal] = useState(false);
  const [bulkReconciling, setBulkReconciling] = useState(false);
  const [highlightTxId, setHighlightTxId] = useState<number | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  /**
   * `apenasSaldos` corta as duas chamadas de apoio (data do último extrato e
   * posição dos fundos). Elas alimentam linhas do card que uma conciliação não
   * altera — nem o extrato importado, nem a cota do fundo mudam porque uma
   * transação ganhou documento. Quem só precisa do saldo e do contador de
   * pendências pede assim e deixa a fila de conexão do navegador livre.
   */
  const loadAccounts = useCallback(async (opts?: { apenasSaldos?: boolean }) => {
    let accs: any[] | null = null;
    await rodarContas(async () => {
      const data = await bankAccountsService.getAll();
      const lidas = Array.isArray(data) ? data : [];
      accs = lidas;
      setAccounts(lidas);
      // Update selectedAccount with fresh data (e.g. currentBalance after import)
      setSelectedAccount((prev: any) => {
        if (!prev) return prev;
        const updated = lidas.find((a: any) => a.id === prev.id);
        return updated || prev;
      });
    });
    // `accs` é preenchida dentro do rodar; o TS não acompanha a atribuição feita no closure, daí o tipo explícito aqui.
    const contasLidas = accs as any[] | null;
    {
      if (contasLidas && contasLidas.length > 0 && !opts?.apenasSaldos) {
        // Os dois blocos abaixo são independentes um do outro (último extrato
        // importado × posição dos fundos) e ficavam encadeados com await: a
        // segunda rodada só começava depois da primeira terminar. Como o gargalo
        // aqui é fila de conexão do browser — o HUB fala com outro domínio, e o
        // backend responde cada rota em dezenas de ms —, serializar custava um
        // round-trip inteiro de graça.
        // Falhando, todo card dizia "Nenhum extrato importado": afirmava que nunca se importou. Agora o card diz que não leu.
        const carregarUltimosExtratos = async () => {
          try {
            const logs = await importLogService.getLastByAccounts(contasLidas.map((a: any) => a.id));
            setLastImports(logs || {});
            setErroDosUltimosExtratos(null);
          } catch (e) { setErroDosUltimosExtratos(mensagemDoErroDeCarga(e)); }
        };

        // Resumo das contas de investimento: é o que responde "quanto posso
        // resgatar hoje sem IOF" direto no card, sem abrir a conta.
        const carregarResumosDeInvestimento = async () => {
          const invAccs = contasLidas.filter((a: any) => a.accountType === 'investimento');
          if (invAccs.length === 0) return;
          const entries = await Promise.all(
            invAccs.map(async (a: any) => {
              try { return [a.id, await investmentsService.getSummary(a.id), null] as const; }
              catch (e) { return [a.id, null, mensagemDoErroDeCarga(e)] as const; }
            }),
          );
          setInvestmentSummaries(Object.fromEntries(entries.filter(([, v]) => v).map(([id, v]) => [id, v])) as any);
          setInvestmentSummaryErros(Object.fromEntries(entries.filter(([, , e]) => e).map(([id, , e]) => [id, e])) as any);
        };

        await Promise.all([carregarUltimosExtratos(), carregarResumosDeInvestimento()]);
      }
    }
  }, [rodarContas]);

  const loadSeqRef = useRef(0);
  const loadTransactions = useCallback(async (accountId: number, opts?: { silent?: boolean }) => {
    // Sequência pra ignorar respostas obsoletas: ao trocar de mês rápido (< >),
    // várias buscas disparam e uma antiga pode chegar depois e sobrescrever a
    // atual (lista de Fev aparecendo com o rótulo de Jan). Só a última vale.
    const seq = ++loadSeqRef.current;
    // `silent` é para refetch DEPOIS de uma ação do operador (conciliar,
    // ignorar, classificar, desfazer). A lista que já está na tela continua
    // valendo, então trocá-la por "Carregando transações..." é só perda: o
    // refetch leva segundos por fila de conexão do browser, e nesse intervalo o
    // operador perde de vista a linha em que estava trabalhando.
    // Troca de conta e de mês continuam limpando — ali as linhas antigas são de
    // outro recorte, e deixá-las sob um cabeçalho novo seria mentira.
    // ⛔ Agora LIMPA de verdade (09/10/2026): o comentário acima prometia, e o código não fazia. Com a leitura da conta
    // nova falhando, a tabela mostrava as linhas da conta ANTERIOR sob o cabeçalho da nova, com conciliar e lote.
    if (opts?.silent) setTransactionsRefreshing(true);
    else { setTransactionsLoading(true); setTransactions([]); }
    try {
      await rodarTransacoes(async () => {
      const params: any = { startDate, endDate };
      if (onlyUnreconciled) params.isReconciled = false;
      const data = await bankAccountsService.getTransactions(accountId, params);
      if (seq !== loadSeqRef.current) return; // resposta obsoleta — descarta
      setTransactions(Array.isArray(data) ? data : data.data || []);
      // Alerta de divergência: saldo do sistema vs saldo do extrato
      if (contaDaConferenciaRef.current !== accountId) setBalanceCheck(null);
      bankAccountsService.balanceCheck(accountId)
        .then((bc: any) => {
          if (seq !== loadSeqRef.current) return;
          contaDaConferenciaRef.current = accountId;
          setBalanceCheck(bc);
          setBalanceCheckErro(null);
        })
        .catch((e: any) => { if (seq === loadSeqRef.current) setBalanceCheckErro(mensagemDoErroDeCarga(e)); });
      });
    } finally {
      if (seq === loadSeqRef.current) {
        setTransactionsLoading(false);
        setTransactionsRefreshing(false);
      }
    }
  }, [startDate, endDate, onlyUnreconciled, rodarTransacoes]);

  const loadOfficialStatements = useCallback(async (accountId: number) => {
    try {
      const data = await bankAccountsService.getStatements(accountId);
      setOfficialStatements(Array.isArray(data) ? data : []);
      setErroDosExtratosOficiais(null);
    } catch (e) { setOfficialStatements([]); setErroDosExtratosOficiais(mensagemDoErroDeCarga(e)); }
  }, []);

  // Fila "sem contrapartida". Não depende do período selecionado de propósito:
  // é um passivo acumulado (77 boletos, R$ 468.982,38 em 29/07/2026), não um
  // recorte do mês. Filtrar por mês esconderia o problema justamente de quem
  // está tentando fechar o mês.
  // Falhando, zerava, e o alerta (só aparece com a fila > 0) sumia calado: o passivo acumulado desaparecia justo de quem
  // fecha o mês. Agora a falha é estado e o alerta diz que não leu; a lista só é trocada quando a leitura dá certo.
  const loadWithoutCounterparty = useCallback(async (accountId: number) => {
    setNoCounterpartyLoading(true);
    await rodarSemContrapartida(async () => {
      const data = await bankTransactionsService.withoutCounterparty(accountId);
      setNoCounterparty(Array.isArray(data) ? data : []);
    });
    setNoCounterpartyLoading(false);
  }, [rodarSemContrapartida]);

  /**
   * Sucesso de conciliação, vindo de qualquer um dos quatro caminhos do modal
   * (vínculo simples, por NF, multi-fatura, criar despesa).
   *
   * Conciliar UMA linha disparava seis requisições e limpava a tabela: 5 a 7
   * segundos de "Carregando transações..." para uma ação que o servidor resolve
   * em dezenas de milissegundos. O tempo não era do banco nem do backend — era
   * fila de conexão do browser, porque o HUB (hub.ipcom.app.br) fala com outro
   * domínio (financeiro.ipcom.com.br) e as chamadas empilham.
   *
   * A resposta aqui é não depender do refetch para dar a notícia: o POST já
   * devolve a transação salva, então a linha muda na hora. O refetch continua
   * existindo — ele traz o que o POST não traz — mas corre por baixo, sem
   * limpar a tela e sem ninguém esperando por ele.
   */
  const handleReconcileDone = useCallback((tx: any, saved?: any) => {
    setReconcileModal(null);
    if (tx) {
      setTransactions(prev => onlyUnreconciled
        // Com o filtro "somente não conciliadas" ligado, a linha sai da lista —
        // é o que o refetch faria de todo jeito, e vê-la ficar verde para sumir
        // meio segundo depois só confunde.
        ? prev.filter((t: any) => t.id !== tx.id)
        // O POST devolve a transação salva, mas sem as relações
        // (invoicePayments/expensePayments) que a coluna "Conciliada" usa para
        // escrever o rótulo do documento. Por isso o badge fica verde já, e o
        // nome da fatura/despesa aparece quando o refetch silencioso chegar.
        // `saved` só é aproveitado quando é mesmo esta transação: o caminho
        // "criar despesa" devolve outro shape, e aí vale só o isReconciled.
        : prev.map((t: any) => t.id === tx.id
          ? { ...t, ...(saved && saved.id === tx.id ? saved : {}), isReconciled: true }
          : t));
      // Conciliada, a linha deixa de ser selecionável — deixar o id na seleção
      // mantinha a barra de ação em lote contando uma transação que ela já não
      // consegue mexer.
      setSelectedTxIds(prev => {
        if (!prev.has(tx.id)) return prev;
        const next = new Set(prev);
        next.delete(tx.id);
        return next;
      });
    }
    void loadAccounts({ apenasSaldos: true });
    if (selectedAccount) {
      void loadTransactions(selectedAccount.id, { silent: true });
      void loadWithoutCounterparty(selectedAccount.id);
    }
  }, [loadAccounts, loadTransactions, loadWithoutCounterparty, onlyUnreconciled, selectedAccount]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadAccounts();
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dependem do ID da conta, não do objeto.
  //
  // `loadAccounts` troca o `selectedAccount` por um objeto NOVO vindo da API a
  // cada chamada — mesma conta, identidade diferente. Pendurados no objeto,
  // estes effects re-disparavam a cada conciliação, e o `loadTransactions`
  // daqui não passa `silent`: era ele, e não o refetch do handler, que limpava
  // a tabela e escrevia "Carregando transações..." por segundos. De quebra
  // vinham `statements` e `without-counterparty` de novo, dobrando a rodada,
  // e o `setSelectedTxIds(new Set())` abaixo apagava a seleção em lote do
  // operador no meio do trabalho.
  //
  // Pelo id, só recarrega quando a conta (ou o período) muda de verdade — que é
  // quando as linhas na tela deixam de valer.
  const selectedAccountId = selectedAccount?.id ?? null;

  useEffect(() => {
    if (selectedAccountId) {
      loadTransactions(selectedAccountId);
      // A lista é por conta: a da anterior sai antes de ler a da nova.
      setOfficialStatements([]);
      setErroDosExtratosOficiais(null);
      loadOfficialStatements(selectedAccountId);
    }
    setSelectedTxIds(new Set());
  }, [selectedAccountId, startDate, endDate, onlyUnreconciled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separado do effect acima porque NÃO depende do período: recarregar a fila a
  // cada troca de mês seria requisição inútil (a lista é a mesma).
  useEffect(() => {
    // A fila é por conta: a da conta anterior sai antes de ler a da nova (agora a falha não zera mais a lista).
    setNoCounterparty([]);
    if (selectedAccountId) loadWithoutCounterparty(selectedAccountId);
  }, [selectedAccountId, loadWithoutCounterparty]);

  useEffect(() => {
    if (contaDaUrl.current == null || accounts.length === 0) return;
    const acc = accounts.find((a: any) => a.id === contaDaUrl.current);
    contaDaUrl.current = null;
    if (acc) setSelectedAccount(acc);
    else trocarParametroNaUrl('conta', null);
  }, [accounts]);
  useEffect(() => {
    if (contaDaUrl.current != null) return; // esperando as contas para abrir a da URL
    trocarParametroNaUrl('conta', selectedAccount?.id ?? null);
  }, [selectedAccount?.id]);

  // Quando navegamos pra cá com foco numa TX específica (via "Abrir transação" da fatura),
  // auto-seleciona a conta + ajusta o período pro mês da TX + marca id pra highlight quando
  // as transações carregarem.
  useEffect(() => {
    if (!initialFocusTx || accounts.length === 0) return;
    const acc = accounts.find((a: any) => a.id === initialFocusTx.bankAccountId);
    if (!acc) return;
    setSelectedAccount(acc);
    const dateStr = String(initialFocusTx.date).split('T')[0];
    const ym = dateStr.slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(ym)) {
      setPeriodFilter(prev => ({ ...prev, mode: 'month', months: [ym] }));
    }
    setHighlightTxId(initialFocusTx.txId);
    onFocusConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFocusTx, accounts.length]);

  // Scroll + flash quando a TX-alvo aparecer na lista
  useEffect(() => {
    if (!highlightTxId || transactions.length === 0) return;
    const el = document.querySelector(`[data-tx-row-id="${highlightTxId}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timer = setTimeout(() => setHighlightTxId(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightTxId, transactions.length]);

  // ============ ACCOUNT FORM ============

  // ============ IMPORT MODAL (OFX/Excel/CSV with unreconciled suggestions) ============


  // ============ RECONCILIATION WIZARD ============

  const handleDeleteAccount = async (id: number) => {
    if (!await confirmarPerigo('Excluir esta conta bancária?\n\nA conta sai da lista, mas não é apagada: transações, conciliações e extratos dela continuam guardados. Não há como reativá-la pela tela.')) return;
    try {
      await bankAccountsService.delete(id);
      showToast('Conta excluída');
      if (selectedAccount?.id === id) setSelectedAccount(null);
      await loadAccounts();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  // Ações da linha da tabela: nenhuma tinha estado de submissão, então os
  // ícones ficavam clicáveis durante a request. Duplo clique no toggle de
  // auto-conciliação voltava ao estado original com dois toasts contraditórios,
  // e no desconciliar disparava duas chamadas.
  // `busyTxId` faz as duas coisas: o `disabled` esmaece só os botões da linha em
  // curso (as outras seguem utilizáveis visualmente), e o early-return é global
  // de propósito — duas ações simultâneas em linhas diferentes recarregariam a
  // lista concorrentemente e uma sobrescreveria o resultado da outra.
  const handleUnreconcile = async (transactionId: number) => {
    if (busyTxId) return;

    // O confirm antigo só falava de DESPESA. Num crédito de lote do Sicredi (um
    // LIQ.COBRANCA cobre N boletos) a ação mexe em dezenas de faturas — há
    // transações com 38 — e o operador não tinha como saber antes de clicar.
    // O preview é consultado no backend para o texto dizer a verdade.
    setBusyTxId(transactionId);
    let preview: any = null;
    try {
      preview = await bankTransactionsService.unreconcilePreview(transactionId);
    } catch {
      // Preview indisponível não deve travar a operação, mas o operador precisa
      // saber que está decidindo sem o detalhe.
      preview = null;
    }
    setBusyTxId(null);

    const linhas: string[] = [];
    if (preview) {
      if (preview.paymentsToDelete > 0) {
        const cods = preview.invoiceCodes?.length
          ? `\n  (${preview.invoiceCodes.slice(0, 8).join(', ')}${preview.invoiceCodes.length > 8 ? ` e mais ${preview.invoiceCodes.length - 8}` : ''})`
          : '';
        linhas.push(
          `• ${preview.paymentsToDelete} pagamento(s) criado(s) por conciliação serão APAGADOS, afetando ${preview.invoicesAffected} fatura(s).${cods}`,
        );
      }
      if (preview.paymentsToUnlink > 0) {
        linhas.push(
          `• ${preview.paymentsToUnlink} pagamento(s) vindos do banco (retorno CNAB / Sicredi) serão apenas DESVINCULADOS. As faturas continuam pagas.`,
        );
      }
      if (preview.expenseWillBeDeleted) {
        linhas.push(`• A despesa "${preview.expenseDescription || ''}" foi criada pela conciliação e será EXCLUÍDA.`);
      } else if (preview.expenseDescription) {
        linhas.push(`• O pagamento da despesa "${preview.expenseDescription}" será removido e ela volta a pendente.`);
      }
      if (linhas.length === 0) linhas.push('• Nada além do vínculo desta transação será alterado.');
    } else {
      linhas.push('⚠ Não foi possível carregar o detalhe do que será desfeito.');
    }

    if (!await confirmar({ title: 'Desfazer conciliação desta transação?', message: linhas.join('\n') })) return;
    setBusyTxId(transactionId);
    try {
      await bankTransactionsService.unreconcile(transactionId);
      showToast('Conciliação removida');
      if (selectedAccount) {
        // Em série custava dois round-trips onde um bastava, e o
        // `loadTransactions` sem `silent` ainda trocava a lista inteira por um
        // spinner — a linha em que o operador estava sumia da tela.
        await Promise.all([
          loadTransactions(selectedAccount.id, { silent: true }),
          loadAccounts({ apenasSaldos: true }),
        ]);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao remover conciliação', 'error');
    } finally {
      setBusyTxId(null);
    }
  };

  const handleDeleteTransaction = async (tx: any) => {
    const transactionId = tx.id;
    if (busyTxId) return;
    if (!await confirmarPerigo(confirmacaoDeExcluirTransacao(tx))) return;
    setBusyTxId(transactionId);
    try {
      await bankTransactionsService.delete(transactionId);
      showToast('Transação excluída');
      if (selectedAccount) {
        await Promise.all([
          loadTransactions(selectedAccount.id, { silent: true }),
          loadAccounts({ apenasSaldos: true }),
        ]);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    } finally {
      setBusyTxId(null);
    }
  };

  const handleIgnoreTransaction = async (transactionId: number) => {
    if (busyTxId) return;
    // Ignorar tira a transação da fila de trabalho: some das pendências sem
    // deixar vínculo nenhum. O ícone fica colado no de conciliar e não pedia
    // confirmação de nada.
    if (!await confirmar({ title: 'Marcar esta transação como ignorada?', message: 'Ela sai das pendências de conciliação sem ser vinculada a fatura ou despesa.' })) return;
    setBusyTxId(transactionId);
    try {
      await bankTransactionsService.ignore(transactionId);
      showToast('Transacao marcada como ignorada');
      if (selectedAccount) {
        await Promise.all([
          loadTransactions(selectedAccount.id, { silent: true }),
          loadAccounts({ apenasSaldos: true }),
        ]);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao ignorar', 'error');
    } finally {
      setBusyTxId(null);
    }
  };

  const handleToggleIgnoreAutoReconcile = async (tx: any) => {
    if (busyTxId) return;
    setBusyTxId(tx.id);
    try {
      await bankTransactionsService.toggleIgnoreAutoReconcile(tx.id);
      showToast(tx.ignoreAutoReconcile ? 'Auto-conciliação permitida' : 'Auto-conciliação bloqueada');
      if (selectedAccount) await loadTransactions(selectedAccount.id, { silent: true });
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao alterar', 'error');
    } finally {
      setBusyTxId(null);
    }
  };

  const openNotesModal = (tx: any) => {
    setNotesModal(tx);
    setNotesDraft(tx.notes || '');
  };

  const handleSaveNotes = async () => {
    if (!notesModal) return;
    setSavingNotes(true);
    try {
      await bankTransactionsService.setNotes(notesModal.id, notesDraft);
      showToast('Observação salva');
      setNotesModal(null);
      if (selectedAccount) await loadTransactions(selectedAccount.id, { silent: true });
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao salvar observação', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  const openCategoryModal = (tx: any) => {
    setCategoryModal(tx);
    setCategoryDraft(MANUAL_TX_CATEGORIES.includes(tx.category) ? tx.category : '');
  };

  const handleSaveCategory = async () => {
    if (!categoryModal) return;
    setSavingCategory(true);
    try {
      await bankTransactionsService.setCategory(categoryModal.id, categoryDraft);
      showToast(categoryDraft ? 'Transação classificada' : 'Classificação removida');
      setCategoryModal(null);
      if (selectedAccount) {
        // Classificar tira a transação da fila "sem contrapartida" (ou a coloca
        // nela, se a categoria foi removida) — a fila tem que refletir na hora.
        // Uma coisa não depende da outra: as duas juntas.
        await Promise.all([
          loadTransactions(selectedAccount.id, { silent: true }),
          loadWithoutCounterparty(selectedAccount.id),
        ]);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao classificar', 'error');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleBulkDeleteTransactions = async () => {
    if (!selectedAccount) return;
    // Apaga o extrato INTEIRO da conta, inclusive transações já conciliadas a
    // faturas e despesas. Um window.confirm genérico ao lado de "Exportar
    // Excel" é fácil demais de aceitar por reflexo: exige digitar o nome.
    const total = transactions.length;
    const nomeDaConta = String(selectedAccount.bankName).trim();
    const digitado = await pedirTexto({
      title: `Apagar TODAS as transações da conta ${nomeDaConta}?`,
      // Sem a contagem da tela (D8): com a carga falhando ela dizia 0 enquanto o servidor apaga o histórico inteiro.
      consequence: 'A exclusão cobre TODO o histórico da conta, não só o período na tela, ' +
        'inclusive as transações já conciliadas a faturas e despesas. Não há como desfazer.',
      label: `Para confirmar, digite o nome da conta: ${nomeDaConta}`,
      linhaUnica: true,
      obrigatorio: true,
      validar: (t) => (t.toLowerCase() === nomeDaConta.toLowerCase() ? null : 'O nome não confere com o da conta.'),
      danger: true,
      confirmLabel: 'Apagar tudo',
    });
    if (digitado === null) return;
    try {
      const result = await bankTransactionsService.bulkDelete(selectedAccount.id);
      showToast(`${result.deleted} transações excluídas`);
      await Promise.all([
        loadTransactions(selectedAccount.id),
        loadAccounts(),
      ]);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  const handleExportExcel = async () => {
    if (!selectedAccount) return;
    setExcelExporting(true);
    try {
      const blob = await bankTransactionsService.exportExcel(selectedAccount.id, startDate, endDate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transacoes_${selectedAccount.bankName}_${periodFilter.months[0] || currentMonthStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Erro ao exportar Excel', 'error');
    }
    setExcelExporting(false);
  };

  const handleExportPdf = async () => {
    if (!selectedAccount) return;
    setPdfExporting(true);
    try {
      const blob = await bankTransactionsService.exportPdf(selectedAccount.id, startDate, endDate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Extrato_${selectedAccount.bankName}_${periodFilter.months[0] || currentMonthStr}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Erro ao exportar PDF', 'error');
    }
    setPdfExporting(false);
  };

  const handleAutoReconcile = async () => {
    if (!selectedAccount) return;
    setAutoReconciling(true);
    try {
      const result = await bankTransactionsService.autoReconcile(selectedAccount.id);
      setAutoReconcileResult(result);
      const total = (result.expensesReconciled || 0) + (result.invoicesReconciled || 0);
      if (total > 0) {
        showToast(`${total} transações conciliadas automaticamente`);
        await Promise.all([
          loadTransactions(selectedAccount.id, { silent: true }),
          loadAccounts({ apenasSaldos: true }),
        ]);
      } else {
        showToast('Nenhuma correspondência encontrada', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro na auto-conciliação', 'error');
    }
    setAutoReconciling(false);
  };

  // ============ AUTO-RECONCILE RESULT MODAL ============

  /**
   * Cartões agrupados: a aplicação financeira aparece DENTRO do card da conta
   * corrente que a alimenta (`bank_accounts.parentAccountId`), não ao lado dela.
   *
   * Na grade plana, ordenada só por bankName, o pareamento era coincidência —
   * funcionava enquanto o nome do banco se repetisse nas duas contas. Abrir uma
   * aplicação num banco sem corrente, ou renomear uma conta, já desfazia. E o
   * dado que resolve sempre esteve na resposta da API, ignorado pela tela.
   *
   * Vale também para o que a tela conta: uma aplicação zerada solta entre os
   * outros cards lê-se como conta morta; pendurada na corrente, lê-se como o
   * que é — a aplicação daquela conta, hoje sem saldo.
   */
  const accountGroups = useMemo(() => agruparContasPorPai(accounts), [accounts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-slate-400">Carregando...</span>
      </div>
    );
  }

  // As contas que o backend somou no "Sistema" do banner, com o saldo de cada uma: um total sozinho não é conferível
  // contra o extrato. A primeira é a própria conta (o saldo dela vem na resposta); as filhas saem da lista de contas. Filha
  // que não está na lista: sem detalhe, em vez de uma parcela de R$ 0,00 inventada.
  // A soma só é o número comparado quando o extrato é CONSOLIDADO (ou no backend antigo, que sempre comparava a soma).
  // Extrato da corrente compara a corrente sozinha: mostrar "corrente + ContaMax" ali seria explicar outro número.
  const comparouASoma = !balanceCheck?.grandezaDoExtrato || balanceCheck.grandezaDoExtrato === 'consolidado';
  const somadas: { nome: string; saldo: number }[] = (() => {
    const ids = balanceCheck?.contasSomadas ?? [];
    if (!comparouASoma || ids.length < 2) return [];
    const filhas = ids.slice(1).map(id => accounts.find(a => a.id === id));
    if (filhas.some(f => !f)) return [];
    return [
      { nome: 'Conta', saldo: Number(balanceCheck!.currentBalance) },
      ...filhas.map(f => ({ nome: f.investmentProduct || f.name, saldo: Number(f.currentBalance) })),
    ];
  })();

  const tipoDaConta = (acc: any) =>
    acc.accountType === 'investimento' ? 'Aplicação' : acc.accountType === 'poupanca' ? 'Poupança' : 'Corrente';

  const identificacaoDaConta = (acc: any) =>
    acc.accountType === 'investimento'
      ? (acc.investmentProduct || `Título ${acc.investmentTitle || acc.accountNumber}`)
      : `${acc.agencyNumber ? `Ag. ${acc.agencyNumber} — ` : ''}Conta ${acc.accountNumber}`;

  const ultimoExtratoDaConta = (acc: any) => (
    <div className="text-xs text-slate-500 mt-1">
      {lastImports[acc.id]
        ? `Ultimo extrato: ${fmtDateTime(lastImports[acc.id].importedAt)}`
        : erroDosUltimosExtratos ? 'Último extrato: não consegui ler' : 'Nenhum extrato importado'}
    </div>
  );

  /**
   * Bloco de posição do fundo. Mesmo conteúdo no card solto e no card-filho: é
   * o que responde "quanto posso resgatar hoje sem IOF" sem abrir a conta, e
   * encolher isso ao aninhar seria perder o motivo de ele existir.
   */
  const renderResumoInvestimento = (acc: any) => {
    const s = investmentSummaries[acc.id];
    if (!s && investmentSummaryErros[acc.id]) {
      return <div className={`text-xs mt-1 ${TONE_TEXT.warning}`} data-testid="resumo-investimento-erro">Não consegui ler a posição: {investmentSummaryErros[acc.id]}</div>;
    }
    if (!s) return <div className="text-xs text-slate-500 mt-1">Sem posição registrada</div>;
    // Varredura (ContaMax, 14/10/2026): a mesma frase do painel, sem posição nem IOF. "Sem posição do banco — saldo pelo
    // custo de aquisição" e "Livre de IOF hoje R$ 0,00" em âmbar eram alarme do modelo do Sicredi.
    const varredura = lerVarredura(s);
    if (varredura) {
      return (
        <div className={`mt-2 text-[11px] ${varredura.origem === 'indisponivel' ? TONE_TEXT.warning : 'text-slate-500'}`} data-resumo-varredura={varredura.origem}>
          {fraseDoSaldoDaVarredura(varredura, fmtDate)}
        </div>
      );
    }
    // Saldo do fundo é `cotas × valor da cota da última posição`. Sem posição
    // nova ele CONGELA no último valor conhecido, e um número velho com cara de
    // saldo de hoje é pior que nenhum número — por isso a data fica junto, e
    // vira alerta aos 7 dias.
    const posDate: string | null = s.lastPosition?.date || null;
    const ageDays = posDate
      ? Math.round(
          (Date.UTC(
            Number(s.referenceDate.slice(0, 4)),
            Number(s.referenceDate.slice(5, 7)) - 1,
            Number(s.referenceDate.slice(8, 10)),
          ) -
            Date.UTC(
              Number(posDate.slice(0, 4)),
              Number(posDate.slice(5, 7)) - 1,
              Number(posDate.slice(8, 10)),
            )) / 86400000,
        )
      : null;
    const posStale = ageDays != null && ageDays > 7;
    return (
      <div className="mt-2 space-y-1.5">
        {posDate ? (
          <div className={`text-[11px] ${posStale ? 'text-amber-400' : 'text-slate-500'}`}>
            Posição de {fmtDate(posDate)}
            {ageDays === 0 ? ' (hoje)' : ageDays === 1 ? ' (ontem)' : ` (há ${ageDays} dias)`}
            {posStale && ' — desatualizada'}
          </div>
        ) : (
          <div className="text-[11px] text-amber-400">Sem posição do banco — saldo pelo custo de aquisição</div>
        )}
        {s.lastPosition && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Líquido para saque</span>
            <span className="text-slate-300 font-medium">R$ {fmt(s.lastPosition.netWithdrawable)}</span>
          </div>
        )}
        {s.accruedYield != null && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Rendimento acumulado</span>
            <span className="text-central-texto font-medium tabular-nums">R$ {fmt(s.accruedYield)}</span>
          </div>
        )}
        {/* A pergunta que se faz antes de todo resgate. */}
        <div className="flex items-center justify-between text-xs pt-1.5 border-t border-central-borda">
          <span className="text-slate-500">Livre de IOF hoje</span>
          <span className="text-central-texto font-semibold tabular-nums">
            R$ {fmt(s.iofFreeGross)}
          </span>
        </div>
        {s.nextIofFreeDate && s.iofLockedGross > 0 && (
          <div className="text-[11px] text-central-texto-fraco">
            R$ {fmt(s.iofLockedGross)} liberam em {fmtDate(s.nextIofFreeDate)}
          </div>
        )}
        {/* Divergência entre o calculado e o que o banco informou = aplicação
            ou resgate que ninguém registrou. */}
        {s.drift != null && Math.abs(s.drift) > 0.01 && (
          <div className="text-[11px] text-red-400">
            Diverge do relatório do banco em R$ {fmt(Math.abs(s.drift))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Badge de pendências. `compacto` omite o "tudo conciliado": dentro do card do
   * pai seria uma linha a mais só para dizer que não há nada a fazer — e num
   * fundo, que não tem bank_transactions, ela nunca diria outra coisa.
   */
  const renderPendencias = (acc: any, compacto = false) => {
    const unrec = Number(acc.unreconciledCount || 0);
    if (unrec === 0) {
      if (compacto) return null;
      return (
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-central-texto-fraco" data-conciliacao="em-dia">
          <Icon name="check" className="w-3.5 h-3.5" />
          Tudo conciliado
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          // Expand date range to "all time" so every pending tx shows,
          // regardless of the current month filter
          setPeriodFilter({
            mode: 'dateRange',
            months: [],
            startDate: '2000-01-01',
            endDate: '2100-12-31',
          });
          setOnlyUnreconciled(true);
          setSelectedAccount(acc);
        }}
        title="Ver e ajustar todas as transações pendentes (qualquer data)"
        className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-medium hover:bg-amber-500/25 hover:text-amber-300 transition-colors cursor-pointer"
      >
        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        {unrec} {unrec === 1 ? 'transação pendente' : 'transações pendentes'}
        <Icon name="arrowRight" className="w-3 h-3" />
      </button>
    );
  };

  /**
   * Editar / importar extrato / excluir. Conta de investimento não oferece a
   * importação: ela não tem bank_transactions — o extrato dela são lotes,
   * resgates e posições, que entram pelo painel da própria conta.
   */
  const renderAcoesDaConta = (acc: any, tamanho: 'normal' | 'compacto' = 'normal') => {
    const cls = tamanho === 'compacto' ? 'p-1 rounded' : 'p-1.5 rounded';
    const icone = tamanho === 'compacto' ? 'w-3.5 h-3.5' : 'w-4 h-4';
    return (
      <>
        <button onClick={(e) => { e.stopPropagation(); setEditingAccount(acc); setAccountModal('edit'); }} className={`${cls} text-slate-400 hover:text-white hover:bg-slate-700`} title="Editar">
          <Icon name="edit" className={icone} />
        </button>
        {acc.accountType !== 'investimento' && (
          <button onClick={(e) => { e.stopPropagation(); setOfxModal(acc); }} className={`${cls} text-slate-400 hover:text-white hover:bg-slate-700`} title="Importar Extrato">
            <Icon name="upload" className={icone} />
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); handleDeleteAccount(acc.id); }} className={`${cls} text-slate-400 hover:text-red-400 hover:bg-red-500/10`} title="Excluir">
          <Icon name="trash" className={icone} />
        </button>
      </>
    );
  };

  /**
   * Conta filha dentro do card do pai: a mesma informação, recuada e com a barra
   * ciano à esquerda marcando de quem ela depende. Clicar abre a conta filha, e
   * não a do pai — daí o stopPropagation.
   */
  const renderContaFilha = (acc: any) => (
    <div
      key={acc.id}
      onClick={(e) => { e.stopPropagation(); setSelectedAccount(acc); }}
      className={`group/filha rounded-central border border-central-borda bg-central-elevado/40 px-3 py-2.5 hover:bg-central-elevado transition-colors ${barraDoSaldo(acc.currentBalance)}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-slate-400 truncate" title={identificacaoDaConta(acc)}>{identificacaoDaConta(acc)}</div>
          <div className="text-xl font-bold mt-0.5 text-central-texto tabular-nums">
            R$ {fmt(acc.currentBalance)}
          </div>
        </div>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 bg-central-elevado text-central-texto-fraco border border-central-borda">
          {tipoDaConta(acc)}
        </span>
      </div>
      {acc.accountType === 'investimento' ? renderResumoInvestimento(acc) : ultimoExtratoDaConta(acc)}
      {renderPendencias(acc, true)}
      <div className="flex gap-1 mt-2 opacity-0 group-hover/filha:opacity-100 transition-opacity">
        {renderAcoesDaConta(acc, 'compacto')}
      </div>
    </div>
  );

  const renderCardDeConta = (acc: any, filhas: any[]) => (
    <div key={acc.id} className={`${CARTAO_DA_CONTA} p-5 hover:bg-central-elevado/40 transition-colors cursor-pointer group ${barraDoSaldo(acc.currentBalance)}`} onClick={() => setSelectedAccount(acc)} data-cartao-conta={acc.id}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: acc.color || '#64748b' }} />
          <span className="text-central-texto font-semibold">{acc.bankName}</span>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-central-elevado text-central-texto-fraco border border-central-borda">
          {tipoDaConta(acc)}
        </span>
      </div>
      <div className="text-slate-400 text-sm mb-1">{identificacaoDaConta(acc)}</div>
      <div className="text-2xl font-bold mt-2 text-central-texto tabular-nums" data-saldo-da-conta>
        R$ {fmt(acc.currentBalance)}
      </div>
      {acc.accountType === 'investimento'
        ? renderResumoInvestimento(acc)
        : contaPelaApi(acc)
          ? <ExtratoSicrediDoCartao acc={acc} podeAtualizar={isAdmin()} aoAtualizar={() => loadAccounts()} avisar={showToast} />
          : ultimoExtratoDaConta(acc)}
      {renderPendencias(acc)}
      <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
        {renderAcoesDaConta(acc)}
      </div>
      {filhas.length > 0 && (
        // O saldo da filha NÃO entra no do pai: dinheiro aplicado e dinheiro em
        // caixa têm liquidez diferente, e somar os dois aqui faria o card
        // prometer como disponível o que está preso por 30 dias de IOF. A
        // separação é a mesma do "Saldo total" no topo da tela.
        <div className="mt-4 pt-3 border-t border-central-borda space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">
            {filhas.length === 1 ? 'Aplicação desta conta' : 'Aplicações desta conta'}
          </div>
          {filhas.map((filha: any) => renderContaFilha(filha))}
        </div>
      )}
    </div>
  );

  const pendentesDoPeriodo = filaDoAssistente<any>(transactions, new Set()).fila.length;

  const modalCtx: BankModalCtx = { showToast, selectedAccount, transactions, periodFilter, currentMonthStr, wizardHandled, setWizardHandled, setWizardMode, wizardSkipped, setWizardSkipped, wizardReconciled, setWizardReconciled, loadTransactions, loadOfficialStatements, loadAccounts, accounts };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-[100] px-4 py-3 rounded-lg border shadow-lg text-white ${toast.type === 'success' ? 'bg-green-500 border-green-600' : 'bg-red-500 border-red-600'}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {selectedAccount && (
            <button onClick={() => setSelectedAccount(null)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
              <Icon name="arrowLeft" />
            </button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-white">
              {selectedAccount ? `${selectedAccount.bankName} — ${selectedAccount.accountNumber}` : 'Contas Bancárias'}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {!selectedAccount
                ? 'Gerencie contas e importe extratos'
                : selectedAccount.accountType === 'investimento'
                  // Varredura (ContaMax) não tem lote nem posição: o saldo vem da corrente (14/10/2026, #507).
                  ? (lerVarredura(investmentSummaries[selectedAccount.id])
                    ? 'Aplicações e resgates automáticos da conta corrente'
                    : 'Aplicações, resgates e posição do fundo')
                  : 'Transações e conciliação'}
            </p>
          </div>
        </div>
        {/* Sem as contas lidas, "Nova Conta" convidava a criar a que já existe. */}
        {!selectedAccount && cargaDasContas.atualizadoEm && (
          <button onClick={() => { setAccountModal('create'); setEditingAccount(null); }} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium flex items-center gap-2">
            <Icon name="plus" className="w-4 h-4" /> Nova Conta
          </button>
        )}
      </div>

      {/* ============ SALDO CONSOLIDADO (soma de todas as contas) ============ */}
      {!selectedAccount && accounts.length > 0 && (() => {
        // Caixa e aplicado são somados no total, mas exibidos separados: são
        // dinheiros com liquidez diferente. Tratar os dois como uma coisa só faz
        // o total parecer disponível quando parte dele está presa por 30 dias.
        const sumOf = (pred: (a: any) => boolean) =>
          accounts.filter(pred).reduce((s: number, a: any) => s + (parseFloat(a.currentBalance) || 0), 0);
        const isInvestment = (a: any) => a.accountType === 'investimento';
        const cash = sumOf((a) => !isInvestment(a));
        const invested = sumOf(isInvestment);
        const totalBalance = cash + invested;
        return (
          <div className={`${CARTAO_DA_CONTA} p-5 mb-4 flex items-center justify-between flex-wrap gap-4 ${barraDoSaldo(totalBalance)}`} data-saldo-consolidado>
            {/* Numa recarga que falha, o total ficava com o número velho sem dizer (os cards abaixo já tinham a faixa). */}
            {cargaDasContas.estado === 'erro' && (
              <p role="status" className={`w-full text-xs ${TONE_TEXT.warning}`} data-testid="saldo-consolidado-velho">
                {textos.naoConseguiAtualizar('os saldos', cargaDasContas.erro)}
                {cargaDasContas.atualizadoEm ? textos.numerosDaUltimaCargaBoa(fmtDateTime(cargaDasContas.atualizadoEm)) : ''}
              </p>
            )}
            <div>
              <p className="text-slate-400 text-sm">Saldo total — {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}</p>
              <p className="text-3xl font-bold mt-1 text-central-texto tabular-nums">
                R$ {fmt(totalBalance)}
              </p>
            </div>
            {invested > 0 && (
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-slate-400 text-xs">Em caixa</p>
                  <p className="text-xl font-semibold mt-0.5 text-central-texto tabular-nums">R$ {fmt(cash)}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Aplicado</p>
                  <p className="text-xl font-semibold mt-0.5 text-central-texto tabular-nums">R$ {fmt(invested)}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Icon name="building" className="w-4 h-4" />
              <span>{invested > 0 ? 'Caixa mais aplicações financeiras' : 'Soma do saldo atual de todas as contas'}</span>
            </div>
          </div>
        );
      })()}

      {/* ============ ACCOUNT CARDS (list view) ============ */}
      {!selectedAccount && (
        <EstadoDaLista
          carga={cargaDasContas}
          temDados={accounts.length > 0}
          oQue="as contas"
          onTentarDeNovo={() => { void loadAccounts(); }}
          vazio={<div className="text-center py-12 text-slate-500">Nenhuma conta bancária cadastrada</div>}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            {accountGroups.map(({ acc, filhas }) => renderCardDeConta(acc, filhas))}
          </div>
        </EstadoDaLista>
      )}

      {/* ============ CONTA DE INVESTIMENTO (detail view própria) ============ */}
      {/* Conta de investimento não tem bank_transactions: o extrato dela é a
          lista de lotes, resgates e posições. Cair na view de transações
          mostraria uma tabela vazia e um botão de importar OFX que não faz
          sentido para um fundo. */}
      {selectedAccount && selectedAccount.accountType === 'investimento' && (
        <InvestmentAccountPanel
          account={selectedAccount}
          onChanged={loadAccounts}
          onBack={() => setSelectedAccount(null)}
        />
      )}

      {/* ============ TRANSACTIONS (detail view) ============ */}
      {selectedAccount && selectedAccount.accountType !== 'investimento' && (
        <>
          {/* Alerta: saldo do sistema diverge do extrato (lançamento faltando/extra) */}
          {balanceCheckErro && (
            <div role="alert" className="mb-3 px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200" data-conferencia-de-saldo="nao-rodou">
              Não consegui conferir o saldo do sistema com o do extrato ({balanceCheckErro}): se houver divergência, ela não
              apareceria aqui.
            </div>
          )}
          {/* Não sei comparar (#450): a grandeza do saldo do extrato não está gravada, ou não há saldo do extrato. Neutro e sem
              "reimporte": não há divergência afirmada, só a falta do que comparar. */}
          {/* Conta que nunca recebeu saldo do extrato (`statementBalance` nulo) não mostra nada (decisão do Heitor, 14/10/2026):
              não há o que comparar, e a caixa em toda conta assim virava ruído. A distinção é pelo CAMPO, não pelo texto do
              motivo: é o mesmo `statementBalance === null` de onde o backend (#450) tira esse motivo. */}
          {balanceCheck && balanceCheck.grandezaDoExtrato && balanceCheck.matches === null && balanceCheck.statementBalance !== null && (
            <div className="rounded-xl border border-slate-700 bg-slate-800/40 px-4 py-3 text-sm" data-conferencia-de-saldo="sem-comparacao">
              <p className="text-slate-300 font-medium">Não dá para comparar com o extrato agora</p>
              {balanceCheck.motivoSemComparacao && <p className="text-xs text-slate-400 mt-0.5">{balanceCheck.motivoSemComparacao}</p>}
            </div>
          )}
          {balanceCheck && balanceCheck.matches === false && balanceCheck.diff !== null && (
            <div className="bg-slate-800/50 border border-amber-600/60 rounded-xl p-4 flex items-start gap-3">
              <span className="text-amber-400 text-xl leading-none">⚠️</span>
              <div className="text-sm">
                <p className="text-amber-300 font-semibold">
                  Saldo do sistema diverge do extrato em R$ {fmt(Math.abs(balanceCheck.diff))}
                </p>
                <p className="text-slate-300 mt-0.5" data-saldo-comparado>
                  Sistema{somadas.length > 1 && <> ({['corrente', ...somadas.slice(1).map(c => c.nome)].join(' + ')})</>}:{' '}
                  <strong>R$ {fmt(balanceCheck.saldoUsadoNaComparacao ?? balanceCheck.saldoComparado ?? balanceCheck.currentBalance)}</strong>
                  {somadas.length > 1 && <> = {somadas.map(c => `${c.nome} R$ ${fmt(c.saldo)}`).join(' + ')}</>}
                  {' '}· Extrato: <strong>R$ {fmt(balanceCheck.statementBalance)}</strong>.
                  {balanceCheck.diff < 0 ? ' Provável lançamento de crédito faltando' : ' Provável lançamento de débito faltando (ou crédito duplicado)'} — reimporte o extrato mais recente para ajustar.
                </p>
                {comparouASoma && balanceCheck.notaDaComparacao && (
                  <p className="text-xs text-slate-400 mt-1" data-nota-da-comparacao>{balanceCheck.notaDaComparacao}</p>
                )}
              </div>
            </div>
          )}
          {/* Balance card */}
          <div className={`${CARTAO_DA_CONTA} p-5 ${barraDoSaldo(selectedAccount.currentBalance)}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Saldo Atual</p>
                <p className="text-3xl font-bold mt-1 text-central-texto tabular-nums" data-saldo-atual>
                  R$ {fmt(selectedAccount.currentBalance)}
                </p>
              </div>
              <div className="flex gap-3 items-center">
                {/* O assistente percorre as NÃO conciliadas da lista carregada, que é a do período na tela (filaDoAssistente):
                    a contagem é a dele, e o rótulo diz "no período" porque o card da conta conta outra coisa (a conta inteira,
                    a partir do início da conciliação). Sem pendente no período, o botão não aparece (30/10/2026). */}
                {pendentesDoPeriodo > 0 && (
                  <button onClick={() => { setWizardMode(true); setWizardHandled(new Set()); setWizardReconciled(0); setWizardSkipped(0); }} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-2" data-conciliar-pendentes>
                    <Icon name="link" className="w-4 h-4" /> Conciliar {pendentesDoPeriodo} {pendentesDoPeriodo === 1 ? 'pendente' : 'pendentes'} no período
                  </button>
                )}
                <button onClick={handleExportExcel} disabled={excelExporting} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {excelExporting ? 'Exportando...' : 'Exportar Excel'}
                </button>
                {(() => {
                  const currentStmt = officialStatements.find((s: any) => s.referenceMonth === (periodFilter.months[0] || currentMonthStr));
                  if (erroDosExtratosOficiais) {
                    return (
                      <span className={`px-3 py-2 rounded-lg text-xs ${TONE_BADGE.warning}`} title={erroDosExtratosOficiais} data-extratos="nao-leu">
                        Extrato oficial: não consegui ler
                      </span>
                    );
                  }
                  return currentStmt ? (
                    <button onClick={async () => {
                      try {
                        const { url } = await bankAccountsService.getStatementUrl(currentStmt.id);
                        window.open(url, '_blank');
                      } catch { showToast('Erro ao abrir extrato oficial', 'error'); }
                    }} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                      <Icon name="eye" className="w-4 h-4" /> Ver Extrato
                    </button>
                  ) : null;
                })()}
                <button onClick={() => setOfxModal(selectedAccount)} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium flex items-center gap-2">
                  <Icon name="upload" className="w-4 h-4" /> Importar Extrato
                </button>
                {/* Auto-conciliar vira caso pontual: o backend concilia sozinho ao fim de cada ciclo da API. Limpar tudo some na
                    conta pela API (o extrato volta no próximo ciclo e apagar desfaz conciliações) e, nas outras, é só de admin,
                    como no backend (@Roles ADMIN no DELETE bulk), com a confirmação digitando o nome (30/10/2026). */}
                {/* Os dois itens são @Roles(ADMIN) no backend: para os outros perfis, o ⋯ não aparece (antes o Auto-Conciliar
                    estava à vista de todos e devolvia 403). */}
                {isAdmin() && (
                  <MaisAcoes
                    title="Mais ações da conta"
                    itens={[
                      { label: autoReconciling ? 'Conciliando…' : 'Auto-conciliar', title: 'Auto-conciliar agora', disabled: autoReconciling, onClick: handleAutoReconcile },
                      ...(!contaPelaApi(selectedAccount)
                        ? [{ label: 'Limpar tudo', title: 'Apagar todas as transações da conta', danger: true, separadoAntes: true, onClick: handleBulkDeleteTransactions }]
                        : []),
                    ]}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ===== Fila: dinheiro sem documento do outro lado =====
              Estas transações NÃO aparecem nas pendências porque `isReconciled`
              está true em 100% da tabela — o extrato registrou o dinheiro e
              ninguém nunca amarrou a fatura. Em 29/07/2026: 77 créditos de
              boleto somando R$ 468.982,38.
              Sem conciliação automática aqui de propósito: casar por valor+data
              foi o que baixou o pagamento 893 na fatura do cliente errado. Cada
              linha abre o modal normal, com sugestões e decisão humana. */}
          {cargaSemContrapartida.estado === 'erro' && (
            <p role="alert" className={`px-4 py-3 rounded-xl border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-sem-contrapartida="nao-leu">
              Não consegui ler as transações sem contrapartida desta conta ({cargaSemContrapartida.erro}). É uma fila acumulada, de todos os meses: sem ela, não dá para saber se há crédito sem fatura.
              <button type="button" onClick={() => selectedAccount && loadWithoutCounterparty(selectedAccount.id)} className="ml-2 underline font-medium">Tentar de novo</button>
            </p>
          )}
          {noCounterparty.length > 0 && (() => {
            const boletos = noCounterparty.filter((t: any) => t.reason === 'boleto_sem_fatura');
            const semCat = noCounterparty.filter((t: any) => t.reason === 'sem_categoria');
            const totalBoletos = boletos.reduce((s: number, t: any) => s + Math.abs(Number(t.amount)), 0);
            return (
              <div className="bg-amber-500/5 rounded-xl border border-amber-500/30 overflow-hidden">
                <button
                  onClick={() => setShowNoCounterparty(v => !v)}
                  className="w-full flex items-center justify-between p-4 hover:bg-amber-500/5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <Icon name="alert-triangle" className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-amber-300 font-semibold text-sm">
                        {noCounterparty.length} {noCounterparty.length === 1 ? 'transação sem contrapartida' : 'transações sem contrapartida'}
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        {boletos.length > 0 && `${boletos.length} ${boletos.length === 1 ? 'crédito de boleto' : 'créditos de boleto'} sem fatura (R$ ${fmt(totalBoletos)})`}
                        {boletos.length > 0 && semCat.length > 0 && ' · '}
                        {semCat.length > 0 && `${semCat.length} sem categoria`}
                        {' · acumulado, todos os meses'}
                      </p>
                    </div>
                  </div>
                  <span className="text-amber-400 text-xs font-medium">
                    {showNoCounterparty ? 'Ocultar' : 'Ver fila'}
                  </span>
                </button>
                {showNoCounterparty && (
                  <div className="border-t border-amber-500/20">
                    <p className="px-4 py-2 text-[11px] text-slate-400 bg-slate-800/40">
                      Dinheiro que entrou ou saiu do banco e não aponta para fatura nem despesa. Não aparece
                      em "pendentes" porque já está marcado como conciliado. Maior valor primeiro.
                    </p>
                    <div className="max-h-96 overflow-y-auto">
                      {noCounterparty.map((t: any) => (
                        <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-700/40 hover:bg-slate-800/40">
                          <span className="text-slate-500 text-xs w-20 flex-shrink-0">{fmtDate(t.date)}</span>
                          <span className="text-slate-200 text-sm flex-1 min-w-0 truncate">{t.description}</span>
                          {t.reason === 'sem_categoria' && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-600/50 text-slate-300 flex-shrink-0">sem categoria</span>
                          )}
                          <span className={`font-bold text-sm w-32 text-right flex-shrink-0 ${t.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                            {t.type === 'credit' ? '+' : '−'} R$ {fmt(Math.abs(Number(t.amount)))}
                          </span>
                          <button
                            onClick={() => setReconcileModal(t)}
                            className="px-2.5 py-1 text-xs rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-600/30 flex-shrink-0"
                            title={t.reason === 'boleto_sem_fatura' ? 'Achar a fatura deste crédito' : 'Vincular ou classificar'}
                          >
                            {t.reason === 'boleto_sem_fatura' ? 'Achar fatura' : 'Vincular'}
                          </button>
                          <button
                            onClick={() => openCategoryModal(t)}
                            className="px-2.5 py-1 text-xs rounded bg-sky-600/20 text-sky-400 hover:bg-sky-600/30 border border-sky-600/30 flex-shrink-0"
                            title="Classificar (transferência interna, aporte, estorno…)"
                          >
                            Classificar
                          </button>
                        </div>
                      ))}
                    </div>
                    {noCounterpartyLoading && (
                      <p className="px-4 py-2 text-xs text-slate-500 border-t border-slate-700/40">Atualizando…</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {wizardMode ? (
            <ReconciliationWizard ctx={modalCtx} />
          ) : (
          <>
          {/* Transaction filters */}
          <div className="bg-central-cartao rounded-central p-4 border border-central-borda">
            <div className="flex flex-wrap gap-3 items-center">
              <PeriodSelector
                value={periodFilter}
                onChange={setPeriodFilter}
                singleMonth={true}
                showAllMonths={false}
              />
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-600 bg-slate-700" checked={onlyUnreconciled} onChange={e => setOnlyUnreconciled(e.target.checked)} />
                Somente não conciliadas
              </label>
            </div>
          </div>

          {/* Bulk selection action bar */}
          {selectedTxIds.size > 0 && (
            // A barra de seleção na superfície da Central, como nas outras listas (era verde: selecionar não é sucesso).
            <div className="bg-central-elevado border border-central-borda rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-white text-sm font-medium">{selectedTxIds.size} transação{selectedTxIds.size !== 1 ? 'ões' : ''} selecionada{selectedTxIds.size !== 1 ? 's' : ''}</span>
                <span className="text-white font-bold text-sm tabular-nums">
                  R$ {fmt(transactions.filter(tx => selectedTxIds.has(tx.id)).reduce((sum, tx) => sum + Math.abs(Number(tx.amount)), 0))}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBulkReconcileModal(true)}
                  className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-strong text-sm font-medium flex items-center gap-2"
                >
                  <Icon name="link" className="w-4 h-4" /> Conciliar com Fatura
                </button>
                <button
                  onClick={() => setBulkReconcileExpenseModal(true)}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium flex items-center gap-2"
                >
                  <Icon name="link" className="w-4 h-4" /> Conciliar com Despesa
                </button>
                <button
                  onClick={() => setSelectedTxIds(new Set())}
                  className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg text-sm"
                >
                  Limpar
                </button>
              </div>
            </div>
          )}

          {/* Transactions table: a casca das listas, sem overflow de rolagem; o cabeçalho fixa no <main> e o ⋯ é fixo. */}
          <CascaDaLista>
            {/* Refetch silencioso: a lista fica na tela e continua utilizável.
                O aviso existe para o operador saber que um número pode mudar em
                seguida — sem isso, atualizar por baixo seria mágica sem sinal. */}
            {transactionsRefreshing && !transactionsLoading && (
              <div className="flex items-center gap-2 px-4 py-1.5 bg-slate-800/80 border-b border-slate-700 text-[11px] text-slate-400">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500" />
                Atualizando...
              </div>
            )}
            {transactionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                <span className="ml-3 text-slate-400">Carregando transações...</span>
              </div>
            ) : (
              <EstadoDaLista
                carga={cargaDasTransacoes}
                temDados={transactions.length > 0}
                oQue="as transações"
                onTentarDeNovo={() => selectedAccount && loadTransactions(selectedAccount.id)}
                vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}
              >
              <TabelaDaLista>
                <CabecalhoDaLista>
                    <ColunaDaLista largura="w-12" rotulo={(() => {
                        const selectableTxs = transactions.filter((tx: any) => !tx.isReconciled && tx.category !== 'ignored' && tx.category !== 'Ignorada');
                        const allSelected = selectableTxs.length > 0 && selectableTxs.every((tx: any) => selectedTxIds.has(tx.id));
                        return (
                          <input
                            type="checkbox"
                            checked={allSelected && selectableTxs.length > 0}
                            onChange={() => {
                              if (allSelected) {
                                setSelectedTxIds(new Set());
                              } else {
                                setSelectedTxIds(new Set(selectableTxs.map((tx: any) => tx.id)));
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 focus:ring-primary cursor-pointer"
                          />
                        );
                      })()} />
                    <ColunaDaLista rotulo="Data" largura="w-28" />
                    <ColunaDaLista rotulo="Descrição" />
                    <ColunaDaLista rotulo="Doc" largura="w-28" />
                    <ColunaDaLista rotulo="Valor" largura="w-36" alinhamento="direita" />
                    <ColunaDaLista rotulo="Saldo" largura="w-36" alinhamento="direita" />
                    <ColunaDaLista rotulo="Conciliada" largura="w-44" alinhamento="centro" />
                    <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-36" />
                </CabecalhoDaLista>
                <tbody className="divide-y divide-slate-700/50">
                  {transactions.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Nenhuma transação encontrada</td></tr>
                  ) : transactions.map((tx: any) => {
                    const isSelectable = !tx.isReconciled && tx.category !== 'ignored' && tx.category !== 'Ignorada';
                    const isSelected = selectedTxIds.has(tx.id);
                    const isHighlighted = highlightTxId === tx.id;
                    return (
                    <LinhaDaLista key={tx.id} data-tx-row-id={tx.id} destacada={isSelected} className={isHighlighted ? 'bg-blue-500/15 ring-2 ring-blue-400/60 animate-pulse' : ''}>
                      <td className={CELULA}>
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedTxIds(prev => {
                                const next = new Set(prev);
                                if (next.has(tx.id)) next.delete(tx.id);
                                else next.add(tx.id);
                                return next;
                              });
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 focus:ring-primary cursor-pointer"
                          />
                        ) : <div className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(tx.date)}</td>
                      <td className="px-4 py-3 text-slate-200 text-sm">
                        {tx.description}
                        {tx.notes && (
                          <div className="flex items-start gap-1 mt-1 text-[11px] text-amber-300/80 italic" title={tx.notes}>
                            <Icon name="document" className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{tx.notes}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs truncate">
                        {tx.fitId ? (() => {
                          const parts = tx.fitId.split('-');
                          // Format: PDF-{date}-{doc}-{amount} or OFX raw fitId
                          if (parts[0] === 'PDF' && parts.length >= 4) return parts.slice(2, -1).join('-');
                          return tx.fitId.length <= 20 ? tx.fitId : '';
                        })() : ''}
                      </td>
                      {/* Extrato: a entrada e a saída dizem o sentido pelo sinal e pelo tom do mapa (V, decisão na prévia). */}
                      <td className={`px-4 py-3 text-right font-medium whitespace-nowrap tabular-nums ${tx.type === 'credit' ? TONE_TEXT.success : TONE_TEXT.danger}`}>
                        {tx.type === 'credit' ? '+' : '-'} R$ {fmt(Math.abs(tx.amount))}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-400 whitespace-nowrap tabular-nums">R$ {fmt(tx.balance)}</td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const ep = tx.expensePayments?.[0]?.expense;
                          const ip = tx.invoicePayments?.[0]?.invoice;
                          const hasDetails = tx.isReconciled || tx.category || ep || ip || tx.expense;
                          const badge = tx.category === 'Ignorada' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-500">Ignorada</span>
                          ) : tx.category === 'Transferência Interna' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">Interna</span>
                          ) : tx.category === 'Cobrança Boleto' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">Boleto</span>
                          ) : tx.category === 'Rendimentos' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400">Rendimento</span>
                          ) : tx.category === 'Cartão de Crédito' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">Cartão</span>
                          ) : tx.loanContractId ? (
                            // Distingue Borderô (desconto de duplicatas) de Empréstimo (SAC, FGO, etc) pelo type do contrato.
                            // Fallback pelo category quando o contrato não vem populado.
                            (tx.loanContract?.type === 'desconto_duplicatas'
                              || (!tx.loanContract && tx.category === 'Antecipação de Recebíveis')
                            ) ? (
                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">Borderô</span>
                            ) : (
                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-400">Empréstimo</span>
                            )
                          ) : tx.category === 'Antecipação de Recebíveis' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">Borderô</span>
                          ) : tx.category === 'Empréstimo a Sócio' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-rose-500/20 text-rose-400">Empr. Sócio</span>
                          ) : tx.category === 'Captação de Empréstimo' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-teal-500/20 text-teal-400">Captação</span>
                          ) : tx.category === 'Aporte de Sócio' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">Aporte</span>
                          ) : tx.category === 'Distribuição de Lucro' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-fuchsia-500/20 text-fuchsia-400">Distrib. Lucro</span>
                          ) : tx.category === 'Outras Receitas' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-lime-500/20 text-lime-400">Outras Rec.</span>
                          ) : tx.category === 'Estorno Bancário' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-300">Estorno</span>
                          ) : tx.category === 'Movimentação Financeira' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400">Aplicação</span>
                          ) : tx.isReconciled ? (
                            // E1: conciliada é o desfecho normal, neutro e cheio; a pendente, só o contorno.
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>Sim</span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium border border-central-borda text-slate-400">Nao</span>
                          );
                          const label = tx.loanContractId ? (tx.loanContract?.contractNumber || `Op. Crédito #${tx.loanContractId}`) : ep ? ep.description : ip ? `Fatura ${ip.code}${ip.client?.tradeName ? ` - ${ip.client.tradeName}` : ip.client?.name ? ` - ${ip.client.name}` : ''}` : tx.expense?.description || null;
                          return (
                            <div className={`flex flex-col items-center gap-0.5 ${hasDetails ? 'cursor-pointer' : ''}`} onClick={() => hasDetails && setTraceModal(tx)}>
                              {badge}
                              {tx.isReconciled && label && (
                                <span className="text-[10px] text-slate-500 truncate max-w-[160px]" title={label}>{label}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* D1: o verbo da pendente (Conciliar) à vista; o resto no ⋯ (MaisAcoes, fixo), com o Excluir em perigo e
                          separado. Eram até seis ícones soltos na linha. */}
                      <CelulaAcoes
                        rotuloDoMenu={`Mais ações da transação ${tx.description || tx.id}`}
                        verbo={!tx.isReconciled ? { rotulo: 'Conciliar', title: 'Conciliar', onClick: () => setReconcileModal(tx) } : undefined}
                        itens={[
                          ...(!tx.isReconciled
                            ? [{ label: 'Ignorar transação', title: 'Ignorar transacao', disabled: busyTxId === tx.id, onClick: () => handleIgnoreTransaction(tx.id) }]
                            : [{ label: 'Desfazer conciliação', title: 'Desfazer conciliacao', disabled: busyTxId === tx.id, onClick: () => handleUnreconcile(tx.id) }]),
                          {
                            label: tx.ignoreAutoReconcile ? 'Permitir auto-conciliação' : 'Bloquear auto-conciliação',
                            title: tx.ignoreAutoReconcile ? 'Permitir auto-conciliação' : 'Bloquear auto-conciliação',
                            disabled: busyTxId === tx.id,
                            onClick: () => handleToggleIgnoreAutoReconcile(tx),
                          },
                          { label: tx.notes ? 'Editar observação' : 'Adicionar observação', title: tx.notes ? 'Editar observação' : 'Adicionar observação', onClick: () => openNotesModal(tx) },
                          // D2: classificar só em linha pendente, ou na que só está "conciliada" pela própria classificação.
                          // Em linha ligada a fatura ou despesa, a classificação gravava por cima do vínculo.
                          ...(podeClassificar(tx) ? [{ label: 'Classificar…', title: 'Classificar (transferência interna, empréstimo a sócio, captação)', onClick: () => openCategoryModal(tx) }] : []),
                          { label: 'Excluir transação', title: 'Excluir transacao', danger: true, separadoAntes: true, disabled: busyTxId === tx.id, onClick: () => handleDeleteTransaction(tx) },
                        ]}
                      />
                    </LinhaDaLista>
                    );
                  })}
                </tbody>
              </TabelaDaLista>
              </EstadoDaLista>
            )}
          </CascaDaLista>
          </>
          )}
        </>
      )}

      {/* Modals */}
      {accountModal && <AccountForm ctx={modalCtx} onClose={() => { setAccountModal(null); setEditingAccount(null); }} account={accountModal === 'edit' ? editingAccount : undefined} />}
      {ofxModal && <OfxImportModal ctx={modalCtx} account={ofxModal} initialTab={ofxModalInitialTab} onClose={() => { setOfxModal(null); setOfxModalInitialTab('import'); loadAccounts(); if (selectedAccount) { loadTransactions(selectedAccount.id); loadOfficialStatements(selectedAccount.id); } }} />}
      {/* Cancelar fecha e pronto. Antes o mesmo `onClose` servia aos dois casos,
          então fechar no X custava quatro requisições para nada — e no sucesso
          ainda era o modal quem pedia o reload das transações, em cima do que o
          pai já pedia. O recarregamento agora está todo em handleReconcileDone. */}
      {reconcileModal && (
        <ReconcileModal
          transaction={reconcileModal}
          onClose={() => setReconcileModal(null)}
          onReconciled={(saved?: any) => handleReconcileDone(reconcileModal, saved)}
          selectedAccount={selectedAccount}
          showToast={showToast}
        />
      )}
      {categoryModal && (
        // Categoria escolhida: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        <CascaDeModal
          largura="xs"
          titulo="Classificar transação"
          onClose={() => setCategoryModal(null)}
          corpoClassName="p-5"
          rodape={
            <>
              <button onClick={() => setCategoryModal(null)} className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm">Cancelar</button>
              <button onClick={handleSaveCategory} disabled={savingCategory} className="flex-1 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium">{savingCategory ? 'Salvando...' : 'Salvar'}</button>
            </>
          }
        >
            <div className="bg-central-elevado/60 rounded p-2 mb-3 text-xs text-slate-400">
              <div className="text-slate-200">{categoryModal.description}</div>
              <div className="mt-0.5">{fmtDate(categoryModal.date)} · <span className={categoryModal.type === 'credit' ? 'text-green-400' : 'text-red-400'}>{categoryModal.type === 'credit' ? '+' : '-'} R$ {fmt(Math.abs(categoryModal.amount))}</span></div>
            </div>
            <p className="text-xs text-slate-500 mb-2">Para transações que não são fatura nem despesa. Classificar resolve a transação (sai das pendências) sem vincular documento.</p>
            <div className="space-y-1.5">
              {MANUAL_TX_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryDraft(cat)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${categoryDraft === cat ? 'bg-orange-500/15 border-orange-500/50 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700/60'}`}
                >
                  {cat}
                </button>
              ))}
              <button
                onClick={() => setCategoryDraft('')}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${categoryDraft === '' ? 'bg-slate-700/60 border-slate-500 text-slate-200' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700/60'}`}
              >
                Remover classificação (volta a pendente)
              </button>
            </div>
            {categoryDraft === 'Distribuição de Lucro' && (
              <div className={`mt-3 flex items-start gap-2 rounded-lg border p-2.5 text-xs ${TONE_BORDER.warning} ${TONE_SURFACE.warning} ${TONE_TEXT.warning}`}>
                <Icon name="alert-triangle" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Atenção: para pró-labore que vira despesa <strong>"Divisão de Lucro"</strong> (apuração por sócio no DRE), concilie a transação com a <strong>despesa</strong> — não use este rótulo, senão perde o vínculo com a apuração por sócio. Use isto só para marcar casos avulsos e tirar da pendência.
                </span>
              </div>
            )}
        </CascaDeModal>
      )}
      {notesModal && (
        // Texto digitado: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        <CascaDeModal
          largura="xs"
          titulo="Observação da transação"
          onClose={() => setNotesModal(null)}
          corpoClassName="p-5"
          rodape={
            <>
              <button onClick={() => setNotesModal(null)} className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm">Cancelar</button>
              <button onClick={handleSaveNotes} disabled={savingNotes} className="flex-1 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium">{savingNotes ? 'Salvando...' : 'Salvar'}</button>
            </>
          }
        >
            <div className="bg-central-elevado/60 rounded p-2 mb-3 text-xs text-slate-400">
              <div className="text-slate-200">{notesModal.description}</div>
              <div className="mt-0.5">{fmtDate(notesModal.date)} · <span className={notesModal.type === 'credit' ? 'text-green-400' : 'text-red-400'}>{notesModal.type === 'credit' ? '+' : '-'} R$ {fmt(Math.abs(notesModal.amount))}</span></div>
            </div>
            <textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              rows={4}
              autoFocus
              placeholder="Ex.: pagamento de fatura atrasada de Dez/2025 (fora do Hub)"
              className={`w-full px-3 py-2 placeholder-slate-500 rounded-lg text-sm resize-none ${campo}`}
            />
        </CascaDeModal>
      )}
      {bulkReconcileModal && (
        <BulkReconcileModal
          selectedTransactions={transactions.filter(tx => selectedTxIds.has(tx.id))}
          onClose={() => setBulkReconcileModal(false)}
          selectedAccount={selectedAccount}
          showToast={showToast}
          loadTransactions={loadTransactions}
          onClearSelection={() => setSelectedTxIds(new Set())}
          onKeepSelection={(ids) => setSelectedTxIds(new Set(ids))}
        />
      )}
      {bulkReconcileExpenseModal && (
        <BulkReconcileExpenseModal
          selectedTransactions={transactions.filter(tx => selectedTxIds.has(tx.id))}
          onClose={() => setBulkReconcileExpenseModal(false)}
          selectedAccount={selectedAccount}
          showToast={showToast}
          loadTransactions={loadTransactions}
          onClearSelection={() => setSelectedTxIds(new Set())}
          onKeepSelection={(ids) => setSelectedTxIds(new Set(ids))}
        />
      )}
      {traceModal && (() => {
        const tx = traceModal;
        const expPayments = tx.expensePayments || [];
        const invPayments = tx.invoicePayments || [];
        const directExpense = tx.expense;

        const monthLabel = (m?: string) => {
          if (!m || !/^\d{4}-\d{2}$/.test(m)) return '';
          const [y, mm] = m.split('-').map(Number);
          const names = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
          return `${names[mm - 1]}/${y}`;
        };
        const statusBadge = (status?: string) => {
          if (!status) return null;
          const map: Record<string, string> = {
            'Pago': `${TONE_BADGE.open} ${TONE_BORDER.open}`,
            'Parcial': `${TONE_BADGE.progress} ${TONE_BORDER.progress}`,
            'Pendente': `${TONE_BADGE.warning} ${TONE_BORDER.warning}`,
            'Vencido': `${TONE_BADGE.danger} ${TONE_BORDER.danger}`,
            'Cancelado': `${TONE_BADGE.neutral} ${TONE_BORDER.neutral}`,
          };
          const cls = map[status] || `${TONE_BADGE.neutral} ${TONE_BORDER.neutral}`;
          return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>{status}</span>;
        };

        const renderExpenseCard = (exp: any, paymentInfo?: { paymentDate?: string; amount?: number; paymentMethod?: string }) => {
          if (!exp) return null;
          const totalAmount = Number(exp.amount || 0);
          const paidAmount = Number(exp.paidAmount || 0);
          const supplier = exp.supplier?.tradeName || exp.supplier?.companyName || exp.supplier?.name;
          const category = exp.category?.name;
          const parent = exp.parentExpense;
          const handleOpen = () => {
            if (!onNavigate) return;
            onNavigate('despesas', exp.description || '');
          };
          return (
            <div className={`bg-slate-800 rounded-lg p-3 border-l-2 ${TONE_BORDER.danger} space-y-2`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-slate-400 text-xs mb-1">Despesa Vinculada</div>
                  <div className="text-white font-medium">{exp.description || `Despesa #${exp.id}`}</div>
                </div>
                {statusBadge(exp.status)}
              </div>
              {paymentInfo && (
                <div className="flex gap-3 text-sm pt-1 border-t border-slate-700/50">
                  <span className="text-slate-500">Pagamento:</span>
                  <span className="text-slate-300">{paymentInfo.paymentDate ? fmtDate(paymentInfo.paymentDate) : '—'}</span>
                  <span className="text-white font-medium">R$ {fmt(Number(paymentInfo.amount || 0))}</span>
                  {paymentInfo.paymentMethod && <span className="text-slate-500">via {paymentInfo.paymentMethod}</span>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {exp.referenceMonth && (
                  <>
                    <span className="text-slate-500">Mês ref.</span>
                    <span className="text-slate-200 capitalize">{monthLabel(exp.referenceMonth)}</span>
                  </>
                )}
                {exp.dueDate && (
                  <>
                    <span className="text-slate-500">Vencimento</span>
                    <span className="text-slate-200">{fmtDate(exp.dueDate)}</span>
                  </>
                )}
                {totalAmount > 0 && (
                  <>
                    <span className="text-slate-500">Valor / Pago</span>
                    <span className="text-slate-200">R$ {fmt(totalAmount)} / <span className="text-green-400">R$ {fmt(paidAmount)}</span></span>
                  </>
                )}
                {category && (
                  <>
                    <span className="text-slate-500">Categoria</span>
                    <span className="text-slate-200">{category}</span>
                  </>
                )}
                {supplier && (
                  <>
                    <span className="text-slate-500">Fornecedor</span>
                    <span className="text-slate-200">{supplier}</span>
                  </>
                )}
              </div>
              {parent && (
                <div className="bg-slate-900/60 rounded p-2 mt-2 border border-slate-700/50">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Filha de</div>
                  <div className="text-slate-200 text-sm font-medium">{parent.description}</div>
                  <div className="flex gap-2 text-xs text-slate-500 mt-0.5">
                    <span>R$ {fmt(Number(parent.amount || 0))}</span>
                    {parent.referenceMonth && <span>· {monthLabel(parent.referenceMonth)}</span>}
                  </div>
                </div>
              )}
              {onNavigate && (
                <button onClick={handleOpen} className="w-full mt-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs font-medium border border-slate-600 transition-colors">
                  Abrir despesa →
                </button>
              )}
            </div>
          );
        };

        return (
          // Só leitura: fecha ao clicar fora (como já era).
          <CascaDeModal
            largura="xs"
            fecharAoClicarFora
            titulo="Rastreio da Conciliação"
            onClose={() => setTraceModal(null)}
            corpoClassName="p-5"
            rodape={
              <button onClick={() => setTraceModal(null)} className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
                Fechar
              </button>
            }
          >
              <div className="space-y-3">
                <div className="bg-central-elevado rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Transacao Bancaria</div>
                  <div className="text-white font-medium">{tx.description || '(sem descricao)'}</div>
                  <div className="flex gap-4 mt-1 text-sm">
                    <span className="text-slate-400">{fmtDate(tx.date)}</span>
                    <span className={tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}>
                      {tx.type === 'credit' ? '+' : '-'} R$ {fmt(Math.abs(tx.amount))}
                    </span>
                  </div>
                </div>
                {tx.category && (
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-slate-400 text-xs mb-1">Categoria</div>
                    <div className="text-white">{tx.category}</div>
                  </div>
                )}
                {expPayments.length > 0 && expPayments.map((ep: any, i: number) => (
                  <div key={`ep-${i}`}>
                    {renderExpenseCard(ep.expense, { paymentDate: ep.paymentDate, amount: ep.amount, paymentMethod: ep.paymentMethod })}
                  </div>
                ))}
                {expPayments.length === 0 && directExpense && renderExpenseCard(directExpense)}
                {invPayments.length > 0 && invPayments.map((ip: any, i: number) => (
                  <div key={`ip-${i}`} className="bg-central-elevado rounded-lg p-3 border-l-2 border-green-500">
                    <div className="text-slate-400 text-xs mb-1">Fatura Vinculada</div>
                    <div className="text-white font-medium">
                      <span
                        className={onNavigate ? 'cursor-pointer hover:text-green-400 transition-colors' : ''}
                        onClick={() => onNavigate && onNavigate('invoices', ip.invoice?.code || '')}
                        title={onNavigate ? 'Ir para fatura' : undefined}
                      >
                        Fatura {ip.invoice?.code || `#${ip.invoiceId}`}
                      </span>
                      {ip.invoice?.client && (
                        <span className="text-slate-400 font-normal text-sm ml-2">— {nomeDeExibicao(ip.invoice.client)}</span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className="text-slate-400">{fmtDate(ip.paymentDate)}</span>
                      <span className="text-green-400">R$ {fmt(ip.amount)}</span>
                      {ip.source && ip.source !== 'manual' && <span className="text-slate-500">{ip.source}</span>}
                    </div>
                  </div>
                ))}
                {expPayments.length === 0 && invPayments.length === 0 && !directExpense && (
                  <div className="bg-central-elevado rounded-lg p-3 text-slate-500 text-sm">
                    {tx.category ? 'Classificado por categoria, sem despesa/fatura vinculada' : 'Sem vinculo detalhado registrado'}
                  </div>
                )}
              </div>
          </CascaDeModal>
        );
      })()}
      {autoReconcileResult && <AutoReconcileResultModal ctx={modalCtx} result={autoReconcileResult} onClose={() => setAutoReconcileResult(null)} />}
    </div>
  );
}
